import { Localization, SessionContext } from "@viamap/viamap2-common";
import JSZip from "jszip";
import { useCallback, useContext, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import type { CSSProperties } from "react";
import { createPortal } from "react-dom";
import { Spinner } from "react-bootstrap";
import { Typeahead } from "react-bootstrap-typeahead";
import { utils as XlsxUtils, write as writeXlsx } from "xlsx";
import { LayerInfo } from "src/common/managers/Types";
import { ADModalBody, ADModalFooter, AdvancedDragModal } from "src/componentsUtils/AdvancedDragModal";
import { MIBIcon } from "src/componentsUtils/MIBIcon";
import { DC } from "src/dataCollector/DataCollector";
import { getTingbogUuid, isUsableTingbogSnapshot } from "src/dataCollector/parseTinglysningsDataXml";
import { ExportData } from "src/managers/ExportData";
import { PropertyInformation } from "src/managers/PropertyInformation";
import { getServitutDownloadFileName } from "src/managers/ServitutDownloadNaming";
import {
   fetchTingbogDocumentBlob,
   getTingbogDownloadTarget,
   isTingbogElectronicDocument,
   parseTinglysningsDatoToUtcMs,
   TINGBOG_SCANNED_AKT_PREFIX,
} from "src/managers/TingbogDownload";
import {
   addServitutPackageToZip,
   getServitutAnnotationDocumentId,
   isEligibleDigitalServitut,
   type ServitutElectronicPackageServitut,
} from "src/propertyInfoTemplates/TingbogServitutBatch";
import { cutPdfToSelectedPages, getPdfPageCount } from "src/modules/ANNModule/ANNPDFCutter";
import { createANNLLMAnalyzer, getANNLLMAnalyzer, getServitutAktFetchCandidates, getServitutAktSearchTargets, listServitutAktSearchLocations, type LLMAnalysisResult, type LLMAnalysisResultItem, type LLMMetadata, type PaataleberettigetResolution } from "src/modules/ANNModule/ANNLLMAnalyzer";
import { PaataleberettigetResolution as PaataleberettigetResolutionView } from "src/modules/ANNModule/PaataleberettigetResolution";
import { normalizeServitutIdentity } from "src/modules/ANNModule/ANNServitutIdentity";
import { downloadOverlayImage, uploadOverlayImage } from "src/modules/ANNModule/ANNImageOverlayStorage";
import { ANNManager } from "src/modules/ANNModule/ANNManager";
import {
   findAllServitutDocumentAnnotations,
   findServitutDocumentAnnotation,
   isServitutPdfDocumentAnnotation,
   normalizeDatoloebenrValue,
   pickStedfaestelseForRow,
   getAnnotationSourceExtractedPages,
   getLlmAnalysisFromAnnotation,
   getViewerInitialAktPages,
   hydrateAnalysisItemFromAnnotation,
   isFullCutAnnotationSelection,
   mapAktPagesToCutSelection,
   resolveAnalysisRelevantPages,
   clampPagesToDocument,
   buildConsecutivePages,
   viewerCloseKeepsPageApproval,
   viewerPagesToRowAktPages,
} from "src/modules/ANNModule/ANNServitutDocumentAnnotations";
import { useANNContext, useDeleteAnnotation, useSaveAnnotation } from "src/modules/ANNModule/ANNState";
import { AnnotationContextType, AnnotationType, type ANNAnnotationRecord } from "src/modules/ANNModule/ANNTypes";
import { MapitStateContext } from "src/states/MapitState";
import { closeWindow, ManagedWindow, minimizeOpenWindowsForStedfaestelse, openWindow, WindowId } from "src/WindowManager/WindowState";
import { isWindowOpen, useWindowContext } from "src/WindowManager/useWindowContext";
import {
   captureAktRestoreSnapshot,
   clearedRowAfterAcceptRevert,
   needsLandRegistryFetchAfterAcceptRevert,
   replaceRowMetadata,
   type ServitutAktRestoreSnapshot,
} from "./ServitutRedegoerelseAktRestore";
import { GlassButton, GlassButtonRow } from "./GlassButtons";
import { GlassInputControl, GlassInputGroup } from "./GlassInput";
import { message } from "./Message";
import { VLine } from "./MitGlassComponents";
import { TooltipWrapper } from "src/propertyInfoTemplates/PropertyComponents";
import { hentServitutDataOgBeliggenhed, SFEAndColorList, type ServitutRapportBfeData, type SFEColorEntry } from "./ServitutRapportData";
import { aktIdFormat } from "./DatarapportShared";
import type { PropertyData } from "src/propertyInfoTemplates/PropertyInfoCollectorTypes";
import { ApplicationStateContext, FeatureType } from "src/states/ApplicationState";
import {
   emptyProvenance,
   historyCommentForProvenance,
   formatProvenanceChip,
   areServitutPagesEqual,
   mergeProvenance,
   provenanceAfterDerivedInvalidation,
   provenanceAfterDocumentChange,
   provenanceAfterFieldAccept,
   provenanceAfterFieldRevert,
   provenanceAfterPageReview,
   type ANNServitutProvenance,
} from "src/modules/ANNModule/ANNServitutProvenance";
import {
   SERVITUT_ANALYSIS_FIELDS,
   analysisFieldLabel,
   collectFieldNotes,
   listFieldNotesOfType,
   overlayAcceptedFieldStatuses,
   formatCaseRelevantLabel,
   getAiFieldText,
   getRowFieldText,
   isRowFieldAccepted,
   isServitutAnalysisFieldNote,
   saveUniqueFieldNote,
   type ServitutAnalysisFieldKey,
   type ServitutFieldAnnotationMap,
} from "src/modules/ANNModule/ANNServitutFieldAnnotations";
import { pdfHasExtractableText } from "src/modules/ANNModule/pdfHasExtractableText";
import {
   canUsePhaseA,
   canUsePhaseB,
   canUseServitutRedegoerelse,
   canWritePhaseA,
   canWritePhaseB,
   type ServitutRedegoerelsePhase,
} from "./ServitutRedegoerelseAccess";
import { AiRobotIcon, ServitutAiFieldChip, ServitutAktSourcePill, ServitutDigitalDocumentChip, ServitutDocumentExtentChip, ServitutImportedChip, ServitutPageFindChip, ServitutProvenanceChip, ServitutSourceHint } from "./ServitutRedegoerelseMarks";
import { ServitutFolderCompareModal } from "./ServitutFolderCompareModal";
import {
   buildAiProvenanceForAnalysisItem,
   buildContentProvenanceForAnalysisItem,
   buildLocateProvenanceForAnalysisItem,
   getContentFieldAiStatus,
   getRowAnalysisItem,
   getRowCopyText,
   getRowDocumentExtent,
   getRowHeading,
   getPageControlTone,
   getRowPageApprovalPlan,
   getRowPageSourceStatus,
   getRowProvenance,
   hasAiPageSuggestion,
   isCustomUploadRow,
   isElectronicServitutRow,
   isImportedAnnotationPendingPageSearch,
   isRowApproved,
   isRowControlled,
   isRowRejected,
   isRowFetched,
} from "./ServitutRedegoerelseRow";
import { ANNEditorActionType, useANNEditorContext } from "src/modules/ANNModule/ANNEditorState";
import "./ServitutRedegoerelseRapport.css";

type ServitutRedegoerelsePreparedAkt = {
   document: string;
   aktId: string;
   documentUrl: string;
   source: "korrektAkt" | "servitutAkt" | "textAkt" | "ovrigAkt";
   servitutKeys: string[];
};

/** Fremdrift ved første indlæsning (tingbog → hver servitut). */
export type ServitutRedegoerelseLoadProgress = {
   stage: "registry" | "easements";
   completed: number;
   total: number;
};

/** Fremdrift ved ZIP-eksport (Excel → hvert dokument → komprimering). */
export type ServitutRedegoerelseExportProgress = {
   completedSteps: number;
   totalSteps: number;
   stage: "spreadsheet" | "document" | "compress";
   activeRowKey: string | null;
   /** 1-based indeks blandt dokumenter (kun ved stage document). */
   documentIndex?: number;
   documentTotal?: number;
};

/** Legacy indskannede akt-Samlinger kræver LLM + sidevalg; digitale tingbogsdokumenter pakkes ved eksport som i ejendomsinfo (akt + SERVIMAP + bilag). */
export type ServitutRedegoerelseRowHandling = "legacyLlmPdf" | "electronicZipPackage";
export type ServitutRedegoerelseDocumentSource = "tingbog" | "annotation" | "customUpload" | "rigsarkivetRequired";

export type ServitutCustomPdf = {
   s3Key: string;
   filename: string;
};

type ServitutMatrixRow = {
   key: string;
   servitut: any;
   handling: ServitutRedegoerelseRowHandling;
   displayAktId: string;
   datoLoebenr: string;
   priority: string;
   summary: string;
   analysisAktId: string;
   includedBfes: Set<number>;
   pdfStatus: "pending" | "ready" | "missing" | "failed";
   pdfSource?: ServitutRedegoerelsePreparedAkt["source"];
   aktDocument?: string;
   aktId?: string;
   documentUrl?: string;
   analysisItem?: LLMAnalysisResultItem;
   selectedPages?: number[];
   exportFileName?: string;
   documentSource?: ServitutRedegoerelseDocumentSource;
   customPdf?: ServitutCustomPdf;
   annotationRecord?: ANNAnnotationRecord;
   annotationDocument?: ANNAnnotationRecord["document"];
   sourceMessage?: string;
   resultsAccepted?: boolean;
   provenance?: ANNServitutProvenance;
   fieldAnnotations?: ServitutFieldAnnotationMap;
   stedfaestelseAnnotation?: ANNAnnotationRecord;
   /** Original akt state captured before a custom PDF upload, used to undo that upload. */
   aktRestore?: ServitutAktRestoreSnapshot;
};

/** Per-BFE metadata for matrix headers (full matrikel list + ejerlav); ejerlav not shown inline on the column. */
export type ServitutMatrixColumnDetail = {
   matrikler: string[];
   ejerlav?: string;
};

type ServitutAktSearchHint = {
   indskannetAktDokumentId?: string | null;
};

type ServitutRedegoerelsePayload = {
   metadata: LLMMetadata[];
   caseDescription?: string;
   analysisResult?: LLMAnalysisResult;
   /** Enough land-registry search context to fetch an akt after an imported annotation is undone. */
   aktSearchByBfe?: Record<string, ServitutAktSearchHint>;
   matrix: {
      columns: number[];
      columnLabels?: Record<string, string>;
      /** Full cadastral breakdown per column for hover popover (keys are BFE numbers as strings). */
      columnDetails?: Record<string, ServitutMatrixColumnDetail>;
      uniqueAktCount: number;
      rows: {
         key: string;
         servitut?: any;
         /** Standard: eksisterende rapporter — behandles som scanned akt + LLM. */
         handling?: ServitutRedegoerelseRowHandling;
         electronicServitut?: ServitutElectronicPackageServitut;
         displayAktId: string;
         datoLoebenr: string;
         priority: string;
         summary: string;
         analysisAktId: string;
         includedBfes: number[];
         pdfStatus: ServitutMatrixRow["pdfStatus"];
         pdfSource?: ServitutRedegoerelsePreparedAkt["source"];
         aktDocument?: string;
         aktId?: string;
         documentUrl?: string;
         analysisItem?: LLMAnalysisResultItem;
         selectedPages?: number[];
         exportFileName?: string;
         documentSource?: ServitutRedegoerelseDocumentSource;
         customPdf?: ServitutCustomPdf;
         annotationRecord?: ANNAnnotationRecord;
         annotationDocument?: ANNAnnotationRecord["document"];
         sourceMessage?: string;
         resultsAccepted?: boolean;
         provenance?: ANNServitutProvenance;
         fieldAnnotations?: ServitutFieldAnnotationMap;
         stedfaestelseAnnotation?: ANNAnnotationRecord;
         aktRestore?: ServitutAktRestoreSnapshot;
      }[];
   };
};

export type ServitutRedegoerelseWindowParams = {
   payload?: ServitutRedegoerelsePayload;
   /** When set, the window fetches land-registry data itself and shows load progress. */
   prepareColorList?: SFEColorEntry[];
   /** When true, AI analysis starts automatically once the window opens with a prepared payload. */
   autoStartAnalysis?: boolean;
   /** Open in find/control (A) or analyse (B). Defaults to A. */
   initialPhase?: ServitutRedegoerelsePhase;
   /** Limit the matrix to a single dato/løbenummer. */
   datoLoebenrFilter?: string;
};

const DEFAULT_SERVITUT_BFE_COLOR = "#2563eb";

type ServitutBfeHeaderStyle = CSSProperties & {
   "--servitut-bfe-head-width": string;
   "--servitut-bfe-head-height": string;
};

const SERVITUT_ANALYSIS_CONCURRENCY = 10;
/** Field-note / metadata annotation writes. No PDF is loaded in the browser. */
const SERVITUT_NOTE_SAVE_CONCURRENCY = 8;
/**
 * Fetch + cut + upload of akt PDFs. Keep this modest so the tab does not hold
 * many full scanned akts in memory at once. Rows on the same akt share one download.
 */
const SERVITUT_PDF_SAVE_CONCURRENCY = 5;
const SERVITUT_CONTENT_CONCURRENCY = 3;
const SERVITUT_REDEGOERELSE_SESSION_CACHE_KEY = "servitut-redegoerelse:last-report";
const SERVITUT_REDEGOERELSE_CASE_DESCRIPTION_KEY = "servitut-redegoerelse:last-case-description";

function loadLastCaseDescription(): string {
   try {
      return localStorage.getItem(SERVITUT_REDEGOERELSE_CASE_DESCRIPTION_KEY) || "";
   } catch {
      return "";
   }
}

function saveLastCaseDescription(value: string) {
   try {
      localStorage.setItem(SERVITUT_REDEGOERELSE_CASE_DESCRIPTION_KEY, value);
   } catch (error) {
      console.warn("[Servitutredegørelse] failed to save case description", error);
   }
}

type ServitutRedegoerelseSessionCache = {
   payload: ServitutRedegoerelsePayload;
   validatedKeys: string[];
   checkedKeys?: string[];
   savedAt: string;
};

const DEFAULT_ANALYSIS_FIELD: ServitutAnalysisFieldKey = "resume";
const SERVITUT_B_TABS = [...SERVITUT_ANALYSIS_FIELDS, "stedfaestelse"] as const;
let lastStedfaestelseRowKey: string | null = null;
type ServitutBTabKey = typeof SERVITUT_B_TABS[number];

function servitutBTabLabel(tab: ServitutBTabKey): string {
   return tab === "stedfaestelse"
      ? Localization.getText("Locate on map")
      : analysisFieldLabel(tab);
}

function loadServitutRedegoerelseSessionCache(): ServitutRedegoerelseSessionCache | null {
   try {
      const raw = sessionStorage.getItem(SERVITUT_REDEGOERELSE_SESSION_CACHE_KEY);
      if (!raw) return null;
      const parsed = JSON.parse(raw) as Partial<ServitutRedegoerelseSessionCache>;
      if (!parsed.payload?.matrix?.rows || !Array.isArray(parsed.validatedKeys)) {
         return null;
      }
      return parsed as ServitutRedegoerelseSessionCache;
   } catch (error) {
      console.warn("[Servitutredegørelse] failed to restore session cache", error);
      return null;
   }
}

function collectAcceptedReviewKeys(payload?: ServitutRedegoerelsePayload | null): string[] {
   if (!payload) {
      return [];
   }
   return payload.matrix.rows
      .filter((row) => (
         Boolean(row.resultsAccepted)
         || Boolean(getLlmAnalysisFromAnnotation(row.annotationRecord)?.resultsAccepted)
      ))
      .map((row) => row.key);
}

function saveServitutRedegoerelseSessionCache(
   payload: ServitutRedegoerelsePayload,
   validatedKeys: Set<string>,
   checkedKeys: Set<string>,
) {
   try {
      const cache: ServitutRedegoerelseSessionCache = {
         payload: {
            ...payload,
            // Raw XML/PDF metadata can be large; the restored UI only needs the prepared matrix/results.
            metadata: [],
         },
         validatedKeys: Array.from(validatedKeys),
         checkedKeys: Array.from(checkedKeys),
         savedAt: new Date().toISOString(),
      };
      sessionStorage.setItem(SERVITUT_REDEGOERELSE_SESSION_CACHE_KEY, JSON.stringify(cache));
   } catch (error) {
      console.warn("[Servitutredegørelse] failed to save session cache", error);
   }
}

export function ServitutRedegoerelseRapport(props: {
   currentRapport: string,
   availableRapports: { value: string, label: string }[],
   onSelectRapport: (a: string) => void,
   onComplete?: () => void
}) {
   const { state: mapitState } = useContext(MapitStateContext);
   const { dispatch: windowDispatch } = useWindowContext();
   const usableLayers = useMemo(() => {
      return Object.values(mapitState.layers).filter((layer): layer is LayerInfo => layer.propertiesInGeoJson?.includes("SFEnummer") ?? false);
   }, [mapitState.layers]);
   const [layerId, setLayerId] = useState<number[]>(usableLayers.map((a) => a.layerId));

   useEffect(() => {
      setLayerId((a) => a.filter((b) => usableLayers.map((layer) => layer.layerId).includes(b)));
   }, [mapitState.layers, usableLayers]);

   const colorList = useMemo(() => {
      return SFEAndColorList(
         layerId.map((a) => mapitState.layers[a]).filter(Boolean) as LayerInfo[]
      );
   }, [layerId, mapitState.layers]);

   function prepareRedegoerelse() {
      windowDispatch(openWindow(WindowId.ServitutRedegoerelse, {
         prepareColorList: colorList,
      }));
      props.onComplete?.();
   }
   return (
      <>
         <ADModalBody>
            <GlassInputGroup>
               <label>{Localization.getText("Document")}</label>
               <GlassInputControl>
                  <select
                     disabled={false}
                     value={props.currentRapport}
                     onChange={(e) => {
                        props.onSelectRapport(e.currentTarget.value);
                     }}
                  >
                     {props.availableRapports.map((a) => (
                        <option key={a.value} value={a.value}>{a.label}</option>
                     ))}
                  </select>
               </GlassInputControl>
               <label>{Localization.getText("Property layer")}</label>
               <Typeahead
                  multiple={true}
                  id="servitutRedegoerelseLayerTypeahead"
                  options={usableLayers.map((a) => ({ id: a.layerId, datasetname: a.datasetname }))}
                  labelKey={"datasetname"}
                  placeholder={Localization.getText("please select") + " " + Localization.getText("Property layer")}
                  emptyLabel={Localization.getText("No results found")}
                  onChange={(selected) => {
                     setLayerId(selected.map((a: any) => a.id));
                  }}
                  selected={usableLayers.map((a) => ({ id: a.layerId, datasetname: a.datasetname })).filter((a) => layerId.includes(a.id))}
               />
               <label></label>
               <GlassButtonRow>
                  <GlassButton onClick={() => setLayerId(usableLayers.map((a) => a.layerId))}>{Localization.getText("Select All")}</GlassButton>
                  <GlassButton onClick={() => setLayerId([])}>{Localization.getText("Remove All")}</GlassButton>
               </GlassButtonRow>
               <VLine color={"#9d9d9d"} /><VLine color={"#9d9d9d"} />
               <label>{Localization.getText("Properties")} {colorList.length}</label>
               <div style={{ display: "flex", flexWrap: "wrap", gap: "2px" }}>
                  {colorList.map((a) => (
                     <div
                        key={a.sfe}
                        title={a.sfe + ""}
                        style={{ background: a.color, borderRadius: "5px", padding: "3px", display: "flex", justifyContent: "center" }}
                     >
                        <MIBIcon icon="property" style={{ aspectRatio: "1", color: "white" }} />
                     </div>
                  ))}
               </div>
            </GlassInputGroup>
         </ADModalBody>
         <ADModalFooter>
            <GlassButton disabled={!colorList.length} onClick={prepareRedegoerelse}>
               {Localization.getText("Generate")}
            </GlassButton>
         </ADModalFooter>
      </>
   );
}

function buildColorListForBfes(bfes: number[]): SFEColorEntry[] {
   return bfes.map((sfe) => ({ sfe, color: DEFAULT_SERVITUT_BFE_COLOR }));
}

function buildColorListForVisibleTingbog(
   propertyData: Partial<PropertyData>,
   bfeNumber: number,
): SFEColorEntry[] {
   const tingbog = propertyData.ViamapTingBog;
   return [{
      sfe: bfeNumber,
      color: DEFAULT_SERVITUT_BFE_COLOR,
      tingbogUuid: getTingbogUuid(tingbog),
      tingbog: isUsableTingbogSnapshot(tingbog) ? tingbog : undefined,
   }];
}

function getPropertyBfeFromData(propertyData: Partial<PropertyData>): number | null {
   const value =
      propertyData?.BBRRel?.bfeNummer ??
      propertyData?.BBRSFE?.[0]?.properties?.BFEnummer ??
      propertyData?.ViamapEjendomData?.sag?.BFEnr ??
      propertyData?.GQL_BBR?.MAT_SamletFastEjendom?.nodes?.[0]?.BFEnummer;
   const parsed = parseInt(`${value || ""}`, 10);
   return Number.isFinite(parsed) ? parsed : null;
}

export async function prepareServitutRedegoerelseForColorList(
   colorList: SFEColorEntry[],
   options: {
      organizationId?: string;
      datoLoebenrFilter?: string;
      onProgress?: (progress: ServitutRedegoerelseLoadProgress) => void;
   } = {},
): Promise<ServitutRedegoerelsePayload> {
   options.onProgress?.({ stage: "registry", completed: 0, total: Math.max(colorList.length, 1) });
   const data = await hentServitutDataOgBeliggenhed(colorList, (completed, total) => {
      options.onProgress?.({ stage: "registry", completed, total });
   });
   const payload = await prepareServitutRedegoerelsePayload(data, colorList, options);
   const filter = options.datoLoebenrFilter?.trim();
   if (!filter) {
      return payload;
   }
   const needle = filter.replace(/[^0-9]/g, "");
   return {
      ...payload,
      matrix: {
         ...payload.matrix,
         rows: payload.matrix.rows.filter((row) => (
            (row.datoLoebenr || "").replace(/[^0-9]/g, "") === needle
            || (row.displayAktId || "").replace(/[^0-9]/g, "") === needle
            || row.key.replace(/[^0-9]/g, "") === needle
         )),
      },
   };
}

export async function prepareServitutRedegoerelseForBfes(
   bfes: number[],
   options: {
      organizationId?: string;
      datoLoebenrFilter?: string;
      onProgress?: (progress: ServitutRedegoerelseLoadProgress) => void;
   } = {},
): Promise<ServitutRedegoerelsePayload> {
   const uniqueBfes = Array.from(new Set(bfes.filter((bfe) => Number.isFinite(bfe))));
   return prepareServitutRedegoerelseForColorList(buildColorListForBfes(uniqueBfes), options);
}

export function ServitutRedegoerelseLaunchButton(props: {
   propertyData: Partial<PropertyData>;
   hasAccessToFeature: (feature: FeatureType) => boolean;
   autoStartAnalysis?: boolean;
   datoLoebenrFilter?: string;
   onMouseDown?: (event: React.MouseEvent<HTMLElement>) => void;
}) {
   const { dispatch: windowDispatch } = useWindowContext();
   const propertyBfe = getPropertyBfeFromData(props.propertyData);
   const canUse = canUseServitutRedegoerelse(props.hasAccessToFeature);

   if (!canUse || propertyBfe === null) {
      return null;
   }
   const bfeNumber = propertyBfe;

   function openServitutRedegoerelse(event: React.MouseEvent<HTMLElement>) {
      event.preventDefault();
      event.stopPropagation();
      windowDispatch(openWindow(WindowId.ServitutRedegoerelse, {
         prepareColorList: buildColorListForVisibleTingbog(props.propertyData, bfeNumber),
         autoStartAnalysis: false,
         initialPhase: "A",
         datoLoebenrFilter: props.datoLoebenrFilter,
      }));
   }

   return (
      <GlassButton
         title={Localization.getText("Open easement review for this property.")}
         onMouseDown={props.onMouseDown}
         onClick={openServitutRedegoerelse}
         style={{
            marginLeft: 0,
            minHeight: 28,
            height: 28,
            padding: "2px 8px",
            display: "inline-flex",
            alignItems: "center",
            gap: "0.35rem",
         }}
      >
         <MIBIcon icon="list" />
         <span style={{ fontSize: 12, whiteSpace: "nowrap" }}>
            {Localization.getText("Review easements")}
         </span>
      </GlassButton>
   );
}

export function ServitutRedegoerelseWindow(props: { managedWindow: ManagedWindow }) {
   const { state: sessionState } = useContext(SessionContext);
   const { dispatch: windowDispatch, state: windowState } = useWindowContext();
   const { dispatch: editorDispatch } = useANNEditorContext();
   const { state: annState } = useANNContext();
   const saveAnnotation = useSaveAnnotation();
   const deleteAnnotation = useDeleteAnnotation();
   const stedfaestelseRowKeyRef = useRef<string | null>(null);
   const editorWasOpenRef = useRef(isWindowOpen(windowState, WindowId.ANNEditor));
   const windowParams = props.managedWindow.windowParams as ServitutRedegoerelseWindowParams | undefined;
   const initialPayload = windowParams?.payload;
   const prepareColorList = windowParams?.prepareColorList;
   const autoStartAnalysis = Boolean(windowParams?.autoStartAnalysis);
   const shouldPrepareOnOpen = Boolean(prepareColorList?.length) && !initialPayload;
   const shouldUseFreshPayload = Boolean(initialPayload) || shouldPrepareOnOpen;
   const prepareRequestKey = shouldPrepareOnOpen
      ? `${prepareColorList!.map((entry) => `${entry.sfe}:${entry.tingbogUuid || ""}`).join(",")}|${windowParams?.datoLoebenrFilter || ""}`
      : "";
   const autoStartAttemptedRef = useRef(false);
   const locateAbortsRef = useRef(new Set<AbortController>());
   const contentAbortsRef = useRef(new Set<AbortController>());
   const locateJobKeysRef = useRef(new Set<string>());
   const contentJobKeysRef = useRef(new Set<string>());
   const saveJobsRef = useRef(new Map<string, Promise<void>>());
   const preparedRef = useRef<ServitutRedegoerelsePayload | null>(null);
   const caseDescriptionRef = useRef("");
   const fieldSaveInFlightRef = useRef(new Set<string>());
   const [prepared, setPrepared] = useState<ServitutRedegoerelsePayload | null>(() => {
      if (shouldPrepareOnOpen) {
         return null;
      }
      if (shouldUseFreshPayload) {
         return initialPayload ?? null;
      }
      return loadServitutRedegoerelseSessionCache()?.payload ?? initialPayload ?? null;
   });
   const [loadProgress, setLoadProgress] = useState<ServitutRedegoerelseLoadProgress | null>(
      shouldPrepareOnOpen ? { stage: "registry", completed: 0, total: Math.max(prepareColorList?.length || 1, 1) } : null,
   );
   const [loadError, setLoadError] = useState<string | null>(null);
   const [locateJobKeys, setLocateJobKeys] = useState<Set<string>>(() => new Set());
   const [contentJobKeys, setContentJobKeys] = useState<Set<string>>(() => new Set());
   const [locateProgress, setLocateProgress] = useState<{ completed: number; total: number } | null>(null);
   const [contentProgress, setContentProgress] = useState<{ completed: number; total: number } | null>(null);
   const [validatedKeys, setValidatedKeys] = useState<Set<string>>(() => {
      if (shouldUseFreshPayload) {
         return new Set();
      }
      return new Set(loadServitutRedegoerelseSessionCache()?.validatedKeys ?? []);
   });
   const [checkedKeys, setCheckedKeys] = useState<Set<string>>(() => {
      if (shouldUseFreshPayload) {
         return new Set(collectAcceptedReviewKeys(initialPayload));
      }
      const cached = loadServitutRedegoerelseSessionCache();
      return new Set([
         ...(cached?.checkedKeys ?? []),
         ...collectAcceptedReviewKeys(cached?.payload ?? initialPayload),
      ]);
   });
   const [analysisField, setAnalysisField] = useState<ServitutAnalysisFieldKey>(DEFAULT_ANALYSIS_FIELD);
   const [openingKey, setOpeningKey] = useState<string | null>(null);
   const [exporting, setExporting] = useState(false);
   const [exportProgress, setExportProgress] = useState<ServitutRedegoerelseExportProgress | null>(null);
   const [savingAnnotationKeys, setSavingAnnotationKeys] = useState<Set<string>>(new Set());
   const [justSavedKeys, setJustSavedKeys] = useState<Set<string>>(new Set());
   const [unsavedChangeKeys, setUnsavedChangeKeys] = useState<Set<string>>(new Set());
   const [reevaluatingKeys, setReevaluatingKeys] = useState<Set<string>>(new Set());
   const [resolvingPaataleKeys, setResolvingPaataleKeys] = useState<Set<string>>(new Set());
   const { hasAccessToFeature } = useContext(ApplicationStateContext);
   const canWriteA = canWritePhaseA(hasAccessToFeature);
   const canWriteB = canWritePhaseB(hasAccessToFeature);
   const canSeeA = canUsePhaseA(hasAccessToFeature);
   const canSeeB = canUsePhaseB(hasAccessToFeature);
   const [phase, setPhase] = useState<ServitutRedegoerelsePhase>(() => {
      if (windowParams?.initialPhase === "B" && canSeeB) {
         return "B";
      }
      if (!canSeeA && canSeeB) {
         return "B";
      }
      return "A";
   });
   const bConfirmDoneRef = useRef(false);
   const [showFolderCompare, setShowFolderCompare] = useState(false);
   const [caseDescription, setCaseDescription] = useState(() => prepared?.caseDescription || loadLastCaseDescription());
   preparedRef.current = prepared;
   caseDescriptionRef.current = caseDescription;
   const aiJobKeys = phase === "B" ? contentJobKeys : locateJobKeys;
   const analyzing = aiJobKeys.size > 0;
   const analysisProgress = phase === "B" ? contentProgress : locateProgress;
   const incompleteARows = (prepared?.matrix.rows || []).filter((row) => !isRowFetched(row) || !isRowControlled(row));
   const rejectedARows = (prepared?.matrix.rows || []).filter(isRowRejected);

   function createJobAbort(kind: "locate" | "content"): AbortController {
      const abortController = new AbortController();
      const bucket = kind === "locate" ? locateAbortsRef : contentAbortsRef;
      bucket.current.add(abortController);
      abortController.signal.addEventListener("abort", () => {
         bucket.current.delete(abortController);
      }, { once: true });
      return abortController;
   }

   function releaseJobAbort(kind: "locate" | "content", abortController: AbortController) {
      const bucket = kind === "locate" ? locateAbortsRef : contentAbortsRef;
      bucket.current.delete(abortController);
   }

   function abortJobs(kind: "locate" | "content") {
      const bucket = kind === "locate" ? locateAbortsRef : contentAbortsRef;
      [...bucket.current].forEach((abortController) => abortController.abort());
      bucket.current.clear();
   }

   function beginRowJob(kind: "locate" | "content", key: string): boolean {
      const keysRef = kind === "locate" ? locateJobKeysRef : contentJobKeysRef;
      if (keysRef.current.has(key)) {
         return false;
      }
      const next = new Set(keysRef.current);
      next.add(key);
      keysRef.current = next;
      (kind === "locate" ? setLocateJobKeys : setContentJobKeys)(next);
      return true;
   }

   function endRowJob(kind: "locate" | "content", key: string) {
      const keysRef = kind === "locate" ? locateJobKeysRef : contentJobKeysRef;
      if (!keysRef.current.has(key)) {
         return;
      }
      const next = new Set(keysRef.current);
      next.delete(key);
      keysRef.current = next;
      (kind === "locate" ? setLocateJobKeys : setContentJobKeys)(next);
   }

   function bumpJobProgress(kind: "locate" | "content") {
      const setter = kind === "locate" ? setLocateProgress : setContentProgress;
      setter((prev) => {
         if (!prev || prev.total < 1) {
            return prev;
         }
         return { completed: Math.min(prev.total, prev.completed + 1), total: prev.total };
      });
   }

   function registerSaveJob(key: string, job: Promise<unknown>) {
      const previous = saveJobsRef.current.get(key);
      const chained = (previous ?? Promise.resolve())
         .catch(() => undefined)
         .then(() => job)
         .then(() => undefined);
      const tracked = chained.finally(() => {
         if (saveJobsRef.current.get(key) === tracked) {
            saveJobsRef.current.delete(key);
         }
      });
      saveJobsRef.current.set(key, tracked);
      return tracked;
   }

   async function waitForRowSave(key: string, signal?: AbortSignal) {
      while (saveJobsRef.current.has(key)) {
         const pending = saveJobsRef.current.get(key);
         if (!pending) {
            return;
         }
         try {
            await waitForPending(pending, signal);
         } catch (error) {
            if (isAbortError(error)) {
               throw error;
            }
            return;
         }
      }
   }

   function getLatestRow(key: string) {
      return preparedRef.current?.matrix.rows.find((row) => row.key === key);
   }

   async function refreshStedfaestelseForRow(key: string) {
      const row = getLatestRow(key);
      if (!row) {
         return;
      }
      const items = await listServitutContextAnnotations(row, sessionState?.customerRef);
      const found = pickStedfaestelseForRow(items, {
         documentId: getCanonicalServitutAnnotationDocumentId(row),
         datoloebenr: row.datoLoebenr,
      });
      if (!found) {
         return;
      }
      setPrepared((current) => applyStedfaestelseToRow(current, key, found));
   }

   function requestPhase(next: ServitutRedegoerelsePhase) {
      if (next === "B" && !canSeeB) {
         return;
      }
      if (next === "A" && !canSeeA) {
         return;
      }
      if (next === "B" && incompleteARows.length && !bConfirmDoneRef.current) {
         const incompleteText = Localization.getFormattedText(
            "{count} easements are not fetched or not controlled. Continue to analysis?",
            { count: incompleteARows.length },
         );
         const rejectedText = rejectedARows.length
            ? Localization.getFormattedText(
               "{count} easements were rejected and are not being analysed.",
               { count: rejectedARows.length },
            )
            : "";
         windowDispatch(openWindow(WindowId.ConfirmationDialog, {
            header: "Find/control is incomplete",
            children: [incompleteText, rejectedText].filter(Boolean).join(" "),
            onSubmit: () => {
               bConfirmDoneRef.current = true;
               setPhase("B");
            },
         }));
         return;
      }
      setPhase(next);
   }

   function patchRowProvenance(key: string, next: ANNServitutProvenance) {
      setPrepared((current) => current ? {
         ...current,
         matrix: {
            ...current.matrix,
            rows: current.matrix.rows.map((row) => row.key === key ? { ...row, provenance: next } : row),
         },
      } : current);
   }

   function patchRowFieldAnnotation(
      key: string,
      field: ServitutAnalysisFieldKey,
      note: ANNAnnotationRecord | undefined,
      provenance: ANNServitutProvenance,
   ) {
      setPrepared((current) => current ? {
         ...current,
         matrix: {
            ...current.matrix,
            rows: current.matrix.rows.map((row) => {
               if (row.key !== key) {
                  return row;
               }
               const fieldAnnotations = { ...row.fieldAnnotations };
               if (note) {
                  fieldAnnotations[field] = note;
               } else {
                  delete fieldAnnotations[field];
               }
               return { ...row, fieldAnnotations, provenance };
            }),
         },
      } : current);
   }

   async function deleteRowFieldNotes(
      row: ServitutRedegoerelsePayload["matrix"]["rows"][number],
   ) {
      if (!sessionState?.customerRef) {
         return;
      }
      const notes = Object.values(row.fieldAnnotations || {}).filter(Boolean) as ANNAnnotationRecord[];
      for (const note of notes) {
         if (!note.id) {
            continue;
         }
         try {
            await deleteAnnotation(note.id, sessionState.customerRef);
         } catch (error) {
            console.warn("[Servitutredegørelse] failed to delete field annotation", row.key, note.id, error);
         }
      }
   }

   async function deleteAllServitutAnnotationsForRow(
      row: ServitutRedegoerelsePayload["matrix"]["rows"][number],
   ) {
      if (!sessionState?.customerRef) {
         return;
      }
      const items = await listServitutContextAnnotations(row, sessionState.customerRef);
      const targetDato = normalizeDatoloebenrValue(row.datoLoebenr);
      for (const item of items) {
         if (!item.id) {
            continue;
         }
         if (targetDato && normalizeDatoloebenrValue(item.datoloebenr) && normalizeDatoloebenrValue(item.datoloebenr) !== targetDato) {
            continue;
         }
         const isManagedField = isServitutAnalysisFieldNote(item);
         const isAcceptedDocument = isServitutPdfDocumentAnnotation(item);
         if (!isManagedField && !isAcceptedDocument) {
            continue;
         }
         try {
            await deleteAnnotation(item.id, sessionState.customerRef);
         } catch (error) {
            console.warn("[Servitutredegørelse] failed to delete servitut annotation", row.key, item.id, error);
         }
      }
   }

   async function acceptAnalysisField(
      row: ServitutRedegoerelsePayload["matrix"]["rows"][number],
      field: ServitutAnalysisFieldKey,
      options?: { quiet?: boolean; text?: string },
   ) {
      if (!sessionState?.userRef || !sessionState?.userName || !sessionState?.customerRef) {
         message.error(Localization.getText("No organization selected"));
         return;
      }
      const text = (options?.text ?? getRowFieldText(row, field, caseDescription)).trim();
      if (!text) {
         if (!options?.quiet) {
            message.error(Localization.getText("No value to accept"));
         }
         return;
      }
      if (locateJobKeysRef.current.has(row.key) || contentJobKeysRef.current.has(row.key) || reevaluatingKeys.has(row.key)) {
         if (!options?.quiet) {
            message.info(Localization.getText("Cannot save while AI is running"));
         }
         return;
      }
      const saveLockKey = `${row.key}:${field}`;
      if (fieldSaveInFlightRef.current.has(saveLockKey)) {
         return;
      }
      fieldSaveInFlightRef.current.add(saveLockKey);
      setSavingAnnotationKeys((prev) => new Set(prev).add(row.key));
      try {
         const contextAnnotations = await listServitutContextAnnotations(row, sessionState.customerRef);
         const siblings = listFieldNotesOfType(contextAnnotations, field, row.datoLoebenr);
         const existing = row.fieldAnnotations?.[field]
            || siblings.find((note) => note.verified)
            || siblings[0];
         const rewritten = Boolean(options?.text !== undefined && options.text.trim() !== getRowFieldText(row, field, caseDescription).trim());
         const record = await saveUniqueFieldNote({
            field,
            documentId: getCanonicalServitutAnnotationDocumentId(row),
            datoLoebenr: row.datoLoebenr,
            organizationId: sessionState.customerRef,
            userId: sessionState.userRef,
            userName: sessionState.userName,
            text,
            existing,
            siblings,
            quality: undefined,
            verified: true,
            historyComment: rewritten
               ? Localization.getText("Rewritten in servitutredegørelse")
               : Localization.getText("Accepted from servitutredegørelse"),
            saveAnnotation,
            deleteAnnotation,
         });
         patchRowFieldAnnotation(
            row.key,
            field,
            record,
            provenanceAfterFieldAccept(getRowProvenance(row), field),
         );
         if (!options?.quiet) {
            message.info(Localization.getText("Field annotation saved"));
         }
      } catch (error) {
         console.error("[Servitutredegørelse] failed to save field annotation", row.key, field, error);
         message.error(error instanceof Error ? error.message : Localization.getText("Failed to save document"));
      } finally {
         fieldSaveInFlightRef.current.delete(saveLockKey);
         setSavingAnnotationKeys((prev) => {
            const next = new Set(prev);
            next.delete(row.key);
            return next;
         });
      }
   }

   function provenanceAfterSingleFieldUndo(
      row: ServitutRedegoerelsePayload["matrix"]["rows"][number],
      field: ServitutAnalysisFieldKey,
   ) {
      const fieldAnnotations = { ...row.fieldAnnotations };
      delete fieldAnnotations[field];
      const restored = buildAiProvenanceForAnalysisItem(
         { ...row, fieldAnnotations },
         row.analysisItem,
         { hasCaseDescription: Boolean(caseDescription.trim()) },
      );
      return provenanceAfterFieldRevert(
         getRowProvenance(row),
         field,
         restored.fieldStatus?.[field],
      );
   }

   async function undoAnalysisField(
      row: ServitutRedegoerelsePayload["matrix"]["rows"][number],
      field: ServitutAnalysisFieldKey,
      options?: { quiet?: boolean },
   ) {
      if (!isRowFieldAccepted(row, field)) {
         return;
      }
      if (locateJobKeysRef.current.has(row.key) || contentJobKeysRef.current.has(row.key) || reevaluatingKeys.has(row.key)) {
         if (!options?.quiet) {
            message.info(Localization.getText("Cannot save while AI is running"));
         }
         return;
      }
      const existing = row.fieldAnnotations?.[field];
      if (existing?.id && sessionState?.customerRef) {
         setSavingAnnotationKeys((prev) => new Set(prev).add(row.key));
         try {
            await deleteAnnotation(existing.id, sessionState.customerRef);
         } catch (error) {
            console.error("[Servitutredegørelse] failed to undo field annotation", row.key, field, error);
            if (!options?.quiet) {
               message.error(error instanceof Error ? error.message : Localization.getText("Failed to save document"));
            }
            setSavingAnnotationKeys((prev) => {
               const next = new Set(prev);
               next.delete(row.key);
               return next;
            });
            return;
         }
         setSavingAnnotationKeys((prev) => {
            const next = new Set(prev);
            next.delete(row.key);
            return next;
         });
      }
      patchRowFieldAnnotation(
         row.key,
         field,
         undefined,
         provenanceAfterSingleFieldUndo(row, field),
      );
   }

   async function acceptAllAnalysisField(
      entries: Array<{ row: ServitutRedegoerelsePayload["matrix"]["rows"][number]; text?: string }>,
      field: ServitutAnalysisFieldKey,
   ) {
      const pending = entries.filter((entry) => {
         const text = (entry.text ?? getRowFieldText(entry.row, field, caseDescription)).trim();
         if (!text || isRowFieldAccepted(entry.row, field)) {
            return false;
         }
         return !locateJobKeysRef.current.has(entry.row.key)
            && !contentJobKeysRef.current.has(entry.row.key)
            && !reevaluatingKeys.has(entry.row.key);
      });
      if (!pending.length) {
         return;
      }
      await runWithConcurrency(pending, SERVITUT_NOTE_SAVE_CONCURRENCY, async (entry) => {
         await acceptAnalysisField(entry.row, field, { quiet: true, text: entry.text });
      });
      message.info(Localization.getFormattedText("Accepted {count} {field} annotations", {
         count: pending.length,
         field: analysisFieldLabel(field),
      }));
   }

   async function undoAllAnalysisField(
      rows: Array<ServitutRedegoerelsePayload["matrix"]["rows"][number]>,
      field: ServitutAnalysisFieldKey,
   ) {
      const pending = rows.filter((row) => (
         isRowFieldAccepted(row, field)
         && !locateJobKeysRef.current.has(row.key)
         && !contentJobKeysRef.current.has(row.key)
         && !reevaluatingKeys.has(row.key)
      ));
      if (!pending.length) {
         return;
      }
      await runWithConcurrency(pending, SERVITUT_NOTE_SAVE_CONCURRENCY, async (row) => {
         await undoAnalysisField(row, field, { quiet: true });
      });
      message.info(Localization.getFormattedText("Undid {count} {field} annotations", {
         count: pending.length,
         field: analysisFieldLabel(field),
      }));
   }

   async function yieldExportPaint(): Promise<void> {
      await new Promise<void>((resolve) => {
         requestAnimationFrame(() => resolve());
      });
   }

   useEffect(() => {
      if (!initialPayload) return;
      setPrepared(initialPayload);
      setLoadProgress(null);
      setLoadError(null);
      setValidatedKeys(new Set());
      setCheckedKeys(new Set(collectAcceptedReviewKeys(initialPayload)));
      setCaseDescription(initialPayload.caseDescription || loadLastCaseDescription());
   }, [initialPayload]);

   useEffect(() => {
      if (!shouldPrepareOnOpen || !prepareColorList?.length) {
         return;
      }
      let cancelled = false;
      setPrepared(null);
      setLoadError(null);
      setLoadProgress({ stage: "registry", completed: 0, total: Math.max(prepareColorList.length, 1) });
      setValidatedKeys(new Set());
      setCheckedKeys(new Set());
      void (async () => {
         try {
            const payload = await prepareServitutRedegoerelseForColorList(prepareColorList, {
               organizationId: sessionState?.customerRef,
               datoLoebenrFilter: windowParams?.datoLoebenrFilter,
               onProgress: (progress) => {
                  if (!cancelled) {
                     setLoadProgress(progress);
                  }
               },
            });
            if (cancelled) {
               return;
            }
            setPrepared(payload);
            setCheckedKeys(new Set(collectAcceptedReviewKeys(payload)));
            setCaseDescription(payload.caseDescription || loadLastCaseDescription());
            setLoadProgress(null);
         } catch (error: unknown) {
            if (cancelled) {
               return;
            }
            const details = error instanceof Error ? error.message : "";
            const text = details
               ? Localization.getFormattedText("An error occurred while generating the report. Please try again later or contact support. Error details: {err}", { err: details })
               : Localization.getText("An error occurred while generating the report. Please try again later or contact support.");
            setLoadError(text);
            setLoadProgress(null);
            message.error(text);
         }
      })();
      return () => {
         cancelled = true;
      };
   }, [prepareRequestKey, sessionState?.customerRef, shouldPrepareOnOpen]);

   useEffect(() => {
      saveLastCaseDescription(caseDescription);
   }, [caseDescription]);

   useEffect(() => {
      if (!prepared) return;
      saveServitutRedegoerelseSessionCache(prepared, validatedKeys, checkedKeys);
   }, [prepared, validatedKeys, checkedKeys]);

   useEffect(() => {
      const editorOpen = isWindowOpen(windowState, WindowId.ANNEditor);
      const rowKey = stedfaestelseRowKeyRef.current || lastStedfaestelseRowKey;
      if (editorWasOpenRef.current && !editorOpen && rowKey) {
         void refreshStedfaestelseForRow(rowKey);
      }
      editorWasOpenRef.current = editorOpen;
   }, [windowState]);

   const hasPreparedRows = Boolean(prepared);
   useEffect(() => {
      const records = Object.values(annState.recordsById).filter((record) => (
         record.type === AnnotationType.Stedfaestelse
      ));
      if (!records.length) {
         return;
      }
      setPrepared((current) => applyStedfaestelseFromRecords(
         current,
         records,
         stedfaestelseRowKeyRef.current || lastStedfaestelseRowKey,
      ));
   }, [annState.recordsById, hasPreparedRows]);

   useEffect(() => {
      if (!prepared) {
         return;
      }
      setValidatedKeys((prev) => {
         let changed = false;
         const next = new Set(prev);
         prepared.matrix.rows.forEach((row) => {
            if (
               next.has(row.key)
               && isServitutRowAwaitingAi(row, {
                  aiJobKeys,
                  reevaluatingKeys,
               })
            ) {
               next.delete(row.key);
               changed = true;
            }
         });
         return changed ? next : prev;
      });
   }, [aiJobKeys, prepared, reevaluatingKeys]);

   useEffect(() => {
      autoStartAttemptedRef.current = false;
   }, [initialPayload, autoStartAnalysis]);

   useEffect(() => {
      return () => {
         abortJobs("locate");
         abortJobs("content");
      };
   }, []);

   function clearJustSaved(keys?: string[]) {
      if (!keys) {
         setJustSavedKeys(new Set());
         return;
      }
      setJustSavedKeys((prev) => {
         const next = new Set(prev);
         keys.forEach((key) => next.delete(key));
         return next;
      });
   }

   function markAnalysisUnsaved(keys?: string[]) {
      clearJustSaved(keys);
      if (!keys) {
         setUnsavedChangeKeys(new Set(prepared?.matrix.rows.map((row) => row.key) ?? []));
         return;
      }
      setUnsavedChangeKeys((prev) => {
         const next = new Set(prev);
         keys.forEach((key) => next.add(key));
         return next;
      });
   }

   function forgetAcceptedReview(keys: string[]) {
      setCheckedKeys((prev) => {
         let changed = false;
         const next = new Set(prev);
         keys.forEach((key) => {
            if (next.delete(key)) {
               changed = true;
            }
         });
         return changed ? next : prev;
      });
   }

   function markAnalysisSaved(keys: string[]) {
      setJustSavedKeys((prev) => {
         const next = new Set(prev);
         keys.forEach((key) => next.add(key));
         return next;
      });
      setUnsavedChangeKeys((prev) => {
         const next = new Set(prev);
         keys.forEach((key) => next.delete(key));
         return next;
      });
   }

   async function startLocatePages() {
      if (!prepared) return;
      if (!hasLocatableServitutRows(prepared)) {
         message.error(Localization.getText("There are no documents to locate in this view."));
         return;
      }
      const requestKeys = [...getLocateRequestKeys(prepared)].filter((key) => !locateJobKeysRef.current.has(key));
      if (!requestKeys.length) {
         return;
      }
      markAnalysisUnsaved(requestKeys);
      await locatePayloadPages(prepared, requestKeys);
   }

   async function startContentAnalysis() {
      if (!prepared) return;
      if (!hasContentAnalyzableRows(prepared)) {
         message.error(Localization.getText("There are no approved easements to analyze in this view."));
         return;
      }
      const payloadForAnalysis = applyCaseDescriptionToPayload(prepared, caseDescription);
      setPrepared(payloadForAnalysis);
      const requestKeys = [...getContentAnalysisRequestKeys(payloadForAnalysis)].filter((key) => !contentJobKeysRef.current.has(key));
      if (!requestKeys.length) {
         return;
      }
      markAnalysisUnsaved(requestKeys);
      await analyzePayloadContent(payloadForAnalysis, requestKeys);
   }

   useEffect(() => {
      if (!autoStartAnalysis || !prepared || autoStartAttemptedRef.current) {
         return;
      }
      if (locateJobKeysRef.current.size > 0) {
         return;
      }
      autoStartAttemptedRef.current = true;
      if (!hasAktBackedLocatableRows(prepared)) {
         return;
      }
      void startLocatePages();
   }, [autoStartAnalysis, prepared]);

   async function locatePayloadPages(payload: ServitutRedegoerelsePayload, requestKeys: string[]) {
      if (!sessionState?.userRef || !sessionState?.userName || !sessionState?.customerRef) {
         message.error(Localization.getText("No organization selected"));
         return;
      }
      const startedKeys = requestKeys.filter((key) => beginRowJob("locate", key));
      if (!startedKeys.length) {
         return;
      }
      const startedKeySet = new Set(startedKeys);
      const abortController = createJobAbort("locate");
      setLocateProgress((prev) => ({
         completed: prev?.completed ?? 0,
         total: (prev?.total ?? 0) + startedKeys.length,
      }));
      const finishKeys = (keys: string[]) => {
         keys.forEach((key) => endRowJob("locate", key));
         keys.forEach(() => bumpJobProgress("locate"));
      };
      try {
         const analyzer = createANNLLMAnalyzer({
            userRef: sessionState.userRef,
            userName: sessionState.userName,
            organizationId: sessionState.customerRef,
         });
         const locateMetadata = payload.metadata.filter((metadata) => (
            Boolean(metadata.aktId)
            && metadata.id
            && startedKeySet.has(metadata.id)
            && !payload.matrix.rows.some((row) => row.key === metadata.id && usesLocalPdfForLocate(row))
         ));
         const metadataBatches = groupMetadataByAkt(locateMetadata);
         let failedBatches = 0;
         await runWithConcurrency(metadataBatches, SERVITUT_ANALYSIS_CONCURRENCY, async (metadataBatch) => {
            if (abortController.signal.aborted) return;
            const batchKeys = metadataBatch.map((metadata) => metadata.id).filter((id): id is string => Boolean(id));
            try {
               const result = await analyzer.locateServitutBatch(
                  metadataBatch.map((metadata) => withSkippedPaataleResolution(metadata)),
                  abortController.signal,
               );
               if (abortController.signal.aborted) return;
               setPrepared((currentPayload) => attachLocateResult(currentPayload ?? payload, result));
            } catch (error) {
               if (isAbortError(error)) return;
               failedBatches += 1;
               console.error("[Servitutredegørelse] AI locate batch failed", metadataBatch, error);
            } finally {
               finishKeys(batchKeys);
            }
         }, abortController.signal);
         const uploadedRows = payload.matrix.rows.filter((row) => (
            startedKeySet.has(row.key) && usesLocalPdfForLocate(row)
         ));
         await runWithConcurrency(uploadedRows, SERVITUT_CONTENT_CONCURRENCY, async (row) => {
            if (abortController.signal.aborted) return;
            try {
               const latest = getLatestRow(row.key) || row;
               const result = await locateOneUploadedRow(latest, payload, analyzer, abortController.signal);
               setPrepared((currentPayload) => attachLocateResult(currentPayload ?? payload, {
                  items: [result],
                  total_processed: 1,
                  total_requested: 1,
                  timestamp: new Date().toISOString(),
               }, { rowKeys: new Set([row.key]) }));
            } catch (error) {
               if (isAbortError(error) || abortController.signal.aborted) return;
               failedBatches += 1;
               console.error("[Servitutredegørelse] AI locate uploaded PDF failed", row.key, error);
            } finally {
               finishKeys([row.key]);
            }
         }, abortController.signal);
         if (abortController.signal.aborted) {
            message.info(Localization.getText("AI analysis stopped."));
            return;
         }
         if (failedBatches) {
            message.error(Localization.getFormattedText("{count} akt batches could not be analyzed.", { count: failedBatches }));
         }
      } catch (error) {
         if (isAbortError(error) || abortController.signal.aborted) {
            message.info(Localization.getText("AI analysis stopped."));
            return;
         }
         console.error("[Servitutredegørelse] AI page finding failed", error);
         message.error(error instanceof Error ? error.message : Localization.getText("Failed to analyze PDF"));
      } finally {
         startedKeys.forEach((key) => {
            if (locateJobKeysRef.current.has(key)) {
               endRowJob("locate", key);
               bumpJobProgress("locate");
            }
         });
         releaseJobAbort("locate", abortController);
         if (locateJobKeysRef.current.size === 0) {
            setLocateProgress(null);
         }
      }
   }

   async function analyzePayloadContent(payload: ServitutRedegoerelsePayload, requestKeys: string[]) {
      if (!sessionState?.userRef || !sessionState?.userName || !sessionState?.customerRef) {
         message.error(Localization.getText("No organization selected"));
         return;
      }
      const startedKeys = requestKeys.filter((key) => beginRowJob("content", key));
      if (!startedKeys.length) {
         return;
      }
      const abortController = createJobAbort("content");
      setContentProgress((prev) => ({
         completed: prev?.completed ?? 0,
         total: (prev?.total ?? 0) + startedKeys.length,
      }));
      try {
         const analyzer = createANNLLMAnalyzer({
            userRef: sessionState.userRef,
            userName: sessionState.userName,
            organizationId: sessionState.customerRef,
         });
         let failedRows = 0;
         await runWithConcurrency(startedKeys, SERVITUT_CONTENT_CONCURRENCY, async (rowKey) => {
            if (abortController.signal.aborted) return;
            try {
               await analyzeOneContentRow(rowKey, analyzer, abortController.signal);
            } catch (error) {
               if (isAbortError(error) || abortController.signal.aborted) return;
               failedRows += 1;
               console.error("[Servitutredegørelse] AI content analysis row failed", rowKey, error);
            } finally {
               endRowJob("content", rowKey);
               bumpJobProgress("content");
            }
         }, abortController.signal);
         if (abortController.signal.aborted) {
            message.info(Localization.getText("AI analysis stopped."));
            return;
         }
         if (failedRows) {
            message.error(Localization.getFormattedText("{count} easements could not be analyzed.", { count: failedRows }));
         }
      } catch (error) {
         if (isAbortError(error) || abortController.signal.aborted) {
            message.info(Localization.getText("AI analysis stopped."));
            return;
         }
         console.error("[Servitutredegørelse] AI content analysis failed", error);
         message.error(error instanceof Error ? error.message : Localization.getText("Failed to analyze PDF"));
      } finally {
         startedKeys.forEach((key) => {
            if (contentJobKeysRef.current.has(key)) {
               endRowJob("content", key);
               bumpJobProgress("content");
            }
         });
         releaseJobAbort("content", abortController);
         if (contentJobKeysRef.current.size === 0) {
            setContentProgress(null);
         }
      }
   }

   async function analyzeOneContentRow(
      rowKey: string,
      analyzer: ReturnType<typeof createANNLLMAnalyzer>,
      signal: AbortSignal,
   ) {
      await waitForRowSave(rowKey, signal);
      if (signal.aborted) {
         throw createAbortError();
      }
      const latest = getLatestRow(rowKey);
      const payload = preparedRef.current;
      if (!latest || !payload || !isRowApproved(latest) || !isRowFetched(latest)) {
         return;
      }
      const metadata = withSkippedPaataleResolution({
         ...buildRowEvaluationMetadata(latest, payload, caseDescriptionRef.current),
         caseDescription: caseDescriptionRef.current.trim() || null,
      });
      const isXmlRow = !isCustomUploadRow(latest)
         && payload.metadata.some((entry) => (
            entry.id === latest.key && entry.aktId == null
         ));
      if (isXmlRow) {
         const result = await analyzer.analyzePdfUrlBatch([metadata], signal);
         if (signal.aborted) {
            throw createAbortError();
         }
         setPrepared((currentPayload) => currentPayload ? attachContentResult(currentPayload, result) : currentPayload);
         return;
      }
      if (isElectronicServitutRow(latest) && !needsUploadedPdfLocate(latest)) {
         return;
      }
      const cutPdf = await getCutServitutPdfForContentAnalysis(latest, signal);
      if (!cutPdf) {
         throw new Error("No cut PDF available");
      }
      if (signal.aborted) {
         throw createAbortError();
      }
      const result = await analyzer.evaluateServitutDocument({
         metadata,
         pdfBlob: cutPdf,
         documentFileName:
            latest.customPdf?.filename
            || latest.annotationDocument?.filename
            || latest.exportFileName
            || latest.aktDocument
            || "servitut.pdf",
         signal,
      });
      if (signal.aborted) {
         throw createAbortError();
      }
      const aktPages = normalizePages(latest.selectedPages?.length ? latest.selectedPages : (latest.analysisItem?.pages_kept || []));
      setPrepared((currentPayload) => currentPayload
         ? attachContentResult(currentPayload, {
            items: [mergeContentIntoAnalysisItem(latest.analysisItem, { ...result, id: latest.key }, aktPages)],
            total_processed: 1,
            total_requested: 1,
            timestamp: new Date().toISOString(),
         })
         : currentPayload);
   }

   function stopAnalysis() {
      abortJobs(phase === "B" ? "content" : "locate");
   }

   async function runAiForRow(row: ServitutRedegoerelsePayload["matrix"]["rows"][number]) {
      if (!prepared) {
         return;
      }
      if (locateJobKeysRef.current.has(row.key) || contentJobKeysRef.current.has(row.key) || reevaluatingKeys.has(row.key)) {
         return;
      }
      if (phase === "B") {
         await reevaluateRowForSelectedPages(row);
         return;
      }
      if (!sessionState?.userRef || !sessionState?.userName || !sessionState?.customerRef) {
         message.error(Localization.getText("No organization selected"));
         return;
      }
      if (!canLocateSingleRow(row)) {
         message.error(Localization.getText("No PDF available for this document"));
         return;
      }
      if (!beginRowJob("locate", row.key)) {
         return;
      }
      const abortController = createJobAbort("locate");
      markAnalysisUnsaved([row.key]);
      setLocateProgress((prev) => ({
         completed: prev?.completed ?? 0,
         total: (prev?.total ?? 0) + 1,
      }));
      try {
         const analyzer = createANNLLMAnalyzer({
            userRef: sessionState.userRef,
            userName: sessionState.userName,
            organizationId: sessionState.customerRef,
         });
         if (usesLocalPdfForLocate(row) || !row.aktId) {
            const result = await locateOneUploadedRow(row, prepared, analyzer, abortController.signal);
            setPrepared((currentPayload) => currentPayload
               ? attachLocateResult(currentPayload, {
                  items: [result],
                  total_processed: 1,
                  total_requested: 1,
                  timestamp: new Date().toISOString(),
               }, { rowKeys: new Set([row.key]) })
               : currentPayload);
         } else {
            const metadata = prepared.metadata.filter((entry) => (
               entry.id === row.key || (row.aktId && entry.aktId === row.aktId)
            ));
            const batch = metadata.length ? metadata : [buildRowEvaluationMetadata(row, prepared)];
            const result = await analyzer.locateServitutBatch(
               batch.map((entry) => withSkippedPaataleResolution(entry)),
               abortController.signal,
            );
            if (abortController.signal.aborted) {
               throw createAbortError();
            }
            setPrepared((currentPayload) => (
               currentPayload ? attachLocateResult(currentPayload, result) : currentPayload
            ));
         }
         message.info(Localization.getText("AI page finding completed."));
      } catch (error) {
         if (isAbortError(error) || abortController.signal.aborted) {
            return;
         }
         console.error("[Servitutredegørelse] single-row AI locate failed", row.key, error);
         message.error(error instanceof Error ? error.message : Localization.getText("Failed to analyze PDF"));
      } finally {
         endRowJob("locate", row.key);
         bumpJobProgress("locate");
         releaseJobAbort("locate", abortController);
         if (locateJobKeysRef.current.size === 0) {
            setLocateProgress(null);
         }
      }
   }

   async function openValidationViewer(row: ServitutRedegoerelsePayload["matrix"]["rows"][number]) {
      const handling = row.handling ?? "legacyLlmPdf";
      const buildViewerParams = (overrides: {
         pdfUrl: string;
         pdfBlob: Blob;
         aktId?: string;
         documentId?: string;
         documentFilename?: string;
      }) => ({
         ...overrides,
         analysisResult: toViewerAnalysisResult(row.analysisItem, phase === "A" ? "summary" : "full"),
         analysisDisplayMode: phase === "A" ? "summary" : "full",
         contextType: AnnotationContextType.Servitut,
         organizationId: sessionState?.customerRef,
         userId: sessionState?.userRef,
         userName: sessionState?.userName,
         datoloebenr: row.datoLoebenr,
         initialSelectedPages: getViewerInitialAktPages(row),
         treatEmptyPagesKeptAsAll: handling === "electronicZipPackage",
         onCloseSelectedPages: (pages: number[]) => updateRowSelectedPages(row.key, pages),
         onDocumentSaved: () => {
            markAnalysisSaved([row.key]);
            void attachExistingAnnotationToRow(row);
         },
      });

      if (row.documentSource === "customUpload") {
         if (!row.customPdf?.s3Key) {
            message.error(Localization.getText("No PDF available for this document"));
            return;
         }
         setOpeningKey(row.key);
         try {
            const blob = await getViewerSourcePdfBlob(row);
            const pdfUrl = URL.createObjectURL(blob);
            const pageCount = await getPdfPageCount(blob).catch(() => 0);
            const openedPages = pageCount
               ? clampPagesToDocument(getViewerInitialAktPages(row), pageCount)
               : getViewerInitialAktPages(row);
            const hasAiPageSearch = Array.isArray(row.analysisItem?.pages_kept);
            const initialSelectedPages = openedPages.length
               ? openedPages
               : (hasAiPageSearch || !pageCount ? openedPages : buildConsecutivePages(pageCount));
            const analysisPages = pageCount
               ? clampPagesToDocument(row.analysisItem?.pages_kept || [], pageCount)
               : (row.analysisItem?.pages_kept || []);
            windowDispatch(openWindow(WindowId.ANNPDFAnalyzerViewer, {
               ...buildViewerParams({
                  pdfUrl,
                  pdfBlob: blob,
                  aktId: row.aktId || row.analysisAktId,
                  documentId: getCanonicalServitutAnnotationDocumentId(row),
                  documentFilename: row.customPdf.filename || row.exportFileName || row.aktDocument,
               }),
               initialSelectedPages,
               analysisResult: toViewerAnalysisResult(
                  row.analysisItem
                     ? { ...row.analysisItem, pages_kept: analysisPages }
                     : row.analysisItem,
                  phase === "A" ? "summary" : "full",
               ),
            }));
         } catch (error) {
            message.error(error instanceof Error ? error.message : Localization.getText("Failed to open scanned akt PDF"));
         } finally {
            setOpeningKey(null);
         }
         return;
      }
      if (row.documentSource === "annotation") {
         if (!row.annotationDocument?.s3Key) {
            message.error(Localization.getText("No PDF available for this document"));
            return;
         }
         setOpeningKey(row.key);
         try {
            const blob = await getViewerSourcePdfBlob(row);
            const pdfUrl = URL.createObjectURL(blob);
            const sourceExtractedPages = normalizePages(row.annotationDocument.extractedPages || []);
            const remappedAnalysisPages = mapAktPagesToCutSelection(
               row.analysisItem?.pages_kept || [],
               sourceExtractedPages,
            );
            const remappedSelectedPages = mapAktPagesToCutSelection(
               getViewerInitialAktPages(row),
               sourceExtractedPages,
            );
            const isCutAnnotation = sourceExtractedPages.length > 0;
            const cutAnalysisItem = row.analysisItem && isCutAnnotation
               ? {
                  ...row.analysisItem,
                  // Highlight using the AI page list remapped into this PDF, not every page.
                  pages_kept: remappedAnalysisPages ?? row.analysisItem.pages_kept ?? [],
               }
               : row.analysisItem;
            windowDispatch(openWindow(WindowId.ANNPDFAnalyzerViewer, {
               ...buildViewerParams({
                  pdfUrl,
                  pdfBlob: blob,
                  aktId: row.aktId || row.analysisAktId,
                  documentId: getCanonicalServitutAnnotationDocumentId(row),
                  documentFilename: row.annotationDocument.filename || row.exportFileName || row.aktDocument,
               }),
               analysisResult: toViewerAnalysisResult(cutAnalysisItem, phase === "A" ? "summary" : "full"),
               pdfSourceKind: isCutAnnotation ? "cutAnnotation" : "fullAkt",
               sourceExtractedPages: isCutAnnotation ? sourceExtractedPages : undefined,
               initialSelectedPages: isCutAnnotation
                  ? (remappedSelectedPages ?? remappedAnalysisPages ?? undefined)
                  : getViewerInitialAktPages(row),
            }));
         } catch (error) {
            message.error(error instanceof Error ? error.message : Localization.getText("Failed to open scanned akt PDF"));
         } finally {
            setOpeningKey(null);
         }
         return;
      }
      if (handling === "electronicZipPackage") {
         const srv = row.electronicServitut;
         const target = srv ? getTingbogDownloadTarget(srv) : undefined;
         if (!srv || !target) {
            message.error(Localization.getText("No PDF available for this document"));
            return;
         }
         setOpeningKey(row.key);
         try {
            const pdfBlob = await getViewerSourcePdfBlob(row);
            const pdfUrl = URL.createObjectURL(pdfBlob);
            windowDispatch(openWindow(WindowId.ANNPDFAnalyzerViewer, buildViewerParams({
               pdfUrl,
               pdfBlob,
               aktId: srv.aktId || srv.aktAliasId || row.analysisAktId,
               documentId: getCanonicalServitutAnnotationDocumentId(row),
               documentFilename: row.exportFileName || `${row.displayAktId}.pdf`,
            })));
         } catch (error) {
            message.error(error instanceof Error ? error.message : Localization.getText("Failed to open scanned akt PDF"));
         } finally {
            setOpeningKey(null);
         }
         return;
      }

      if (!row.aktId) {
         message.error(Localization.getText("No PDF available for this document"));
         return;
      }
      setOpeningKey(row.key);
      try {
         const pdfBlob = await getViewerSourcePdfBlob(row);
         const pdfUrl = URL.createObjectURL(pdfBlob);
         windowDispatch(openWindow(WindowId.ANNPDFAnalyzerViewer, buildViewerParams({
            pdfUrl,
            pdfBlob,
            aktId: row.aktId,
            documentId: getCanonicalServitutAnnotationDocumentId(row),
            documentFilename: row.aktDocument,
         })));
      } catch (error) {
         message.error(error instanceof Error ? error.message : Localization.getText("Failed to open scanned akt PDF"));
      } finally {
         setOpeningKey(null);
      }
   }

   function updateRowSelectedPages(key: string, pages: number[]) {
      const previousRow = prepared?.matrix.rows.find((row) => row.key === key);
      if (!previousRow) {
         return;
      }
      const normalizedPages = viewerPagesToRowAktPages(pages, previousRow);
      const keepApproval = viewerCloseKeepsPageApproval(previousRow.selectedPages, normalizedPages);
      const hadContent = hasContentAnalysisFields(previousRow.analysisItem)
         || Object.keys(previousRow.fieldAnnotations || {}).length > 0;
      setPrepared((currentPayload) => {
         if (!currentPayload) {
            return currentPayload;
         }
         return {
            ...currentPayload,
            matrix: {
               ...currentPayload.matrix,
               rows: currentPayload.matrix.rows.map((row) => {
                  if (row.key !== key) {
                     return row;
                  }
                  if (keepApproval) {
                     return { ...row, selectedPages: normalizedPages };
                  }
                  const provenance = hadContent
                     ? provenanceAfterDerivedInvalidation(getRowProvenance(row))
                     : getRowProvenance(row);
                  return {
                     ...row,
                     selectedPages: normalizedPages,
                     fieldAnnotations: hadContent ? {} : row.fieldAnnotations,
                     provenance: mergeProvenance(provenance, {
                        reviewState: "unchecked",
                     }),
                  };
               }),
            },
         };
      });
      if (!keepApproval && hadContent && Object.keys(previousRow.fieldAnnotations || {}).length) {
         void deleteRowFieldNotes(previousRow);
         message.info(Localization.getText("Derived analysis annotations were marked outdated"));
      }
   }

   async function reevaluateRowForSelectedPages(
      row: ServitutRedegoerelsePayload["matrix"]["rows"][number],
      pages?: number[],
   ) {
      if (!sessionState?.userRef || !sessionState?.userName || !sessionState?.customerRef) {
         message.error(Localization.getText("No organization selected"));
         return;
      }
      if (contentJobKeysRef.current.has(row.key) || reevaluatingKeys.has(row.key)) {
         return;
      }
      if (!beginRowJob("content", row.key)) {
         return;
      }
      const aktPages = normalizePages(pages?.length ? pages : (row.selectedPages || row.analysisItem?.pages_kept || []));
      const abortController = createJobAbort("content");
      forgetAcceptedReview([row.key]);
      markAnalysisUnsaved([row.key]);
      setContentProgress((prev) => ({
         completed: prev?.completed ?? 0,
         total: (prev?.total ?? 0) + 1,
      }));
      try {
         if (aktPages.length) {
            setPrepared((currentPayload) => currentPayload ? {
               ...currentPayload,
               matrix: {
                  ...currentPayload.matrix,
                  rows: currentPayload.matrix.rows.map((currentRow) => (
                     currentRow.key === row.key
                        ? { ...currentRow, selectedPages: aktPages }
                        : currentRow
                  )),
               },
            } : currentPayload);
         }
         const analyzer = createANNLLMAnalyzer({
            userRef: sessionState.userRef,
            userName: sessionState.userName,
            organizationId: sessionState.customerRef,
         });
         await analyzeOneContentRow(row.key, analyzer, abortController.signal);
         message.info(row.analysisItem
            ? Localization.getText("AI analysis updated for selected pages.")
            : Localization.getText("AI analysis completed."));
      } catch (error) {
         if (isAbortError(error) || abortController.signal.aborted) {
            return;
         }
         console.error("[Servitutredegørelse] page selection re-evaluation failed", row.key, error);
         message.error(error instanceof Error ? error.message : Localization.getText("Failed to analyze PDF"));
      } finally {
         endRowJob("content", row.key);
         bumpJobProgress("content");
         releaseJobAbort("content", abortController);
         if (contentJobKeysRef.current.size === 0) {
            setContentProgress(null);
         }
      }
   }

   async function uploadCustomPdfForRow(
      row: ServitutRedegoerelsePayload["matrix"]["rows"][number],
      file: File,
   ) {
      if (!sessionState?.userRef || !sessionState?.userName || !sessionState?.customerRef) {
         message.error(Localization.getText("No organization selected"));
         return;
      }
      const isPdf = file.type === "application/pdf" || file.name.toLowerCase().endsWith(".pdf");
      if (!isPdf) {
         message.error(Localization.getText("Please upload a PDF file"));
         return;
      }
      if (!(await pdfHasExtractableText(file))) {
         message.error(Localization.getText("The uploaded PDF has no extractable text"));
         return;
      }
      if (reevaluatingKeys.has(row.key)) {
         return;
      }
      setReevaluatingKeys((prev) => new Set(prev).add(row.key));
      forgetAcceptedReview([row.key]);
      markAnalysisUnsaved([row.key]);
      try {
         const uploadResult = await uploadOverlayImage(file);
         const customPdf: ServitutCustomPdf = {
            s3Key: uploadResult.key,
            filename: file.name,
         };
         const pageCount = await getPdfPageCount(file).catch(() => 0);
         const selectedPages = pageCount > 0
            ? Array.from({ length: pageCount }, (_, index) => index + 1)
            : undefined;
         const currentMetadata = prepared?.metadata.find((entry) => entry.id === row.key);
         const nextRow = {
            ...row,
            handling: "legacyLlmPdf" as const,
            documentSource: "customUpload" as const,
            customPdf,
            pdfStatus: "ready" as const,
            aktDocument: file.name,
            analysisItem: undefined,
            selectedPages,
            resultsAccepted: false,
            fieldAnnotations: {},
            electronicServitut: undefined,
            provenance: provenanceAfterDocumentChange(getRowProvenance(row), { source: "file", origin: "person" }),
            sourceMessage: Localization.getText("Using uploaded PDF"),
            aktRestore: row.aktRestore ?? captureAktRestoreSnapshot(row, currentMetadata),
         };
         const uploadMetadata = buildCustomUploadMetadata(nextRow, file.name);
         setPrepared((currentPayload) => {
            if (!currentPayload) {
               return currentPayload;
            }
            const hasMetadata = currentPayload.metadata.some((entry) => entry.id === row.key);
            return {
               ...currentPayload,
               metadata: hasMetadata
                  ? currentPayload.metadata.map((entry) => (
                     entry.id === row.key ? uploadMetadata : entry
                  ))
                  : [...currentPayload.metadata, uploadMetadata],
               matrix: {
                  ...currentPayload.matrix,
                  rows: currentPayload.matrix.rows.map((currentRow) => (
                     currentRow.key === row.key ? nextRow : currentRow
                  )),
               },
            };
         });
         void deleteRowFieldNotes(row);
         if (Object.keys(row.fieldAnnotations || {}).length) {
            message.info(Localization.getText("Derived analysis annotations were marked outdated"));
         }
         message.info(Localization.getText("Using uploaded PDF"));
      } catch (error) {
         console.error("[Servitutredegørelse] custom PDF upload failed", row.key, error);
         message.error(error instanceof Error ? error.message : Localization.getText("Failed to save document"));
      } finally {
         setReevaluatingKeys((prev) => {
            const next = new Set(prev);
            next.delete(row.key);
            return next;
         });
      }
   }

   async function applyPaataleberettigetResolutionToRow(
      row: ServitutRedegoerelsePayload["matrix"]["rows"][number],
      mergedItem: LLMAnalysisResultItem,
   ) {
      setPrepared((currentPayload) => currentPayload
         ? attachPaataleberettigetResolution(currentPayload, row.key, mergedItem)
         : currentPayload);
      markAnalysisUnsaved([row.key]);
      const nextText = getAiFieldText("entitledParty", mergedItem, {
         caseDescription,
         tingbogSummary: row.summary,
      }).trim();
      if (!nextText) {
         return;
      }
      const latest = getLatestRow(row.key) || { ...row, analysisItem: mergedItem };
      if (isRowFieldAccepted(latest, "entitledParty") || isRowFieldAccepted(row, "entitledParty")) {
         await acceptAnalysisField({ ...latest, analysisItem: mergedItem }, "entitledParty", {
            quiet: true,
            text: nextText,
         });
      }
   }

   async function resolvePaataleberettigetForRow(row: ServitutRedegoerelsePayload["matrix"]["rows"][number]) {
      if (!prepared) return;
      if (!sessionState?.userRef || !sessionState?.userName || !sessionState?.customerRef) {
         message.error(Localization.getText("No organization selected"));
         return;
      }
      const historicalName = row.analysisItem?.paataleberettiget?.trim();
      if (!historicalName || !canResolvePaataleberettiget(row)) {
         message.error(Localization.getText("No historical entitled party to resolve"));
         return;
      }
      if (resolvingPaataleKeys.has(row.key)) return;

      setResolvingPaataleKeys((prev) => new Set(prev).add(row.key));
      try {
         const analyzer = createANNLLMAnalyzer({
            userRef: sessionState.userRef,
            userName: sessionState.userName,
            organizationId: sessionState.customerRef,
         });
         const metadata: LLMMetadata = {
            ...buildRowEvaluationMetadata(row, prepared, caseDescription),
            skipPaataleberettigetResolution: false,
         };
         let resultItem: LLMAnalysisResultItem | undefined;

         if (row.documentSource === "annotation" || (row.handling ?? "legacyLlmPdf") === "electronicZipPackage") {
            const pdfBlob = await getCutServitutPdfForContentAnalysis(row) || await getRowSourcePdfBlob(row);
            if (!pdfBlob) {
               throw new Error(Localization.getText("No PDF available for this document"));
            }
            resultItem = await analyzer.evaluateServitutDocument({
               metadata,
               pdfBlob,
               documentFileName:
                  row.annotationDocument?.filename ||
                  row.exportFileName ||
                  row.aktDocument ||
                  "servitut.pdf",
            });
         } else if (metadata.documentUrl) {
            const cutPdf = await getCutServitutPdfForContentAnalysis(row);
            if (cutPdf) {
               resultItem = await analyzer.evaluateServitutDocument({
                  metadata,
                  pdfBlob: cutPdf,
                  documentFileName: row.exportFileName || row.aktDocument || "servitut.pdf",
               });
            } else {
               const result = await analyzer.analyzePdfUrlBatch([metadata]);
               resultItem = result.items?.find((item) => item.id === row.key)
                  || result.items?.find((item) => item.aktId && item.aktId === row.analysisAktId)
                  || result.items?.[0];
            }
         } else {
            const pdfBlob = await getCutServitutPdfForContentAnalysis(row) || await getRowSourcePdfBlob(row);
            if (!pdfBlob) {
               throw new Error(Localization.getText("No PDF available for this document"));
            }
            resultItem = await analyzer.evaluateServitutDocument({
               metadata,
               pdfBlob,
               documentFileName: row.exportFileName || row.aktDocument || "servitut.pdf",
            });
         }

         const resolution = resultItem?.paataleberettiget_resolution
            || makeFailedPaataleResolution(historicalName, "not_found");
         const foundCurrent = Boolean(resolution.suggested_current_name);

         const mergedItem: LLMAnalysisResultItem = {
            ...(row.analysisItem || resultItem),
            ...resultItem,
            id: row.key,
            pages_kept: row.analysisItem?.pages_kept || resultItem?.pages_kept || [],
            original_pages: row.analysisItem?.original_pages ?? resultItem?.original_pages,
            extracted_pages: row.analysisItem?.extracted_pages ?? resultItem?.extracted_pages,
            paataleberettiget: row.analysisItem?.paataleberettiget || resultItem?.paataleberettiget || historicalName,
            paataleberettiget_confidence:
               row.analysisItem?.paataleberettiget_confidence ?? resultItem?.paataleberettiget_confidence,
            paataleberettiget_resolution: resolution,
         };

         await applyPaataleberettigetResolutionToRow(row, mergedItem);
         message.info(
            foundCurrent
               ? Localization.getText("Current entitled party suggestion ready.")
               : Localization.getText("Current entitled party AI could not find"),
         );
      } catch (error) {
         console.error("[Servitutredegørelse] påtaleberettiget resolution failed", row.key, error);
         const failedItem: LLMAnalysisResultItem = {
            ...(row.analysisItem || {
               id: row.key,
               aktId: row.analysisAktId || row.aktId || null,
               processing_method: "error",
               reasoning: "",
               pages_kept: row.selectedPages || [],
               original_pages: 0,
               extracted_pages: 0,
               paataleberettiget: historicalName,
            }),
            paataleberettiget: row.analysisItem?.paataleberettiget || historicalName,
            paataleberettiget_resolution: makeFailedPaataleResolution(historicalName, "error"),
         };
         await applyPaataleberettigetResolutionToRow(row, failedItem);
         message.error(error instanceof Error ? error.message : Localization.getText("Failed to resolve current entitled party"));
      } finally {
         setResolvingPaataleKeys((prev) => {
            const next = new Set(prev);
            next.delete(row.key);
            return next;
         });
      }
   }

   async function attachExistingAnnotationToRow(row: ServitutRedegoerelsePayload["matrix"]["rows"][number]) {
      const existing = await findExistingServitutPdfAnnotation(row, sessionState?.customerRef);
      if (!existing) {
         return;
      }
      setPrepared((currentPayload) => currentPayload ? {
         ...currentPayload,
         matrix: {
            ...currentPayload.matrix,
            rows: currentPayload.matrix.rows.map((currentRow) => currentRow.key === row.key ? {
               ...currentRow,
               annotationRecord: existing,
               annotationDocument: existing.document,
               customPdf: currentRow.customPdf,
               documentSource: currentRow.documentSource === "customUpload" ? "customUpload" : "annotation",
               pdfStatus: "ready",
               sourceMessage: existing.document?.llmAnalysis
                  ? Localization.getText("Using saved annotation analysis")
                  : Localization.getText("Using saved annotation PDF"),
               resultsAccepted: Boolean(existing.document?.llmAnalysis?.resultsAccepted) || currentRow.resultsAccepted,
               aktDocument: existing.document?.filename || currentRow.aktDocument,
            } : currentRow),
         },
      } : currentPayload);
   }

   async function exportBehandlede(mode: "excel" | "zip") {
      if (!prepared) return;
      const approvedRows = prepared.matrix.rows.filter(isRowApproved);
      if (!approvedRows.length) {
         message.error(Localization.getText("There are no treated easements to export."));
         return;
      }
      const exportPayload = {
         ...prepared,
         matrix: {
            ...prepared.matrix,
            rows: approvedRows,
         },
      };
      const includeAnalysisFields = phase === "B";

      if (mode === "excel") {
         setExporting(true);
         setExportProgress({
            completedSteps: 0,
            totalSteps: 1,
            stage: "spreadsheet",
            activeRowKey: null,
         });
         await yieldExportPaint();
         try {
            const excelBlob = buildServitutMatrixExcelBlob(exportPayload, { includeAnalysisFields });
            ExportData.downloadBlob(`Servitutredegørelse_${Date.now()}.xlsx`, excelBlob);
            message.info(Localization.getFormattedText("{count} treated easements have been exported.", { count: approvedRows.length }));
         } catch (error) {
            console.error("[Servitutredegørelse] excel export failed", error);
            message.error(error instanceof Error ? error.message : Localization.getText("Export failed"));
         } finally {
            setExporting(false);
            setExportProgress(null);
         }
         return;
      }

      const totalSteps = 2 + approvedRows.length;
      setExporting(true);
      setExportProgress({
         completedSteps: 0,
         totalSteps,
         stage: "spreadsheet",
         activeRowKey: null,
      });
      await yieldExportPaint();
      try {
         const zipBlob = await buildServitutRedegoerelseZip(
            exportPayload,
            async (prog) => {
            setExportProgress(prog);
            await yieldExportPaint();
         },
            { includeAnalysisFields },
         );
         ExportData.downloadBlob(`Servitutredegørelse_${Date.now()}.zip`, zipBlob);
         message.info(Localization.getFormattedText("{count} treated easements have been exported.", { count: approvedRows.length }));
      } catch (error) {
         console.error("[Servitutredegørelse] export failed", error);
         message.error(error instanceof Error ? error.message : Localization.getText("Export failed"));
      } finally {
         setExporting(false);
         setExportProgress(null);
      }
   }

   async function saveOneRowAsAnnotation(
      row: ServitutRedegoerelsePayload["matrix"]["rows"][number],
      options?: { pagesOnly?: boolean; pdfCache?: Map<string, Promise<Blob | null>> },
   ): Promise<"saved" | "skipped" | "unchanged"> {
      if (!sessionState?.userRef || !sessionState?.userName || !sessionState?.customerRef) {
         throw new Error(Localization.getText("No organization selected"));
      }
      const existingAnnotation = row.annotationRecord;
      const storedDocument = row.annotationDocument || existingAnnotation?.document;
      if (options?.pagesOnly && storedDocument?.s3Key && (row.documentSource === "annotation" || existingAnnotation)) {
         const storedPages = normalizePages(storedDocument.extractedPages || []);
         const pagesToKeep = normalizePages(row.selectedPages || []);
         const pagesUnchanged = areServitutPagesEqual(storedPages, pagesToKeep);
         if (pagesUnchanged && existingAnnotation?.verified) {
            return "unchanged";
         }
         const record = await saveAnnotation({
            type: AnnotationType.Document,
            documentId: existingAnnotation?.documentId || getCanonicalServitutAnnotationDocumentId(row),
            contextType: AnnotationContextType.Servitut,
            organizationId: sessionState.customerRef,
            userId: sessionState.userRef,
            userName: sessionState.userName,
            datoloebenr: existingAnnotation?.datoloebenr || row.datoLoebenr,
            document: {
               filename: storedDocument.filename,
               s3Key: storedDocument.s3Key,
               s3Url: storedDocument.s3Url,
               mimeType: storedDocument.mimeType || "application/pdf",
               size: storedDocument.size,
               llmAnalysis: storedDocument.llmAnalysis,
               provenance: getRowProvenance(row),
               ...(pagesToKeep.length ? { extractedPages: pagesToKeep } : {}),
            },
            title: existingAnnotation?.title || (storedDocument.filename || "").replace(/\.pdf$/i, ""),
            historyComment: [
               pagesToKeep.length
                  ? Localization.getFormattedText("Saved from servitutredegørelse. Pages: {pages}", {
                     pages: pagesToKeep.join(", "),
                  })
                  : Localization.getText("Saved from servitutredegørelse."),
               historyCommentForProvenance(getRowProvenance(row)),
            ].filter(Boolean).join(" · "),
            quality: undefined,
            verified: true,
            annotationType: existingAnnotation?.annotationType || "Dokument",
            id: existingAnnotation?.id,
            originalCreatedAt: existingAnnotation?.createdAt,
            originalCreatedBy: existingAnnotation?.createdBy,
            originalCreatedByName: existingAnnotation?.createdByName,
         });
         setPrepared((currentPayload) => {
            if (!currentPayload) {
               return currentPayload;
            }
            const next = {
               ...currentPayload,
               matrix: {
                  ...currentPayload.matrix,
                  rows: currentPayload.matrix.rows.map((currentRow) => currentRow.key === row.key ? {
                     ...currentRow,
                     annotationRecord: record,
                     annotationDocument: record.document,
                     documentSource: "annotation" as const,
                     pdfStatus: "ready" as const,
                     sourceMessage: Localization.getText("Using saved annotation PDF"),
                     aktDocument: record.document?.filename || currentRow.aktDocument,
                  } : currentRow),
               },
            };
            preparedRef.current = next;
            return next;
         });
         markAnalysisSaved([row.key]);
         return "saved";
      }
      if (!(options?.pagesOnly || canSaveServitutRowAsAnnotation(row))) {
         return "skipped";
      }
      const pdfCache = options?.pdfCache;
      const sourcePdf = options?.pagesOnly
         ? await getCachedRowPdfBlob(row, "original", pdfCache)
         : await getCachedRowPdfBlob(row, "source", pdfCache);
      if (!sourcePdf) {
         return "skipped";
      }
      const existingItems = await findAllExistingServitutPdfAnnotations(row, sessionState.customerRef);
      const overwriteTarget = existingItems[0];
      const pagesToKeep = normalizePages(row.selectedPages || []);
      const cutSource = sourcePdf.slice();
      const sourcePageCount = await getPdfPageCount(cutSource).catch(() => 0);
      const pagesToCut = sourcePageCount ? clampPagesToDocument(pagesToKeep, sourcePageCount) : pagesToKeep;
      const documentBlob = row.handling === "electronicZipPackage"
         ? cutSource
         : pagesToCut.length
            ? await cutPdfToSelectedPages(cutSource, pagesToCut)
            : cutSource;
      const filename = row.exportFileName || `${row.displayAktId || row.key}.pdf`;
      const file = new File([documentBlob], filename, { type: "application/pdf" });
      const uploadResult = await uploadOverlayImage(file);
      const documentId = overwriteTarget?.documentId || getCanonicalServitutAnnotationDocumentId(row);
      const documentPayload = {
         filename,
         s3Key: uploadResult.key,
         s3Url: uploadResult.url,
         mimeType: "application/pdf" as const,
         size: documentBlob.size,
         ...(pagesToKeep.length ? { extractedPages: pagesToKeep } : {}),
         provenance: getRowProvenance(row),
      };
      const record = await saveAnnotation({
         type: AnnotationType.Document,
         documentId,
         contextType: AnnotationContextType.Servitut,
         organizationId: sessionState.customerRef,
         userId: sessionState.userRef,
         userName: sessionState.userName,
         datoloebenr: overwriteTarget?.datoloebenr || row.datoLoebenr,
         document: { ...documentPayload, llmAnalysis: undefined },
         title: overwriteTarget?.title || filename.replace(/\.pdf$/i, ""),
         historyComment: [
            pagesToKeep.length
               ? Localization.getFormattedText("Saved from servitutredegørelse. Pages: {pages}", {
                  pages: pagesToKeep.join(", "),
               })
               : Localization.getText("Saved from servitutredegørelse."),
            historyCommentForProvenance(getRowProvenance(row)),
         ].filter(Boolean).join(" · "),
         quality: undefined,
         verified: true,
         annotationType: overwriteTarget?.annotationType || "Dokument",
         id: overwriteTarget?.id,
         originalCreatedAt: overwriteTarget?.createdAt,
         originalCreatedBy: overwriteTarget?.createdBy,
         originalCreatedByName: overwriteTarget?.createdByName,
      });
      for (const duplicate of existingItems.slice(1)) {
         if (!duplicate.id || duplicate.id === overwriteTarget?.id) {
            continue;
         }
         try {
            await deleteAnnotation(duplicate.id, sessionState.customerRef);
         } catch (deleteErr) {
            console.warn("[Servitutredegørelse] Failed to delete duplicate servitut text annotation", deleteErr);
         }
      }
      setPrepared((currentPayload) => {
         if (!currentPayload) {
            return currentPayload;
         }
         const next = {
            ...currentPayload,
            matrix: {
               ...currentPayload.matrix,
               rows: currentPayload.matrix.rows.map((currentRow) => currentRow.key === row.key ? {
                  ...currentRow,
                  annotationRecord: record,
                  annotationDocument: record.document,
                  documentSource: "annotation" as const,
                  pdfStatus: "ready" as const,
                  sourceMessage: Localization.getText("Using saved annotation PDF"),
                  aktDocument: record.document?.filename || currentRow.aktDocument,
               } : currentRow),
            },
         };
         preparedRef.current = next;
         return next;
      });
      markAnalysisSaved([row.key]);
      return "saved";
   }

   async function saveRowsAsAnnotations(
      rows: ServitutRedegoerelsePayload["matrix"]["rows"],
      options?: { quiet?: boolean; pagesOnly?: boolean },
   ) {
      if (!sessionState?.userRef || !sessionState?.userName || !sessionState?.customerRef) {
         message.error(Localization.getText("No organization selected"));
         return;
      }
      if (!rows.length) {
         return;
      }

      if (!options?.quiet) {
         setJustSavedKeys(new Set());
      }
      setSavingAnnotationKeys((prev) => {
         const next = new Set(prev);
         rows.forEach((row) => next.add(row.key));
         return next;
      });
      const pdfCache = new Map<string, Promise<Blob | null>>();
      const queued = rows.map((row) => {
         const gate = createDeferred();
         const job = gate.promise.then(() => saveOneRowAsAnnotation(row, { ...options, pdfCache }));
         registerSaveJob(row.key, job);
         return { row, gate, job };
      });
      const metadataQueued = queued.filter((item) => canSaveRowWithoutPdfFetch(item.row, options?.pagesOnly));
      const pdfQueued = queued.filter((item) => !canSaveRowWithoutPdfFetch(item.row, options?.pagesOnly));
      let savedCount = 0;
      let skippedCount = 0;
      let unchangedCount = 0;
      let failedCount = 0;
      const runQueued = async (
         items: typeof queued,
         concurrency: number,
      ) => {
         await runWithConcurrency(items, concurrency, async (item) => {
            item.gate.resolve();
            try {
               const status = await item.job;
               if (status === "saved") {
                  savedCount += 1;
               } else if (status === "unchanged") {
                  unchangedCount += 1;
               } else {
                  skippedCount += 1;
               }
            } catch (error) {
               failedCount += 1;
               console.error("[Servitutredegørelse] failed to save annotation", item.row.key, error);
            } finally {
               setSavingAnnotationKeys((prev) => {
                  const next = new Set(prev);
                  next.delete(item.row.key);
                  return next;
               });
            }
         });
      };
      await Promise.all([
         runQueued(metadataQueued, SERVITUT_NOTE_SAVE_CONCURRENCY),
         runQueued(pdfQueued, SERVITUT_PDF_SAVE_CONCURRENCY),
      ]);
      if (options?.quiet) {
         if (failedCount) {
            message.error(Localization.getText("Failed to save document"));
         } else if (skippedCount && !savedCount && !unchangedCount) {
            message.error(Localization.getText("No PDF available for this document"));
         }
      } else {
         message.info(Localization.getFormattedText("Saved {saved} annotations. Skipped {skipped}. Failed {failed}.", {
            saved: savedCount,
            skipped: skippedCount,
            failed: failedCount,
         }));
      }
   }

   function applyPageApprovals(rows: ServitutRedegoerelsePayload["matrix"]["rows"]) {
      const plans = rows
         .filter((row) => !saveJobsRef.current.has(row.key) && !savingAnnotationKeys.has(row.key))
         .filter((row) => !locateJobKeysRef.current.has(row.key) && !contentJobKeysRef.current.has(row.key) && !reevaluatingKeys.has(row.key))
         .map((row) => ({ row, plan: getRowPageApprovalPlan(row) }))
         .filter((entry) => entry.plan.canApprove);
      if (!plans.length) {
         return;
      }
      const byKey = new Map(plans.map((entry) => [entry.row.key, entry]));
      setPrepared((current) => current ? {
         ...current,
         matrix: {
            ...current.matrix,
            rows: current.matrix.rows.map((item) => {
               const entry = byKey.get(item.key);
               if (!entry) {
                  return item;
               }
               const reviewOrigin = entry.plan.differs || !hasAiPageSuggestion(item)
                  ? "person"
                  : "ai";
               const nextProvenance = entry.plan.differs
                  ? provenanceAfterDerivedInvalidation(provenanceAfterPageReview(
                     getRowProvenance(item),
                     "checked",
                     reviewOrigin,
                  ))
                  : provenanceAfterPageReview(
                     getRowProvenance(item),
                     "checked",
                     reviewOrigin,
                  );
               return {
                  ...item,
                  selectedPages: entry.plan.pagesToSave,
                  provenance: nextProvenance,
                  ...(entry.plan.differs ? { fieldAnnotations: {}, analysisItem: undefined, resultsAccepted: false } : {}),
               };
            }),
         },
      } : current);
      void saveRowsAsAnnotations(plans.map((entry) => {
         const reviewOrigin = entry.plan.differs || !hasAiPageSuggestion(entry.row)
            ? "person"
            : "ai";
         const nextProvenance = entry.plan.differs
            ? provenanceAfterDerivedInvalidation(provenanceAfterPageReview(
               getRowProvenance(entry.row),
               "checked",
               reviewOrigin,
            ))
            : provenanceAfterPageReview(
               getRowProvenance(entry.row),
               "checked",
               reviewOrigin,
            );
         return {
            ...entry.row,
            provenance: nextProvenance,
            selectedPages: entry.plan.pagesToSave,
         };
      }), {
         quiet: true,
         pagesOnly: true,
      });
      const invalidated = plans.filter((entry) => entry.plan.differs);
      invalidated.forEach((entry) => {
         void deleteRowFieldNotes(entry.row);
      });
      if (invalidated.some((entry) => Object.keys(entry.row.fieldAnnotations || {}).length)) {
         message.info(Localization.getText("Derived analysis annotations were marked outdated"));
      }
   }

   function approveRowPages(row: ServitutRedegoerelsePayload["matrix"]["rows"][number]) {
      if (isRowApproved(row)) {
         return;
      }
      if (locateJobKeysRef.current.has(row.key) || contentJobKeysRef.current.has(row.key) || reevaluatingKeys.has(row.key)) {
         message.info(Localization.getText("Cannot save while AI is running"));
         return;
      }
      const plan = getRowPageApprovalPlan(row);
      if (!plan.canApprove) {
         message.error(Localization.getText("No PDF available for this document"));
         return;
      }
      const apply = () => applyPageApprovals([row]);
      if (plan.differs) {
         windowDispatch(openWindow(WindowId.ConfirmationDialog, {
            header: "Save pages different from AI",
            children: Localization.getText("You are saving a page selection that is not the AI suggestion. Continue?"),
            submitLabel: "Yes",
            cancelLabel: "No",
            onSubmit: apply,
         }));
         return;
      }
      apply();
   }

   async function fetchLandRegistryForRow(rowKey: string) {
      const payload = preparedRef.current;
      const latest = payload?.matrix.rows.find((item) => item.key === rowKey);
      if (!payload || !latest) {
         return;
      }
      try {
         const bound = await bindPreparedRowToLandRegistry(latest, {
            stamOplysninger: stamOplysningerHintForRow(payload, latest),
         });
         setPrepared((current) => {
            if (!current) {
               return current;
            }
            const currentRow = current.matrix.rows.find((item) => item.key === rowKey) || latest;
            const nextRow = {
               ...clearedRowAfterAcceptRevert(currentRow),
               ...landRegistryBindPatch(bound.row),
            };
            return {
               ...current,
               metadata: replaceRowMetadata(current.metadata, rowKey, bound.metadata),
               matrix: {
                  ...current.matrix,
                  rows: current.matrix.rows.map((item) => item.key === rowKey ? nextRow : item),
               },
            };
         });
      } catch (error) {
         console.error("[Servitutredegørelse] failed to fetch akt after accept revert", rowKey, error);
         message.error(error instanceof Error ? error.message : Localization.getText("Failed to open scanned akt PDF"));
      }
   }

   async function revertRowPages(
      row: ServitutRedegoerelsePayload["matrix"]["rows"][number],
      options?: { quiet?: boolean },
   ) {
      if (!isRowApproved(row)) {
         return;
      }
      if (locateJobKeysRef.current.has(row.key) || contentJobKeysRef.current.has(row.key) || reevaluatingKeys.has(row.key)) {
         if (!options?.quiet) {
            message.info(Localization.getText("Cannot save while AI is running"));
         }
         return;
      }
      setSavingAnnotationKeys((prev) => new Set(prev).add(row.key));
      const fetchLandRegistry = needsLandRegistryFetchAfterAcceptRevert(row);
      try {
         await deleteAllServitutAnnotationsForRow(row);
      } catch (error) {
         console.error("[Servitutredegørelse] failed to revert page approval", row.key, error);
         if (!options?.quiet) {
            message.error(error instanceof Error ? error.message : Localization.getText("Failed to save document"));
         }
         setSavingAnnotationKeys((prev) => {
            const next = new Set(prev);
            next.delete(row.key);
            return next;
         });
         return;
      }
      forgetAcceptedReview([row.key]);
      setPrepared((current) => {
         if (!current) {
            return current;
         }
         const latest = current.matrix.rows.find((item) => item.key === row.key) || row;
         const restored = clearedRowAfterAcceptRevert(latest);
         return {
            ...current,
            matrix: {
               ...current.matrix,
               rows: current.matrix.rows.map((item) => item.key === row.key ? restored : item),
            },
         };
      });
      try {
         if (fetchLandRegistry) {
            await fetchLandRegistryForRow(row.key);
         }
      } finally {
         setSavingAnnotationKeys((prev) => {
            const next = new Set(prev);
            next.delete(row.key);
            return next;
         });
      }
   }

   function approveAllPages(rows: ServitutRedegoerelsePayload["matrix"]["rows"]) {
      const eligible = rows.filter((row) => (
         !isRowApproved(row)
         && getRowPageApprovalPlan(row).canApprove
         && !locateJobKeysRef.current.has(row.key)
         && !contentJobKeysRef.current.has(row.key)
         && !reevaluatingKeys.has(row.key)
      ));
      if (!eligible.length) {
         return;
      }
      const apply = () => applyPageApprovals(eligible);
      if (eligible.some((row) => getRowPageApprovalPlan(row).differs)) {
         windowDispatch(openWindow(WindowId.ConfirmationDialog, {
            header: "Save pages different from AI",
            children: Localization.getText("You are saving a page selection that is not the AI suggestion. Continue?"),
            submitLabel: "Yes",
            cancelLabel: "No",
            onSubmit: apply,
         }));
         return;
      }
      apply();
   }

   async function revertAllPages(rows: ServitutRedegoerelsePayload["matrix"]["rows"]) {
      const eligible = rows.filter((row) => (
         isRowApproved(row)
         && !locateJobKeysRef.current.has(row.key)
         && !contentJobKeysRef.current.has(row.key)
         && !reevaluatingKeys.has(row.key)
      ));
      if (!eligible.length) {
         return;
      }
      await runWithConcurrency(eligible, SERVITUT_NOTE_SAVE_CONCURRENCY, async (row) => {
         await revertRowPages(row, { quiet: true });
      });
      message.info(Localization.getFormattedText("Undid {count} page approvals", { count: eligible.length }));
   }

   return (
      <AdvancedDragModal
         window={props.managedWindow}
         title={Localization.getText("Easement review")}
         onClose={() => windowDispatch(closeWindow(props.managedWindow.id))}
         allowFullscreen={true}
         allowUserMiniMize={true}
         allowPopout={false}
         PosDefault={{ top: "48px", left: "48px", width: "1360px", height: "860px" }}
      >
         <ADModalBody>
            {prepared ? (
               <>
               <ServitutMatrix
                  payload={prepared}
                  analyzing={analyzing}
                  aiJobKeys={aiJobKeys}
                  analysisProgress={analysisProgress}
                  openingKey={openingKey}
                  exporting={exporting}
                  exportProgress={exportProgress}
                  savingAnnotationKeys={savingAnnotationKeys}
                  justSavedKeys={justSavedKeys}
                  unsavedChangeKeys={unsavedChangeKeys}
                  reevaluatingKeys={reevaluatingKeys}
                  resolvingPaataleKeys={resolvingPaataleKeys}
                  analysisField={analysisField}
                  onAnalysisFieldChange={setAnalysisField}
                  onOpenRow={openValidationViewer}
                  onAcceptField={(row, text) => { void acceptAnalysisField(row, analysisField, { text }); }}
                  onUndoField={(row) => { void undoAnalysisField(row, analysisField); }}
                  onAcceptAllField={(entries) => { void acceptAllAnalysisField(entries, analysisField); }}
                  onUndoAllField={(rows) => { void undoAllAnalysisField(rows, analysisField); }}
                  onResolvePaataleberettiget={resolvePaataleberettigetForRow}
                  onUploadCustomPdf={(row, file) => { void uploadCustomPdfForRow(row, file); }}
                  onAnalyze={phase === "B" ? startContentAnalysis : startLocatePages}
                  onStopAnalysis={stopAnalysis}
                  onRunRowAi={(row) => { void runAiForRow(row); }}
                  canAnalyze={phase === "B" ? hasContentAnalyzableRows(prepared) : hasLocatableServitutRows(prepared)}
                  caseDescription={caseDescription}
                  caseDescriptionLocked={false}
                  onCaseDescriptionChange={setCaseDescription}
                  onExport={exportBehandlede}
                  phase={phase}
                  onPhaseChange={requestPhase}
                  canSeeA={canSeeA}
                  canSeeB={canSeeB}
                  canWriteA={canWriteA}
                  canWriteB={canWriteB}
                  incompleteACount={incompleteARows.length}
                  rejectedACount={rejectedARows.length}
                  onCompareFolder={() => setShowFolderCompare(true)}
                  onOpenStedfaestelse={(row) => {
                     lastStedfaestelseRowKey = row.key;
                     stedfaestelseRowKeyRef.current = row.key;
                     const identifier = getCanonicalServitutAnnotationDocumentId(row);
                     const restoreMinimizedWindowIds = minimizeOpenWindowsForStedfaestelse(windowState, windowDispatch);
                     editorDispatch({
                        type: ANNEditorActionType.Open,
                        payload: { contextType: AnnotationContextType.Servitut, identifier },
                     });
                     windowDispatch(openWindow(WindowId.ANNEditor, {
                        contextType: AnnotationContextType.Servitut,
                        identifier,
                        datoloebenr: row.datoLoebenr,
                        restoreMinimizedWindowIds,
                     }));
                  }}
                  onApprovePages={(row) => {
                     approveRowPages(row);
                  }}
                  onRevertPages={(row) => {
                     void revertRowPages(row);
                  }}
                  onApproveAllPages={(rows) => {
                     approveAllPages(rows);
                  }}
                  onRevertAllPages={(rows) => {
                     void revertAllPages(rows);
                  }}
               />
               {showFolderCompare ? (
                  <ServitutFolderCompareModal
                     rows={prepared.matrix.rows}
                     onClose={() => setShowFolderCompare(false)}
                  />
               ) : null}
               </>
            ) : (
               <ServitutRedegoerelseBootProgress progress={loadProgress} error={loadError} />
            )}
         </ADModalBody>
      </AdvancedDragModal>
   );
}

function servitutLoadProgressPercent(p: ServitutRedegoerelseLoadProgress): number {
   if (p.total < 1) {
      return 0;
   }
   return Math.min(100, Math.round((p.completed / p.total) * 100));
}

function servitutLoadProgressCaption(p: ServitutRedegoerelseLoadProgress): string {
   if (p.stage === "registry") {
      return p.total > 1
         ? Localization.getText("Fetching properties from the land registry…")
         : Localization.getText("Preparing easement review from land registry…");
   }
   return Localization.getText("Loading easements…");
}

function ServitutRedegoerelseBootProgress(props: {
   progress: ServitutRedegoerelseLoadProgress | null;
   error: string | null;
}) {
   if (props.error) {
      return (
         <div className="servitut-redegoerelse servitut-redegoerelse--boot">
            <div className="servitut-redegoerelse__boot" role="alert">
               <div className="servitut-redegoerelse__bootTitle">{props.error}</div>
            </div>
         </div>
      );
   }
   const progress = props.progress;
   const indeterminate = !progress || progress.total < 1 || (progress.stage === "registry" && progress.completed < 1);
   const percent = progress ? servitutLoadProgressPercent(progress) : 0;
   return (
      <div className="servitut-redegoerelse servitut-redegoerelse--boot">
         <div className="servitut-redegoerelse__boot" role="status" aria-live="polite">
            <Spinner animation="border" role="status" />
            <div className="servitut-redegoerelse__bootTitle">
               {progress ? servitutLoadProgressCaption(progress) : Localization.getText("Loading easements…")}
            </div>
            <div
               className="servitut-redegoerelse__bootBar"
               role="progressbar"
               aria-valuemin={0}
               aria-valuemax={progress?.total || 100}
               aria-valuenow={indeterminate ? undefined : progress?.completed}
               aria-label={progress ? servitutLoadProgressCaption(progress) : Localization.getText("Loading easements…")}
            >
               <div className="servitut-redegoerelse__bootTrack">
                  <div
                     className={`servitut-redegoerelse__bootFill${indeterminate ? " servitut-redegoerelse__bootFill--indeterminate" : ""}`}
                     style={indeterminate ? undefined : { width: `${percent}%` }}
                  />
               </div>
               {progress && progress.total > 0 ? (
                  <span className="servitut-redegoerelse__bootCount">
                     {progress.completed}/{progress.total}
                  </span>
               ) : null}
            </div>
         </div>
      </div>
   );
}

function servitutExportProgressPercent(p: ServitutRedegoerelseExportProgress): number {
   if (!p.totalSteps) {
      return 0;
   }
   return Math.min(100, Math.round((p.completedSteps / p.totalSteps) * 100));
}

function servitutExportProgressCaption(p: ServitutRedegoerelseExportProgress): string {
   if (p.stage === "spreadsheet" && p.completedSteps === 0) {
      return Localization.getText("Preparing ZIP export…");
   }
   if (p.stage === "spreadsheet") {
      return Localization.getText("Adding Excel to the archive…");
   }
   if (p.stage === "compress" && p.completedSteps < p.totalSteps) {
      return Localization.getText("Compressing ZIP…");
   }
   if (p.stage === "compress") {
      return Localization.getText("Finishing…");
   }
   if (p.documentIndex !== undefined && p.documentTotal !== undefined) {
      return Localization.getFormattedText("Fetching and adding document {index} of {total}…", {
         index: p.documentIndex,
         total: p.documentTotal,
      });
   }
   return Localization.getText("Exporting…");
}

const BFE_HEAD_POPOVER_HIDE_MS = 140;

function ServitutBfeColumnHeadCell(props: {
   bfe: number;
   shortLabel: string;
   popover: ServitutMatrixColumnDetail;
   ariaLabel: string;
}) {
   const wrapRef = useRef<HTMLDivElement | null>(null);
   const innerRef = useRef<HTMLSpanElement | null>(null);
   const [labelBox, setLabelBox] = useState<{ w: number; h: number } | null>(null);
   const [open, setOpen] = useState(false);
   const [pos, setPos] = useState({ top: 0, left: 0 });
   const hideTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

   const measureLabel = useCallback(() => {
      const inner = innerRef.current;
      if (!inner) return;
      const r = inner.getBoundingClientRect();
      /** Axis-aligned bounds of the rotated label — drives column width and header row height. */
      const horizontalPad = 12;
      const bottomPad = 12;
      setLabelBox({
         w: Math.ceil(r.width) + horizontalPad,
         h: Math.ceil(r.height) + bottomPad,
      });
   }, []);

   useLayoutEffect(() => {
      measureLabel();
   }, [measureLabel, props.shortLabel]);

   useEffect(() => {
      const inner = innerRef.current;
      if (!inner) return;
      const ro = new ResizeObserver(() => measureLabel());
      ro.observe(inner);
      return () => ro.disconnect();
   }, [measureLabel]);

   useEffect(() => {
      const onResize = () => measureLabel();
      window.addEventListener("resize", onResize);
      return () => window.removeEventListener("resize", onResize);
   }, [measureLabel]);

   useEffect(() => {
      const fonts = document.fonts;
      if (!fonts?.ready) return;
      let cancelled = false;
      fonts.ready.then(() => {
         if (!cancelled) measureLabel();
      });
      return () => {
         cancelled = true;
      };
   }, [measureLabel]);

   const clearHideTimer = () => {
      if (hideTimerRef.current !== null) {
         clearTimeout(hideTimerRef.current);
         hideTimerRef.current = null;
      }
   };

   const updatePosition = () => {
      const el = wrapRef.current;
      if (!el) return;
      const r = el.getBoundingClientRect();
      const margin = 8;
      setPos({ top: r.bottom + margin, left: r.left });
   };

   const openPopover = () => {
      clearHideTimer();
      updatePosition();
      setOpen(true);
   };

   const scheduleClose = () => {
      clearHideTimer();
      hideTimerRef.current = setTimeout(() => setOpen(false), BFE_HEAD_POPOVER_HIDE_MS);
   };

   useLayoutEffect(() => {
      if (!open) return;
      updatePosition();
   }, [open, props.shortLabel]);

   useEffect(() => {
      if (!open) return;
      const close = () => setOpen(false);
      window.addEventListener("scroll", close, true);
      window.addEventListener("resize", close);
      return () => {
         window.removeEventListener("scroll", close, true);
         window.removeEventListener("resize", close);
      };
   }, [open]);

   useEffect(() => () => clearHideTimer(), []);

   const popoverContent = (
      <div
         className="servitut-redegoerelse__bfePopover"
         style={{ top: pos.top, left: pos.left }}
         onMouseEnter={clearHideTimer}
         onMouseLeave={scheduleClose}
         role="tooltip"
      >
         <div className="servitut-redegoerelse__bfePopoverBfe">
            {Localization.getFormattedText("BFE {bfe}", { bfe: props.bfe })}
         </div>
         {props.popover.ejerlav ? (
            <div className="servitut-redegoerelse__bfePopoverEjerlav">
               <span className="servitut-redegoerelse__bfePopoverK">{Localization.getText("Cadastral district (ejerlav)")}</span>
               {props.popover.ejerlav}
            </div>
         ) : null}
         <div className="servitut-redegoerelse__bfePopoverMatrikelBlock">
            <span className="servitut-redegoerelse__bfePopoverK">{Localization.getText("Cadastral plots")}</span>
            <ul className="servitut-redegoerelse__bfePopoverList">
               {props.popover.matrikler.map((m, index) => (
                  <li key={`${props.bfe}-${index}-${m}`}>{m}</li>
               ))}
            </ul>
         </div>
      </div>
   );

   const thStyle: ServitutBfeHeaderStyle | undefined = labelBox
      ? ({
         "--servitut-bfe-head-width": `${labelBox.w}px`,
         "--servitut-bfe-head-height": `${labelBox.h}px`,
      } as ServitutBfeHeaderStyle)
      : undefined;

   return (
      <th
         className="servitut-redegoerelse__bfeHead"
         style={thStyle}
         aria-label={props.ariaLabel}
      >
         <div
            ref={wrapRef}
            className="servitut-redegoerelse__bfeHeadTrigger"
            onMouseEnter={openPopover}
            onMouseLeave={scheduleClose}
         >
            <span ref={innerRef} className="servitut-redegoerelse__bfeHeadInner">
               <span className="servitut-redegoerelse__bfeHeadMatrikel">{props.shortLabel}</span>
            </span>
         </div>
         {open ? createPortal(popoverContent, document.body) : null}
      </th>
   );
}

function ServitutMatrix(props: {
   payload: ServitutRedegoerelsePayload;
   analyzing: boolean;
   aiJobKeys: Set<string>;
   analysisProgress: { completed: number; total: number } | null;
   openingKey: string | null;
   exporting: boolean;
   exportProgress: ServitutRedegoerelseExportProgress | null;
   savingAnnotationKeys: Set<string>;
   justSavedKeys: Set<string>;
   unsavedChangeKeys: Set<string>;
   reevaluatingKeys: Set<string>;
   resolvingPaataleKeys: Set<string>;
   analysisField: ServitutAnalysisFieldKey;
   onAnalysisFieldChange: (next: ServitutAnalysisFieldKey) => void;
   onOpenRow: (row: ServitutRedegoerelsePayload["matrix"]["rows"][number]) => void;
   onAcceptField: (row: ServitutRedegoerelsePayload["matrix"]["rows"][number], text?: string) => void | Promise<void>;
   onUndoField: (row: ServitutRedegoerelsePayload["matrix"]["rows"][number]) => void;
   onAcceptAllField: (entries: Array<{ row: ServitutRedegoerelsePayload["matrix"]["rows"][number]; text?: string }>) => void;
   onUndoAllField?: (rows: Array<ServitutRedegoerelsePayload["matrix"]["rows"][number]>) => void;
   onResolvePaataleberettiget: (row: ServitutRedegoerelsePayload["matrix"]["rows"][number]) => void;
   onUploadCustomPdf: (row: ServitutRedegoerelsePayload["matrix"]["rows"][number], file: File) => void;
   onAnalyze: () => void;
   onStopAnalysis: () => void;
   onRunRowAi?: (row: ServitutRedegoerelsePayload["matrix"]["rows"][number]) => void;
   canAnalyze: boolean;
   caseDescription: string;
   caseDescriptionLocked: boolean;
   onCaseDescriptionChange: (value: string) => void;
   onExport: (mode: "excel" | "zip") => void;
   phase?: ServitutRedegoerelsePhase;
   onPhaseChange?: (phase: ServitutRedegoerelsePhase) => void;
   canSeeA?: boolean;
   canSeeB?: boolean;
   canWriteA?: boolean;
   canWriteB?: boolean;
   incompleteACount?: number;
   rejectedACount?: number;
   onCompareFolder?: () => void;
   onApprovePages?: (row: ServitutRedegoerelsePayload["matrix"]["rows"][number]) => void;
   onRevertPages?: (row: ServitutRedegoerelsePayload["matrix"]["rows"][number]) => void;
   onApproveAllPages?: (rows: ServitutRedegoerelsePayload["matrix"]["rows"]) => void;
   onRevertAllPages?: (rows: ServitutRedegoerelsePayload["matrix"]["rows"]) => void;
   onOpenStedfaestelse?: (row: ServitutRedegoerelsePayload["matrix"]["rows"][number]) => void;
}) {
   const phase = props.phase || "A";
   const showAnalysisColumns = phase === "B";
   const uploadInputRef = useRef<HTMLInputElement | null>(null);
   const uploadRowRef = useRef<ServitutRedegoerelsePayload["matrix"]["rows"][number] | null>(null);
   const exportMenuRef = useRef<HTMLDetailsElement | null>(null);
   const [query, setQuery] = useState("");
   const [statusFilter, setStatusFilter] = useState<"all" | "found" | "uncertain" | "missing" | "treated" | "untreated">("all");
   const [matrixSort, setMatrixSort] = useState<ServitutMatrixSortState>(null);
   const [fieldDrafts, setFieldDrafts] = useState<Record<string, string>>({});
   const [editingFieldKey, setEditingFieldKey] = useState<string | null>(null);
   const [bTab, setBTab] = useState<ServitutBTabKey>(props.analysisField);
   const [paataleNoticeKey, setPaataleNoticeKey] = useState<string | null>(null);
   const isStedfaestelseTab = bTab === "stedfaestelse";
   const selectBTab = (tab: ServitutBTabKey) => {
      setBTab(tab);
      if (tab !== "stedfaestelse") {
         props.onAnalysisFieldChange(tab);
      }
   };
   const fieldDraftKey = (rowKey: string) => `${rowKey}:${props.analysisField}`;
   const displayedFieldText = (row: ServitutRedegoerelsePayload["matrix"]["rows"][number]) => (
      fieldDrafts[fieldDraftKey(row.key)] ?? getRowFieldText(row, props.analysisField, props.caseDescription)
   );
   const clearFieldDraft = (rowKey: string) => {
      setFieldDrafts((prev) => {
         const key = fieldDraftKey(rowKey);
         if (!(key in prev)) {
            return prev;
         }
         const copy = { ...prev };
         delete copy[key];
         return copy;
      });
   };
   const stopEditingField = (rowKey?: string) => {
      setEditingFieldKey((current) => (
         !rowKey || current === fieldDraftKey(rowKey) ? null : current
      ));
   };
   useEffect(() => {
      setEditingFieldKey(null);
   }, [props.analysisField]);
   const previousPaataleStampsRef = useRef<Record<string, string>>({});
   useEffect(() => {
      const nextStamps: Record<string, string> = {};
      const changedKeys: string[] = [];
      props.payload.matrix.rows.forEach((row) => {
         const stamp = row.analysisItem?.paataleberettiget_resolution?.searched_at || "";
         nextStamps[row.key] = stamp;
         if (previousPaataleStampsRef.current[row.key] !== undefined && previousPaataleStampsRef.current[row.key] !== stamp) {
            changedKeys.push(row.key);
         }
      });
      previousPaataleStampsRef.current = nextStamps;
      if (!changedKeys.length) {
         return;
      }
      setFieldDrafts((prev) => {
         const copy = { ...prev };
         let changed = false;
         changedKeys.forEach((rowKey) => {
            const key = `${rowKey}:entitledParty`;
            if (key in copy) {
               delete copy[key];
               changed = true;
            }
         });
         return changed ? copy : prev;
      });
   }, [props.payload.matrix.rows]);
   const normalizedQuery = query.trim().toLowerCase();
   // Prefer the live editor value so a case typed after import (before full re-run) updates the pill.
   const hasCaseDescription = Boolean(props.caseDescription.trim() || props.payload.caseDescription?.trim());
   const filteredRows = useMemo(() => {
      const rows = props.payload.matrix.rows.filter((row) => {
         const handling = row.handling ?? "legacyLlmPdf";
         const analysisSearchText = [
            row.analysisItem?.servitut_type,
            row.analysisItem?.paataleberettiget,
            row.analysisItem?.paataleberettiget_resolution?.suggested_current_name,
            row.analysisItem?.paataleberettiget_resolution?.reasoning,
            ...(row.analysisItem?.paataleberettiget_resolution?.alternatives || []).map((alternative) => alternative.name),
            row.analysisItem?.case_relevance_reasoning,
            row.analysisItem?.overall_confidence_reasoning,
            ...(row.analysisItem?.review_reasons || []),
            formatCaseRelevantLabel(row.analysisItem?.case_relevant),
         ].filter(Boolean).join(" ").toLowerCase();
         const matchesQuery = !normalizedQuery ||
            row.datoLoebenr.toLowerCase().includes(normalizedQuery) ||
            row.displayAktId.toLowerCase().includes(normalizedQuery) ||
            row.summary.toLowerCase().includes(normalizedQuery) ||
            analysisSearchText.includes(normalizedQuery) ||
            row.includedBfes.some((bfe) => `${bfe}`.includes(normalizedQuery));
         if (!matchesQuery) return false;
         if (phase === "B" && !isRowApproved(row)) return false;
         if (statusFilter === "found") {
            const source = getRowPageSourceStatus(row);
            return (source.kind === "ai_pages" && source.confident)
               || (handling === "electronicZipPackage" && row.pdfStatus === "ready");
         }
         if (statusFilter === "uncertain") {
            const source = getRowPageSourceStatus(row);
            return source.kind === "ai_pages" && !source.confident;
         }
         if (statusFilter === "missing") {
            return handling === "legacyLlmPdf"
               && Boolean(row.analysisItem)
               && !(row.analysisItem?.pages_kept?.length);
         }
         if (statusFilter === "treated") {
            return phase === "B" ? isRowFieldAccepted(row, props.analysisField) : isRowApproved(row);
         }
         if (statusFilter === "untreated") {
            return phase === "B" ? !isRowFieldAccepted(row, props.analysisField) : !isRowApproved(row);
         }
         return true;
      });
      return applyServitutMatrixSort(rows, props.payload.matrix.columns, hasCaseDescription, matrixSort);
   }, [hasCaseDescription, matrixSort, normalizedQuery, phase, props.analysisField, props.caseDescription, props.payload.caseDescription, props.payload.matrix.columns, props.payload.matrix.rows, statusFilter]);
   const contentAnalyzedCount = props.payload.matrix.rows.filter((row) => hasContentAnalysisFields(row.analysisItem)).length;
   const approvedCount = props.payload.matrix.rows.filter(isRowApproved).length;
   const fieldAcceptableRows = filteredRows.filter((row) => (
      displayedFieldText(row).trim()
      && !isRowFieldAccepted(row, props.analysisField)
      && !isServitutRowAwaitingAi(row, {
         aiJobKeys: props.aiJobKeys,
         reevaluatingKeys: props.reevaluatingKeys,
      })
   ));
   const pageApprovableRows = filteredRows.filter((row) => (
      !isRowApproved(row)
      && getRowPageApprovalPlan(row).canApprove
      && !props.savingAnnotationKeys.has(row.key)
      && !isServitutRowAwaitingAi(row, {
         aiJobKeys: props.aiJobKeys,
         reevaluatingKeys: props.reevaluatingKeys,
      })
   ));
   const pageRevertableRows = filteredRows.filter((row) => (
      isRowApproved(row)
      && !props.savingAnnotationKeys.has(row.key)
      && !isServitutRowAwaitingAi(row, {
         aiJobKeys: props.aiJobKeys,
         reevaluatingKeys: props.reevaluatingKeys,
      })
   ));
   const fieldUndoableRows = filteredRows.filter((row) => (
      isRowFieldAccepted(row, props.analysisField)
      && !props.savingAnnotationKeys.has(row.key)
      && !isServitutRowAwaitingAi(row, {
         aiJobKeys: props.aiJobKeys,
         reevaluatingKeys: props.reevaluatingKeys,
      })
   ));
   const cycleMatrixSort = (column: ServitutMatrixSortColumn) => {
      setMatrixSort((prev) => {
         if (!prev || prev.column !== column) {
            const direction = column === "confidence" ? "desc" : "asc";
            return { column, direction };
         }
         if (column === "confidence") {
            if (prev.direction === "desc") return { column, direction: "asc" };
            return null;
         }
         if (prev.direction === "asc") return { column, direction: "desc" };
         return null;
      });
   };
   const matrixSortIndicator = (column: ServitutMatrixSortColumn) => {
      if (!matrixSort || matrixSort.column !== column) return "↕";
      return matrixSort.direction === "asc" ? "↑" : "↓";
   };
   const copyTextToClipboard = async (text: string) => {
      const value = text.trim();
      if (!value) {
         message.error(Localization.getText("Nothing to copy"));
         return;
      }
      try {
         await navigator.clipboard.writeText(value);
         message.info(Localization.getText("Copied to clipboard"));
      } catch (error) {
         console.error("[Servitutredegørelse] clipboard copy failed", error);
         message.error(Localization.getText("Failed to copy to clipboard"));
      }
   };

   const exportProgressPct = props.exportProgress
      ? servitutExportProgressPercent(props.exportProgress)
      : 0;
   const exportProgressLabel = props.exportProgress
      ? servitutExportProgressCaption(props.exportProgress)
      : "";
   const paataleNoticeRow = paataleNoticeKey
      ? props.payload.matrix.rows.find((row) => row.key === paataleNoticeKey)
      : undefined;
   const paataleNoticeResolution = paataleNoticeRow?.analysisItem?.paataleberettiget_resolution;

   return (
      <div className="servitut-redegoerelse">
         <div className="servitut-redegoerelse__hero">
            <div>
               <div className="servitut-redegoerelse__eyebrow">{Localization.getText("Easement review")}</div>
               {props.canSeeB !== false ? (
                  <div className="servitut-redegoerelse__phaseTabs" role="tablist">
                     {props.canSeeA !== false ? (
                        <button
                           type="button"
                           role="tab"
                           aria-selected={phase === "A"}
                           className={`servitut-redegoerelse__phaseTab ${phase === "A" ? "servitut-redegoerelse__phaseTab--active" : ""}`}
                           onClick={() => props.onPhaseChange?.("A")}
                        >
                           {Localization.getText("A: Find and control")}
                        </button>
                     ) : null}
                     <button
                        type="button"
                        role="tab"
                        aria-selected={phase === "B"}
                        className={`servitut-redegoerelse__phaseTab ${phase === "B" ? "servitut-redegoerelse__phaseTab--active" : ""}`}
                        onClick={() => props.onPhaseChange?.("B")}
                     >
                        {Localization.getText("B: Analyse")}
                     </button>
                  </div>
               ) : null}
               <h2>{phase === "B" ? Localization.getText("Analyse easement content") : Localization.getText("Find and control easements")}</h2>
               <p>
                  {phase === "B"
                     ? Localization.getText("Rewrite a field if AI is wrong or missing. That text is saved as the only annotation of that kind.")
                     : Localization.getText("Fetch or upload documents, check that AI found the pages, then approve the page selection.")}
               </p>
               {phase === "A" ? (
                  <button
                     type="button"
                     className="servitut-redegoerelse__folderCompare"
                     onClick={props.onCompareFolder}
                  >
                     {Localization.getText("Compare with folder")}
                  </button>
               ) : null}
               {phase === "B" && ((props.incompleteACount || 0) > 0 || (props.rejectedACount || 0) > 0) ? (
                  <div className="servitut-redegoerelse__incompleteBanner" role="status">
                     {(props.incompleteACount || 0) > 0 ? (
                        <div>
                           {Localization.getFormattedText("{count} easements are not fetched or not controlled.", { count: props.incompleteACount || 0 })}
                        </div>
                     ) : null}
                     {(props.rejectedACount || 0) > 0 ? (
                        <div>
                           {Localization.getFormattedText("{count} easements were rejected and are not being analysed.", { count: props.rejectedACount || 0 })}
                        </div>
                     ) : null}
                  </div>
               ) : null}
            </div>
            <div className={`servitut-redegoerelse__aiPanel servitut-redegoerelse__aiPanel--hero${phase === "B" ? " servitut-redegoerelse__aiPanel--withCase" : ""}`}>
               {phase === "B" ? (
                  <label className="servitut-redegoerelse__casePanel">
                     <span className="servitut-redegoerelse__caseLabel">
                        {Localization.getText("Problemstilling")}
                        <TooltipWrapper
                           title={Localization.getText("Write a problem statement. AI will assess whether the easement is relevant to it.")}
                           className="ViewButtonToolTip GlassLabelInfo"
                        >
                           <span
                              className="servitut-redegoerelse__caseHelp"
                              aria-label={Localization.getText("Write a problem statement. AI will assess whether the easement is relevant to it.")}
                           >
                              <MIBIcon icon="info" style={{ fontSize: 13 }} />
                           </span>
                        </TooltipWrapper>
                     </span>
                     <textarea
                        className="servitut-redegoerelse__caseInput"
                        value={props.caseDescription}
                        disabled={props.caseDescriptionLocked}
                        onChange={(e) => props.onCaseDescriptionChange(e.currentTarget.value)}
                        placeholder={Localization.getText("Optional: describe the problemstilling that AI should assess for relevance.")}
                        rows={3}
                     />
                  </label>
               ) : null}
               <button
                  key={phase === "A" ? "ai-start-A" : "ai-start-B"}
                  className={`servitut-redegoerelse__aiStart servitut-redegoerelse__aiStart--hero ${props.analyzing ? "servitut-redegoerelse__aiStart--stop" : ""}`}
                  disabled={!props.analyzing && (props.exporting || !props.canAnalyze || (phase === "A" ? props.canWriteA === false : props.canWriteB === false))}
                  onClick={props.analyzing ? props.onStopAnalysis : props.onAnalyze}
               >
                  {props.analyzing ? (
                     <>
                        <AiRobotIcon />
                        <Spinner size="sm" />
                        <span>
                           {phase === "A"
                              ? Localization.getText("Stop page finding")
                              : Localization.getText("Stop content analysis")}
                        </span>
                     </>
                  ) : (
                     <>
                        <AiRobotIcon />
                        <span>
                           {phase === "A"
                              ? Localization.getText("Start page finding with AI")
                              : (contentAnalyzedCount
                                 ? Localization.getText("Restart content analysis with AI")
                                 : Localization.getText("Start content analysis with AI"))}
                        </span>
                     </>
                  )}
               </button>
               {props.analyzing && props.analysisProgress && props.analysisProgress.total > 0 ? (
                  <div
                     className="servitut-redegoerelse__aiProgress"
                     role="progressbar"
                     aria-valuemin={0}
                     aria-valuemax={props.analysisProgress.total}
                     aria-valuenow={props.analysisProgress.completed}
                     aria-label={Localization.getText("AI analysis progress")}
                  >
                     <div className="servitut-redegoerelse__aiProgressTrack">
                        <div
                           className="servitut-redegoerelse__aiProgressFill"
                           style={{
                              width: `${Math.round((props.analysisProgress.completed / props.analysisProgress.total) * 100)}%`,
                           }}
                        />
                     </div>
                     <span className="servitut-redegoerelse__aiProgressCount">
                        {props.analysisProgress.completed}/{props.analysisProgress.total}
                     </span>
                  </div>
               ) : null}
            </div>
         </div>

         <div className="servitut-redegoerelse__toolbar">
            <div className="servitut-redegoerelse__toolbarRow">
               <input
                  className="servitut-redegoerelse__search"
                  value={query}
                  onChange={(e) => setQuery(e.currentTarget.value)}
                  placeholder={Localization.getText("Search akt, text or BFE…")}
               />
               <select
                  className="servitut-redegoerelse__filter"
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.currentTarget.value as typeof statusFilter)}
               >
                  <option value="all">{Localization.getText("All rows")}</option>
                  <option value="found">{Localization.getText("AI found pages")}</option>
                  <option value="uncertain">{Localization.getText("AI found pages, not confident")}</option>
                  <option value="missing">{Localization.getText("AI found no pages")}</option>
                  <option value="treated">{phase === "B" ? Localization.getText("Accepted") : Localization.getText("Approved")}</option>
                  <option value="untreated">{phase === "B" ? Localization.getText("Not accepted") : Localization.getText("Not treated")}</option>
               </select>
               {props.exporting || approvedCount === 0 ? (
                  <button
                     type="button"
                     className="servitut-redegoerelse__export"
                     disabled
                     aria-label={Localization.getFormattedText("Export ({count})", { count: approvedCount })}
                  >
                     {props.exporting
                        ? Localization.getText("Exporting...")
                        : Localization.getFormattedText("Export ({count})", { count: approvedCount })}
                  </button>
               ) : (
                  <details ref={exportMenuRef} className="servitut-redegoerelse__exportDetails">
                     <summary
                        className="servitut-redegoerelse__export"
                        aria-label={Localization.getFormattedText("Export ({count})", { count: approvedCount })}
                     >
                        {Localization.getFormattedText("Export ({count})", { count: approvedCount })}
                     </summary>
                     <div className="servitut-redegoerelse__exportOptions" role="menu">
                        <button
                           type="button"
                           role="menuitem"
                           onClick={() => {
                              if (exportMenuRef.current) {
                                 exportMenuRef.current.open = false;
                              }
                              props.onExport("excel");
                           }}
                        >
                           {Localization.getText("Export Excel")}
                        </button>
                        <button
                           type="button"
                           role="menuitem"
                           onClick={() => {
                              if (exportMenuRef.current) {
                                 exportMenuRef.current.open = false;
                              }
                              props.onExport("zip");
                           }}
                        >
                           {Localization.getText("Export easements and Excel")}
                        </button>
                     </div>
                  </details>
               )}
            </div>
            {props.exporting && props.exportProgress ? (
               <div className="servitut-redegoerelse__exportProgress" aria-live="polite">
                  <div className="servitut-redegoerelse__exportProgressTrack">
                     <div
                        className="servitut-redegoerelse__exportProgressFill"
                        style={{ width: `${exportProgressPct}%` }}
                     />
                  </div>
                  <div className="servitut-redegoerelse__exportProgressFooter">
                     <Spinner animation="border" size="sm" className="servitut-redegoerelse__exportProgressSpinner" />
                     <span className="servitut-redegoerelse__exportProgressText">
                        {exportProgressPct}% — {exportProgressLabel}
                     </span>
                  </div>
               </div>
            ) : null}
         </div>

         {showAnalysisColumns ? (
            <div className="servitut-redegoerelse__fieldTabs" role="tablist" aria-label={Localization.getText("Analysis field")}>
               {SERVITUT_B_TABS.map((tab) => (
                  <button
                     key={tab}
                     type="button"
                     role="tab"
                     aria-selected={bTab === tab}
                     className={`servitut-redegoerelse__fieldTab ${bTab === tab ? "servitut-redegoerelse__fieldTab--active" : ""}`}
                     onClick={() => selectBTab(tab)}
                  >
                     {servitutBTabLabel(tab)}
                  </button>
               ))}
            </div>
         ) : null}

         <div className="servitut-redegoerelse__tableWrap">
            <input
               ref={uploadInputRef}
               type="file"
               accept="application/pdf,.pdf"
               hidden
               onChange={(event) => {
                  const file = event.currentTarget.files?.[0];
                  const row = uploadRowRef.current;
                  event.currentTarget.value = "";
                  uploadRowRef.current = null;
                  if (file && row) {
                     props.onUploadCustomPdf(row, file);
                  }
               }}
            />
            <table className={`servitut-redegoerelse__table ${phase === "B" ? "servitut-redegoerelse__table--phaseB" : "servitut-redegoerelse__table--phaseA"}`}>
               <thead>
                  <tr>
                     <th className="servitut-redegoerelse__priorityHead">
                        <button
                           type="button"
                           className="servitut-redegoerelse__sortButton"
                           onClick={() => cycleMatrixSort("priority")}
                           title={Localization.getText("Sort by priority. Click again for ascending, descending and default sort.")}
                        >
                           <span>{Localization.getText("Priority")}</span>
                           <span>{matrixSortIndicator("priority")}</span>
                        </button>
                     </th>
                     <th className="servitut-redegoerelse__datoHead">
                        <button
                           type="button"
                           className="servitut-redegoerelse__sortButton"
                           onClick={() => cycleMatrixSort("dato")}
                           title={Localization.getText("Sort by date / serial no. Click again for ascending, descending and default sort.")}
                        >
                           <span>{Localization.getText("Date / serial no.")}</span>
                           <span>{matrixSortIndicator("dato")}</span>
                        </button>
                     </th>
                     <th className="servitut-redegoerelse__savedHead">{Localization.getText("Status")}</th>
                     {showAnalysisColumns ? (
                        <th className="servitut-redegoerelse__fieldHead servitut-redegoerelse__focusCol">
                           {servitutBTabLabel(bTab)}
                        </th>
                     ) : null}
                     {phase !== "B" ? (
                        <th className="servitut-redegoerelse__actionsHead">
                           <div className="servitut-redegoerelse__actionsHeadInner">
                              <span>{Localization.getText("Actions")}</span>
                              <button
                                 type="button"
                                 className="servitut-redegoerelse__rowAction servitut-redegoerelse__rowAction--approve"
                                 disabled={pageApprovableRows.length === 0 || props.exporting || props.canWriteA === false}
                                 title={
                                    pageApprovableRows.length === 0 && props.analyzing
                                       ? Localization.getText("Cannot save while AI is running")
                                       : Localization.getText("Approve all")
                                 }
                                 onClick={() => props.onApproveAllPages?.(pageApprovableRows)}
                              >
                                 {Localization.getText("Approve all")}
                              </button>
                              <button
                                 type="button"
                                 className="servitut-redegoerelse__rowAction servitut-redegoerelse__rowAction--revert"
                                 disabled={pageRevertableRows.length === 0 || props.exporting || props.canWriteA === false}
                                 title={
                                    pageRevertableRows.length === 0 && props.analyzing
                                       ? Localization.getText("Cannot save while AI is running")
                                       : Localization.getText("Undo all")
                                 }
                                 onClick={() => props.onRevertAllPages?.(pageRevertableRows)}
                              >
                                 {Localization.getText("Undo all")}
                              </button>
                           </div>
                        </th>
                     ) : null}
                     {showAnalysisColumns && !isStedfaestelseTab ? (
                        <th className="servitut-redegoerelse__actionsHead">
                           <div className="servitut-redegoerelse__actionsHeadInner">
                              <button
                                 type="button"
                                 className="servitut-redegoerelse__rowAction servitut-redegoerelse__rowAction--accept"
                                 disabled={fieldAcceptableRows.length === 0 || props.exporting || props.canWriteB === false}
                                 title={
                                    fieldAcceptableRows.length === 0 && props.analyzing
                                       ? Localization.getText("Cannot save while AI is running")
                                       : Localization.getText("Accept all of this field")
                                 }
                                 onClick={() => {
                                    setEditingFieldKey(null);
                                    props.onAcceptAllField(
                                       fieldAcceptableRows.map((row) => ({ row, text: displayedFieldText(row) })),
                                    );
                                 }}
                              >
                                 {Localization.getText("Accept all")}
                              </button>
                              <button
                                 type="button"
                                 className="servitut-redegoerelse__rowAction servitut-redegoerelse__rowAction--revert"
                                 disabled={fieldUndoableRows.length === 0 || props.exporting || props.canWriteB === false}
                                 title={
                                    fieldUndoableRows.length === 0 && props.analyzing
                                       ? Localization.getText("Cannot save while AI is running")
                                       : Localization.getText("Undo all of this field")
                                 }
                                 onClick={() => {
                                    setEditingFieldKey(null);
                                    props.onUndoAllField?.(fieldUndoableRows);
                                 }}
                              >
                                 {Localization.getText("Undo all")}
                              </button>
                           </div>
                        </th>
                     ) : null}
                     {props.payload.matrix.columns.map((bfe) => {
                        const label = getServitutMatrixColumnLabel(props.payload, bfe);
                        const shortHeaderText = getServitutMatrixColumnHeaderShort(props.payload, bfe);
                        const popoverDetail = getServitutMatrixColumnPopoverDetail(props.payload, bfe);
                        const columnAriaLabel = Localization.getFormattedText("{label} (BFE {bfe})", { label, bfe });
                        return (
                           <ServitutBfeColumnHeadCell
                              key={bfe}
                              bfe={bfe}
                              shortLabel={shortHeaderText}
                              popover={popoverDetail}
                              ariaLabel={columnAriaLabel}
                           />
                        );
                     })}
                  </tr>
               </thead>
               <tbody>
                  {filteredRows.map((row) => {
                     const isReevaluating = props.reevaluatingKeys.has(row.key);
                     const isResolvingPaatale = props.resolvingPaataleKeys.has(row.key);
                     const isSavingAnnotation = props.savingAnnotationKeys.has(row.key);
                     const wasJustSaved = props.justSavedKeys.has(row.key);
                     const hasUnsavedChange = props.unsavedChangeKeys.has(row.key);
                     const actionStatus = getRowActionStatus({
                        hasAnalysis: Boolean(row.analysisItem),
                        isSaving: isSavingAnnotation,
                        wasJustSaved,
                        hasUnsavedChange,
                        hasSavedAnalysis: Boolean(row.annotationRecord?.document?.llmAnalysis),
                     });
                     const awaitingAi = isServitutRowAwaitingAi(row, {
                        aiJobKeys: props.aiJobKeys,
                        reevaluatingKeys: props.reevaluatingKeys,
                     });
                     const rowIsBeingAnalyzed = awaitingAi;
                     const rowIsOpening = props.openingKey === row.key;
                     const rowOpensViewer = row.documentSource === "rigsarkivetRequired"
                        ? false
                        : row.documentSource === "customUpload"
                        ? Boolean(row.customPdf?.s3Key)
                        : (row.handling ?? "legacyLlmPdf") === "electronicZipPackage"
                        ? Boolean(row.electronicServitut)
                        : row.pdfStatus === "ready" || row.documentSource === "annotation";
                     const rowIsExportFocus = Boolean(
                        props.exporting
                        && props.exportProgress?.stage === "document"
                        && props.exportProgress.activeRowKey === row.key,
                     );
                     const rowIsBusy = rowIsBeingAnalyzed
                        || isReevaluating
                        || rowIsOpening
                        || isResolvingPaatale
                        || isSavingAnnotation
                        || rowIsExportFocus;
                     const fieldAccepted = isRowFieldAccepted(row, props.analysisField);
                     const fieldText = displayedFieldText(row);
                     const fieldStatus = fieldAccepted
                        ? "person_approved" as const
                        : getContentFieldAiStatus(row, props.analysisField, { hasCaseDescription });
                     const pageTone = getPageControlTone(row);
                     const datoLoebenrCopyText = getRowCopyText(row);
                     const headingText = getRowHeading(row);
                     const annotationHint = (
                        <ServitutSourceHint
                           mark="A"
                           tone="annotation"
                           text={row.sourceMessage || Localization.getText("Using saved annotation PDF")}
                        />
                     );
                     const fieldAnnotationHint = (
                        <ServitutSourceHint
                           mark="A"
                           tone="annotation"
                           text={Localization.getText("Field annotation saved")}
                        />
                     );
                     const sourceHint = phase === "B"
                        ? (isStedfaestelseTab
                           ? (row.stedfaestelseAnnotation ? fieldAnnotationHint : null)
                           : (fieldAccepted ? fieldAnnotationHint : null))
                        : isRowApproved(row)
                           ? annotationHint
                           : row.pdfSource === "ovrigAkt" ? (
                              <ServitutSourceHint
                                 mark="!"
                                 tone="ovrig"
                                 text={getAktSourceText(row.pdfSource)}
                              />
                           ) : row.pdfSource === "korrektAkt" ? (
                              <ServitutSourceHint
                                 mark="K"
                                 tone="korrekt"
                                 text={getAktSourceText(row.pdfSource)}
                              />
                           ) : row.pdfSource === "textAkt" ? (
                              <ServitutSourceHint
                                 mark="T"
                                 tone="text"
                                 text={getAktSourceText(row.pdfSource)}
                              />
                           ) : row.documentSource === "annotation" ? annotationHint
                           : row.documentSource === "rigsarkivetRequired" ? (
                              <ServitutSourceHint
                                 mark="R"
                                 tone="rigsarkiv"
                                 text={row.sourceMessage || Localization.getText("Rigsarkivet")}
                              />
                           ) : row.sourceMessage && (row.pdfStatus === "missing" || row.pdfStatus === "failed") ? (
                              <ServitutSourceHint
                                 mark="?"
                                 tone="missing"
                                 text={row.sourceMessage}
                              />
                           ) : null;
                     const isElectronicRow = (row.handling ?? "legacyLlmPdf") === "electronicZipPackage";
                     const aktSourceLocations = isElectronicRow
                        ? []
                        : listServitutAktSearchLocations(
                           row.servitut,
                           stamOplysningerHintForRow(props.payload, row),
                        );
                     const pageSource = getRowPageSourceStatus(row);
                     const documentExtent = getRowDocumentExtent(row);
                     const pageSourceForChip = isRowApproved(row)
                        && (pageSource.kind === "ai_none"
                           || (pageSource.kind === "ai_pages" && !pageSource.confident))
                        ? { kind: "hidden" as const }
                        : pageSource;
                     const viewControl = rowIsOpening ? (
                        <span className="servitut-redegoerelse__viewButton servitut-redegoerelse__viewButton--busy" title={Localization.getText("Loading...")}>
                           <Spinner
                              animation="border"
                              role="status"
                              size="sm"
                              className="servitut-redegoerelse__rowBusySpinner"
                           />
                        </span>
                     ) : (
                        <button
                           type="button"
                           className="servitut-redegoerelse__viewButton"
                           disabled={!rowOpensViewer}
                           onClick={() => {
                              if (rowOpensViewer) {
                                 props.onOpenRow(row);
                              }
                           }}
                           title={rowOpensViewer
                              ? Localization.getText("View document")
                              : Localization.getText("No PDF available for this document")}
                           aria-label={rowOpensViewer
                              ? Localization.getText("View document")
                              : Localization.getText("No PDF available for this document")}
                        >
                           <MIBIcon icon="pdf" style={{ fontSize: 14 }} />
                           <span>{Localization.getText("View document")}</span>
                        </button>
                     );
                     return (
                        <tr
                           key={row.key}
                           className={`servitut-redegoerelse__row ${
                              phase === "A"
                                 ? `servitut-redegoerelse__row--page-${pageTone}`
                                 : `servitut-redegoerelse__row--field-${
                                    (isStedfaestelseTab ? Boolean(row.stedfaestelseAnnotation) : fieldAccepted)
                                       ? "accepted"
                                       : "pending"
                                 }`
                           }${rowIsExportFocus ? " servitut-redegoerelse__row--exportActive" : ""}${rowIsBusy ? " servitut-redegoerelse__row--busy" : ""}`}
                        >
                           <td className="servitut-redegoerelse__priorityCell"><span className="servitut-redegoerelse__priority">{row.priority || "-"}</span></td>
                           <td className="servitut-redegoerelse__aktCell">
                              <div className="servitut-redegoerelse__aktMain">
                                 <div className="servitut-redegoerelse__aktIdLine">
                                    <div className="servitut-redegoerelse__aktId">{row.datoLoebenr || row.displayAktId}</div>
                                    {isElectronicRow ? null : (
                                       <ServitutAktSourcePill
                                          aktId={row.aktId}
                                          usedSource={row.pdfSource}
                                          locations={aktSourceLocations}
                                       />
                                    )}
                                    {sourceHint}
                                    <div className="servitut-redegoerelse__aktActions">
                                       {viewControl}
                                       <button
                                          type="button"
                                          className="servitut-redegoerelse__viewButton"
                                          disabled={!datoLoebenrCopyText}
                                          title={Localization.getText("Copy to clipboard")}
                                          aria-label={Localization.getText("Copy text")}
                                          onClick={() => void copyTextToClipboard(datoLoebenrCopyText)}
                                       >
                                          <MIBIcon icon="copy" style={{ fontSize: 14 }} />
                                          <span>{Localization.getText("Copy text")}</span>
                                       </button>
                                    </div>
                                 </div>
                                 {headingText ? (
                                    <div className="servitut-redegoerelse__aktHeadingLine">
                                       <span className="servitut-redegoerelse__aktHeading">{headingText}</span>
                                    </div>
                                 ) : null}
                              </div>
                           </td>
                           <td className="servitut-redegoerelse__savedCell">
                              {phase === "B" ? (
                                 isStedfaestelseTab ? (
                                    <div className="servitut-redegoerelse__statusStack">
                                       {row.stedfaestelseAnnotation ? (
                                          <span className="servitut-redegoerelse__statusPill servitut-redegoerelse__statusPill--saved">
                                             {Localization.getText("Saved")}
                                          </span>
                                       ) : (
                                          <span className="servitut-redegoerelse__statusPill servitut-redegoerelse__statusPill--notSaved">
                                             {Localization.getText("Not saved yet")}
                                          </span>
                                       )}
                                    </div>
                                 ) : isSavingAnnotation || awaitingAi ? (
                                    <span className="servitut-redegoerelse__savedPending" title={isSavingAnnotation ? Localization.getText("Saving annotation…") : Localization.getText("Analyzing easement content...")}>
                                       {awaitingAi && !isSavingAnnotation ? <AiRobotIcon /> : null}
                                       <Spinner animation="border" role="status" size="sm" />
                                    </span>
                                 ) : (
                                    <div className="servitut-redegoerelse__statusStack">
                                       <ServitutAiFieldChip
                                          status={fieldStatus}
                                          text={
                                             fieldStatus === "not_run"
                                                && props.analysisField === "statement"
                                                && !hasCaseDescription
                                                ? Localization.getText("No problem statement")
                                                : undefined
                                          }
                                       />
                                       {fieldAccepted ? (
                                          <span className="servitut-redegoerelse__statusPill servitut-redegoerelse__statusPill--saved">
                                             {Localization.getText("Accepted")}
                                          </span>
                                       ) : (
                                          <span className="servitut-redegoerelse__statusPill servitut-redegoerelse__statusPill--notSaved">
                                             {Localization.getText("Not accepted yet")}
                                          </span>
                                       )}
                                    </div>
                                 )
                              ) : (
                                 <div className="servitut-redegoerelse__statusStack">
                                    {actionStatus === "saving" ? (
                                       <span className="servitut-redegoerelse__savedPending" title={Localization.getText("Saving annotation…")}>
                                          <Spinner animation="border" role="status" size="sm" />
                                       </span>
                                    ) : null}
                                    {isImportedAnnotationPendingPageSearch(row) ? (
                                       <ServitutImportedChip />
                                    ) : (
                                       <>
                                          {isElectronicServitutRow(row) ? <ServitutDigitalDocumentChip /> : null}
                                          <ServitutProvenanceChip
                                             provenance={getRowProvenance(row)}
                                             hideAiGenerated
                                             hideFetchedFromAkt={documentExtent.kind === "entire_akt"}
                                          />
                                       </>
                                    )}
                                    <ServitutDocumentExtentChip extent={documentExtent} />
                                    <ServitutPageFindChip source={pageSourceForChip} />
                                    {!isRowApproved(row) && !isRowRejected(row) ? (
                                       <span className="servitut-redegoerelse__statusPill servitut-redegoerelse__statusPill--notSaved">
                                          {Localization.getText("Not accepted yet")}
                                       </span>
                                    ) : null}
                                 </div>
                              )}
                           </td>
                           {phase !== "B" ? (
                           <td className="servitut-redegoerelse__actionsCell">
                              <div className="servitut-redegoerelse__pageActions">
                                 <button
                                    type="button"
                                    className="servitut-redegoerelse__rowAction"
                                    disabled={
                                       props.canWriteA === false
                                       || isSavingAnnotation
                                       || awaitingAi
                                       || !canLocateSingleRow(row)
                                    }
                                    title={!canLocateSingleRow(row) ? Localization.getText("No PDF available for this document") : Localization.getText("Run AI on this row")}
                                    onClick={() => props.onRunRowAi?.(row)}
                                 >
                                    {awaitingAi ? (
                                       <>
                                          <AiRobotIcon />
                                          <Spinner animation="border" role="status" size="sm" className="servitut-redegoerelse__rowBusySpinner" />
                                       </>
                                    ) : (
                                       <>
                                          <AiRobotIcon />
                                          <span>{Localization.getText("Run AI")}</span>
                                       </>
                                    )}
                                 </button>
                                 {isRowApproved(row) ? (
                                    <button
                                       type="button"
                                       className="servitut-redegoerelse__rowAction servitut-redegoerelse__rowAction--revert"
                                       disabled={props.canWriteA === false || isSavingAnnotation || awaitingAi}
                                       title={awaitingAi ? Localization.getText("Cannot save while AI is running") : undefined}
                                       onClick={() => props.onRevertPages?.(row)}
                                    >
                                       {Localization.getText("Undo approve")}
                                    </button>
                                 ) : (
                                    <button
                                       type="button"
                                       className="servitut-redegoerelse__rowAction servitut-redegoerelse__rowAction--approve"
                                       disabled={props.canWriteA === false || isSavingAnnotation || awaitingAi || !isRowFetched(row)}
                                       title={
                                          awaitingAi
                                             ? Localization.getText("Cannot save while AI is running")
                                             : !isRowFetched(row)
                                                ? Localization.getText("No PDF available for this document")
                                                : undefined
                                       }
                                       onClick={() => props.onApprovePages?.(row)}
                                    >
                                       {Localization.getText("Approve")}
                                    </button>
                                 )}
                                 <button
                                    type="button"
                                    className="servitut-redegoerelse__rowAction"
                                    disabled={props.exporting || awaitingAi || isReevaluating || isSavingAnnotation || props.canWriteA === false}
                                    title={Localization.getText("Upload PDF")}
                                    onClick={() => {
                                       uploadRowRef.current = row;
                                       uploadInputRef.current?.click();
                                    }}
                                 >
                                    {isReevaluating && !row.analysisItem ? (
                                       <Spinner animation="border" role="status" size="sm" className="servitut-redegoerelse__rowBusySpinner" />
                                    ) : (
                                       Localization.getText("Upload PDF")
                                    )}
                                 </button>
                              </div>
                           </td>
                           ) : null}
                           {showAnalysisColumns ? (
                              <td className="servitut-redegoerelse__fieldCell servitut-redegoerelse__focusCol">
                                 {isStedfaestelseTab ? (
                                    <button
                                       type="button"
                                       className="servitut-redegoerelse__rowAction"
                                       onClick={() => props.onOpenStedfaestelse?.(row)}
                                    >
                                       {Localization.getText("Locate on map")}
                                    </button>
                                 ) : (
                                 <div className="servitut-redegoerelse__textWithCopy">
                                    <div className="servitut-redegoerelse__fieldEditor">
                                       {props.analysisField === "entitledParty"
                                          && (canResolvePaataleberettiget(row) || row.analysisItem?.paataleberettiget_resolution) ? (
                                          <div className="servitut-redegoerelse__paataleActions">
                                             {canResolvePaataleberettiget(row) ? (
                                                <TooltipWrapper
                                                   title={
                                                      props.resolvingPaataleKeys.has(row.key)
                                                         ? Localization.getText("Resolving current entitled party…")
                                                         : Localization.getText("Find current entitled party")
                                                   }
                                                >
                                                   <button
                                                      type="button"
                                                      className="servitut-redegoerelse__paataleResolve"
                                                      disabled={props.resolvingPaataleKeys.has(row.key) || awaitingAi || props.exporting}
                                                      onClick={() => props.onResolvePaataleberettiget(row)}
                                                      aria-label={Localization.getText("Find current entitled party")}
                                                   >
                                                      {props.resolvingPaataleKeys.has(row.key) ? (
                                                         <Spinner animation="border" role="status" size="sm" className="servitut-redegoerelse__rowBusySpinner" />
                                                      ) : (
                                                         <MIBIcon icon="switchLicense" style={{ fontSize: 14 }} />
                                                      )}
                                                   </button>
                                                </TooltipWrapper>
                                             ) : null}
                                             {row.analysisItem?.paataleberettiget_resolution ? (
                                                <TooltipWrapper title={Localization.getText("Show suggestion details")}>
                                                   <button
                                                      type="button"
                                                      className="servitut-redegoerelse__paataleResolve"
                                                      onClick={() => setPaataleNoticeKey(row.key)}
                                                      aria-label={Localization.getText("Show suggestion details")}
                                                   >
                                                      <MIBIcon icon="mapLink" style={{ fontSize: 14 }} />
                                                   </button>
                                                </TooltipWrapper>
                                             ) : null}
                                          </div>
                                       ) : null}
                                       {editingFieldKey === fieldDraftKey(row.key) ? (
                                          <textarea
                                             className="servitut-redegoerelse__fieldInput"
                                             value={fieldText}
                                             autoFocus
                                             disabled={props.canWriteB === false || isSavingAnnotation || props.exporting || awaitingAi}
                                             placeholder={Localization.getText("Write or correct this field")}
                                             rows={4}
                                             onChange={(event) => {
                                                const next = event.currentTarget.value;
                                                setFieldDrafts((prev) => ({ ...prev, [fieldDraftKey(row.key)]: next }));
                                             }}
                                             onBlur={(event) => {
                                                const related = event.relatedTarget as HTMLElement | null;
                                                if (related?.closest(".servitut-redegoerelse__fieldCell, .servitut-redegoerelse__pageActions")) {
                                                   return;
                                                }
                                                stopEditingField(row.key);
                                             }}
                                          />
                                       ) : (
                                          <div
                                             className={`servitut-redegoerelse__fieldPlain${fieldText.trim() ? "" : " servitut-redegoerelse__fieldPlain--empty"}`}
                                          >
                                             {fieldText.trim()}
                                          </div>
                                       )}
                                    </div>
                                    <div className="servitut-redegoerelse__fieldSideActions">
                                       <button
                                          type="button"
                                          className="servitut-redegoerelse__copyButton"
                                          disabled={!fieldText.trim()}
                                          title={Localization.getText("Copy to clipboard")}
                                          aria-label={Localization.getText("Copy to clipboard")}
                                          onClick={() => void copyTextToClipboard(fieldText)}
                                       >
                                          <MIBIcon icon="copy" style={{ fontSize: 14 }} />
                                       </button>
                                       {props.canWriteB !== false ? (
                                          editingFieldKey === fieldDraftKey(row.key) ? (
                                             <button
                                                type="button"
                                                className="servitut-redegoerelse__copyButton"
                                                disabled={isSavingAnnotation || props.exporting || awaitingAi}
                                                title={Localization.getText("Annuller")}
                                                aria-label={Localization.getText("Annuller")}
                                                onClick={() => {
                                                   clearFieldDraft(row.key);
                                                   stopEditingField(row.key);
                                                }}
                                             >
                                                <MIBIcon icon="close" style={{ fontSize: 14 }} />
                                             </button>
                                          ) : (
                                             <button
                                                type="button"
                                                className="servitut-redegoerelse__copyButton"
                                                disabled={isSavingAnnotation || props.exporting || awaitingAi}
                                                title={Localization.getText("Edit")}
                                                aria-label={Localization.getText("Edit")}
                                                onClick={() => setEditingFieldKey(fieldDraftKey(row.key))}
                                             >
                                                <MIBIcon icon="edit" style={{ fontSize: 14 }} />
                                             </button>
                                          )
                                       ) : null}
                                    </div>
                                 </div>
                                 )}
                              </td>
                           ) : null}
                           {showAnalysisColumns && !isStedfaestelseTab ? (
                              <td className="servitut-redegoerelse__actionsCell">
                                 <div className="servitut-redegoerelse__pageActions">
                                    <button
                                       type="button"
                                       className="servitut-redegoerelse__rowAction"
                                       disabled={
                                          props.canWriteB === false
                                          || awaitingAi
                                          || !isRowFetched(row)
                                       }
                                       title={!isRowFetched(row) ? Localization.getText("No PDF available for this document") : Localization.getText("Run AI on this row")}
                                       onClick={() => props.onRunRowAi?.(row)}
                                    >
                                       {awaitingAi ? (
                                          <>
                                             <AiRobotIcon />
                                             <Spinner animation="border" role="status" size="sm" className="servitut-redegoerelse__rowBusySpinner" />
                                          </>
                                       ) : (
                                          <>
                                             <AiRobotIcon />
                                             <span>{Localization.getText("Run AI")}</span>
                                          </>
                                       )}
                                    </button>
                                    {fieldAccepted && editingFieldKey !== fieldDraftKey(row.key) ? (
                                       <button
                                          type="button"
                                          className="servitut-redegoerelse__rowAction servitut-redegoerelse__rowAction--revert"
                                          disabled={props.canWriteB === false || isSavingAnnotation || props.exporting}
                                          onClick={() => {
                                             clearFieldDraft(row.key);
                                             stopEditingField(row.key);
                                             props.onUndoField(row);
                                          }}
                                       >
                                          {Localization.getText("Undo accept")}
                                       </button>
                                    ) : (
                                       <button
                                          type="button"
                                          className="servitut-redegoerelse__rowAction servitut-redegoerelse__rowAction--accept"
                                          disabled={
                                             props.canWriteB === false
                                             || isSavingAnnotation
                                             || props.exporting
                                             || awaitingAi
                                             || !fieldText.trim()
                                          }
                                          title={awaitingAi ? Localization.getText("Cannot save while AI is running") : undefined}
                                          onClick={() => {
                                             const next = fieldText.trim();
                                             if (!next) {
                                                return;
                                             }
                                             void Promise.resolve(props.onAcceptField(row, next)).then(() => {
                                                clearFieldDraft(row.key);
                                                stopEditingField(row.key);
                                             });
                                          }}
                                       >
                                          {Localization.getText("Accept field")}
                                       </button>
                                    )}
                                 </div>
                              </td>
                           ) : null}
                           {props.payload.matrix.columns.map((bfe) => (
                              <td key={`${row.key}-${bfe}`} className="servitut-redegoerelse__bfeCell">
                                 {row.includedBfes.includes(bfe) ? <span className="servitut-redegoerelse__included">✓</span> : ""}
                              </td>
                           ))}
                        </tr>
                     );
                  })}
               </tbody>
            </table>
            {filteredRows.length === 0 ? (
               <div className="servitut-redegoerelse__empty">
                  {phase === "B" && !props.payload.matrix.rows.some(isRowApproved)
                     ? Localization.getText("No approved easements. Approve pages in Find and control first.")
                     : Localization.getText("No rows match the filter.")}
               </div>
            ) : null}
         </div>
         {paataleNoticeResolution ? (
            <AdvancedDragModal
               variant="Small"
               title={Localization.getText("AI suggested current")}
               onClose={() => setPaataleNoticeKey(null)}
               PosDefault={{ top: "120px", left: "50%", width: "560px", height: "620px", transform: "translate(-50%, 0)" }}
            >
               <ADModalBody>
                  <PaataleberettigetResolutionView
                     resolution={paataleNoticeResolution}
                     defaultOpenChain
                  />
               </ADModalBody>
            </AdvancedDragModal>
         ) : null}
      </div>
   );
}

function getSortedJordstykkerFromBfe(bfeData?: ServitutRapportBfeData): any[] {
   const sfe = bfeData?.GQL_BFE?.MAT_SamletFastEjendom?.nodes?.find((node: any) => node.status === "Gældende")
      || bfeData?.GQL_BFE?.MAT_SamletFastEjendom?.nodes?.[0];
   return (sfe?.jordstykkeSamlesISamletFastEjendom?.nodes || [])
      .filter((jordstykke: any) => jordstykke.status === "Gældende")
      .filter((jordstykke: any) => !sfe?.id_lokalId || jordstykke.samletFastEjendomLokalId === sfe.id_lokalId)
      .sort((a: any, b: any) => {
         const ejerlavDiff = parseInt(a.jordstykkeLiggerIEjerlav?.ejerlavskode || "0", 10)
            - parseInt(b.jordstykkeLiggerIEjerlav?.ejerlavskode || "0", 10);
         if (ejerlavDiff !== 0) {
            return ejerlavDiff;
         }
         return PropertyInformation.matrikelSortFunc(a.matrikelnummer || "", b.matrikelnummer || "");
      });
}

function buildServitutMatrixColumnDetail(bfeData?: ServitutRapportBfeData): ServitutMatrixColumnDetail | null {
   const jordstykker = getSortedJordstykkerFromBfe(bfeData);
   const matrikler = jordstykker.map((j: any) => j.matrikelnummer).filter(Boolean);
   if (!matrikler.length) {
      return null;
   }
   const ejerlavNames = new Set<string>();
   jordstykker.forEach((j: any) => {
      const navn = j.jordstykkeLiggerIEjerlav?.ejerlavsnavn?.trim();
      if (navn) {
         ejerlavNames.add(navn);
      }
   });
   const ejerlav = ejerlavNames.size ? Array.from(ejerlavNames).join(", ") : undefined;
   return { matrikler, ejerlav };
}

function formatServitutMatrixPropertyColumn(bfeData?: ServitutRapportBfeData): string {
   const detail = buildServitutMatrixColumnDetail(bfeData);
   if (!detail?.matrikler.length) {
      return "";
   }
   return [
      `${detail.matrikler[0]}${detail.matrikler.length > 1 ? " m.fl." : ""}`,
      detail.ejerlav,
   ].filter(Boolean).join(", ");
}

function buildServitutMatrixColumnDetails(
   data: ServitutRapportBfeData[],
   colorList: SFEColorEntry[]
): Record<string, ServitutMatrixColumnDetail> {
   const dataByBfe = new Map(data.map((entry) => [Number(entry.BFEnummer), entry]));
   return Object.fromEntries(
      colorList.map((entry) => {
         const detail = buildServitutMatrixColumnDetail(dataByBfe.get(entry.sfe));
         return [
            String(entry.sfe),
            detail ?? { matrikler: [`${entry.sfe}`] },
         ] as const;
      }),
   );
}

function buildServitutMatrixColumnLabels(
   data: ServitutRapportBfeData[],
   colorList: SFEColorEntry[]
): Record<string, string> {
   const dataByBfe = new Map(data.map((entry) => [Number(entry.BFEnummer), entry]));
   return Object.fromEntries(
      colorList.map((entry) => [
         entry.sfe,
         formatServitutMatrixPropertyColumn(dataByBfe.get(entry.sfe)) || `${entry.sfe}`,
      ])
   );
}

function getServitutMatrixColumnLabel(payload: ServitutRedegoerelsePayload, bfe: number): string {
   return payload.matrix.columnLabels?.[bfe] ?? payload.matrix.columnLabels?.[String(bfe)] ?? `${bfe}`;
}

function getServitutMatrixColumnLabelParts(label: string): { matrikel: string; ejerlav?: string } {
   const [matrikel, ...ejerlavParts] = label.split(", ");
   return {
      matrikel: matrikel || label,
      ejerlav: ejerlavParts.join(", ") || undefined,
   };
}

function getServitutMatrixColumnHeaderShort(payload: ServitutRedegoerelsePayload, bfe: number): string {
   const detail = payload.matrix.columnDetails?.[String(bfe)];
   if (detail?.matrikler?.length) {
      return `${detail.matrikler[0]}${detail.matrikler.length > 1 ? " m.fl." : ""}`;
   }
   const label = getServitutMatrixColumnLabel(payload, bfe);
   const parts = getServitutMatrixColumnLabelParts(label);
   return parts.matrikel || `${bfe}`;
}

function getServitutMatrixColumnPopoverDetail(
   payload: ServitutRedegoerelsePayload,
   bfe: number
): ServitutMatrixColumnDetail {
   const fromPayload = payload.matrix.columnDetails?.[String(bfe)];
   if (fromPayload?.matrikler?.length) {
      return fromPayload;
   }
   const label = getServitutMatrixColumnLabel(payload, bfe);
   const parts = getServitutMatrixColumnLabelParts(label);
   return {
      matrikler: parts.matrikel ? [parts.matrikel] : [`${bfe}`],
      ejerlav: parts.ejerlav,
   };
}

function applyCaseDescriptionToPayload(
   payload: ServitutRedegoerelsePayload,
   caseDescription: string
): ServitutRedegoerelsePayload {
   const normalizedCaseDescription = caseDescription.trim();
   return {
      ...payload,
      caseDescription: normalizedCaseDescription || undefined,
      metadata: payload.metadata.map((metadata) => {
         if (!normalizedCaseDescription) {
            const rest = { ...metadata };
            delete rest.caseDescription;
            return rest;
         }
         return {
            ...metadata,
            caseDescription: normalizedCaseDescription,
         };
      }),
   };
}

function isServitutRowAwaitingAi(
   row: ServitutRedegoerelsePayload["matrix"]["rows"][number],
   params: {
      aiJobKeys: Set<string>;
      reevaluatingKeys: Set<string>;
   },
): boolean {
   return params.aiJobKeys.has(row.key) || params.reevaluatingKeys.has(row.key);
}

function isCutAnnotationRow(row: ServitutRedegoerelsePayload["matrix"]["rows"][number]): boolean {
   return row.documentSource === "annotation"
      && (row.annotationDocument?.extractedPages?.length ?? 0) > 0;
}

function needsUploadedPdfLocate(row: ServitutRedegoerelsePayload["matrix"]["rows"][number]): boolean {
   if (row.pdfStatus !== "ready") {
      return false;
   }
   if (row.documentSource === "customUpload") {
      return false;
   }
   if (row.documentSource === "annotation" && !isCutAnnotationRow(row)) {
      return Boolean(row.annotationDocument?.s3Key);
   }
   return false;
}

function usesLocalPdfForLocate(row: ServitutRedegoerelsePayload["matrix"]["rows"][number]): boolean {
   if (isCustomUploadRow(row) && row.pdfStatus === "ready" && Boolean(row.customPdf?.s3Key)) {
      return true;
   }
   return needsUploadedPdfLocate(row);
}

function hasAktBackedLocatableRows(payload: ServitutRedegoerelsePayload | null): boolean {
   if (!payload) {
      return false;
   }
   return payload.metadata.some((metadata) => Boolean(metadata.aktId))
      || payload.matrix.rows.some(needsUploadedPdfLocate);
}

function hasContentAnalysisFields(item?: LLMAnalysisResultItem): boolean {
   return Boolean(
      item?.summary
      || item?.servitut_type
      || item?.paataleberettiget
      || item?.case_relevance_reasoning
      || item?.case_relevant === true,
   );
}

function hasLocatableServitutRows(payload: ServitutRedegoerelsePayload | null): boolean {
   if (!payload) {
      return false;
   }
   return hasAktBackedLocatableRows(payload)
      || payload.matrix.rows.some(usesLocalPdfForLocate);
}

function canLocateSingleRow(row: ServitutRedegoerelsePayload["matrix"]["rows"][number]): boolean {
   if (row.documentSource === "rigsarkivetRequired") {
      return false;
   }
   if (isElectronicServitutRow(row) && !needsUploadedPdfLocate(row)) {
      return false;
   }
   return isRowFetched(row) || Boolean(row.aktId) || needsUploadedPdfLocate(row);
}

function hasContentAnalyzableRows(payload: ServitutRedegoerelsePayload | null): boolean {
   if (!payload) {
      return false;
   }
   return payload.matrix.rows.some((row) => isRowApproved(row) && isRowFetched(row));
}

function getLocateRequestKeys(payload: ServitutRedegoerelsePayload): Set<string> {
   const keys = new Set<string>();
   payload.metadata.forEach((metadata) => {
      if (!metadata.aktId || !metadata.id) {
         return;
      }
      const row = payload.matrix.rows.find((entry) => entry.key === metadata.id);
      if (row && usesLocalPdfForLocate(row)) {
         return;
      }
      keys.add(metadata.id);
   });
   payload.matrix.rows.forEach((row) => {
      if (usesLocalPdfForLocate(row)) {
         keys.add(row.key);
      }
   });
   return keys;
}

function getContentAnalysisRequestKeys(payload: ServitutRedegoerelsePayload): Set<string> {
   return new Set(
      payload.matrix.rows
         .filter((row) => isRowApproved(row) && isRowFetched(row))
         .map((row) => row.key),
   );
}

function withSkippedPaataleResolution(metadata: LLMMetadata): LLMMetadata {
   return {
      ...metadata,
      skipPaataleberettigetResolution: true,
   };
}

function toSummaryOnlyAnalysisItem(item: LLMAnalysisResultItem): LLMAnalysisResultItem {
   return {
      id: item.id,
      aktId: item.aktId,
      processing_method: item.processing_method,
      reasoning: item.reasoning,
      pages_kept: item.pages_kept,
      original_pages: item.original_pages,
      extracted_pages: item.extracted_pages,
      summary: item.summary,
      confidence: item.confidence,
      overall_confidence: item.overall_confidence,
      overall_confidence_reasoning: item.overall_confidence_reasoning,
      needs_review: item.needs_review,
      review_reasons: item.review_reasons,
   };
}

/** Wrap a single row's AI item in the shape ANNPDFViewer expects. */
function toViewerAnalysisResult(
   item?: LLMAnalysisResultItem,
   mode: "summary" | "full" = "full",
): LLMAnalysisResult | undefined {
   if (!item) return undefined;
   return {
      items: [mode === "summary" ? toSummaryOnlyAnalysisItem(item) : item],
      total_processed: 1,
      total_requested: 1,
      timestamp: new Date().toISOString(),
   };
}

function canResolvePaataleberettiget(row: ServitutRedegoerelsePayload["matrix"]["rows"][number]): boolean {
   const historicalName = row.analysisItem?.paataleberettiget?.trim();
   if (!historicalName) return false;
   const normalized = historicalName
      .normalize("NFKC")
      .toLocaleLowerCase("da-DK")
      .replace(/[.,;:!?]+$/g, "")
      .trim();
   const missingValues = new Set([
      "",
      "-",
      "ingen",
      "ingen påtaleberettiget",
      "ikke angivet",
      "ikke fundet",
      "ikke identificeret",
      "ikke oplyst",
      "n/a",
      "null",
      "ukendt",
      "undefined",
   ]);
   if (missingValues.has(normalized) || normalized.includes("påtaleberettiget ikke fundet")) {
      return false;
   }
   return true;
}

function makeFailedPaataleResolution(
   historicalName: string,
   status: "not_found" | "error",
): PaataleberettigetResolution {
   return {
      historical_name: historicalName,
      suggested_current_name: null,
      status,
      successor_chain: [],
      reasoning: Localization.getText("Current entitled party AI could not find"),
      alternatives: [],
      sources: [],
      searched_at: new Date().toISOString(),
      model: "",
      disclaimer: "",
   };
}

function newestStedfaestelseRecord(records: ANNAnnotationRecord[]): ANNAnnotationRecord | null {
   return records.slice().sort((left, right) => (
      String(right.lastEditedAt || right.createdAt || "").localeCompare(
         String(left.lastEditedAt || left.createdAt || ""),
      )
   ))[0] || null;
}

function applyStedfaestelseToRow(
   payload: ServitutRedegoerelsePayload | null,
   rowKey: string,
   annotation: ANNAnnotationRecord | null,
): ServitutRedegoerelsePayload | null {
   if (!payload || !annotation) {
      return payload;
   }
   let changed = false;
   const rows = payload.matrix.rows.map((row) => {
      if (row.key !== rowKey || row.stedfaestelseAnnotation?.id === annotation.id) {
         return row;
      }
      changed = true;
      return { ...row, stedfaestelseAnnotation: annotation };
   });
   if (!changed) {
      return payload;
   }
   return {
      ...payload,
      matrix: {
         ...payload.matrix,
         rows,
      },
   };
}

function applyStedfaestelseRecords(
   payload: ServitutRedegoerelsePayload,
   records: ANNAnnotationRecord[],
): ServitutRedegoerelsePayload {
   let changed = false;
   const rows = payload.matrix.rows.map((row) => {
      const match = pickStedfaestelseForRow(records, {
         documentId: getCanonicalServitutAnnotationDocumentId(row),
         datoloebenr: row.datoLoebenr,
      });
      if (!match || match.id === row.stedfaestelseAnnotation?.id) {
         return row;
      }
      changed = true;
      return { ...row, stedfaestelseAnnotation: match };
   });
   if (!changed) {
      return payload;
   }
   return {
      ...payload,
      matrix: {
         ...payload.matrix,
         rows,
      },
   };
}

function applyStedfaestelseFromRecords(
   payload: ServitutRedegoerelsePayload | null,
   records: ANNAnnotationRecord[],
   openedRowKey?: string | null,
): ServitutRedegoerelsePayload | null {
   if (!payload) {
      return payload;
   }
   const next = applyStedfaestelseRecords(payload, records);
   if (!openedRowKey) {
      return next;
   }
   const openedRow = next.matrix.rows.find((row) => row.key === openedRowKey);
   if (!openedRow || openedRow.stedfaestelseAnnotation) {
      return next;
   }
   const forOpenedRow = pickStedfaestelseForRow(records, {
      documentId: getCanonicalServitutAnnotationDocumentId(openedRow),
      datoloebenr: openedRow.datoLoebenr,
   }) || newestStedfaestelseRecord(records);
   return applyStedfaestelseToRow(next, openedRowKey, forOpenedRow);
}

function attachPaataleberettigetResolution(
   payload: ServitutRedegoerelsePayload,
   rowKey: string,
   resultItem: LLMAnalysisResultItem,
): ServitutRedegoerelsePayload {
   return {
      ...payload,
      matrix: {
         ...payload.matrix,
         rows: payload.matrix.rows.map((row) => {
            if (row.key !== rowKey) return row;
            const existing = row.analysisItem;
            if (!existing) {
               return {
                  ...row,
                  analysisItem: { ...resultItem, id: row.key },
               };
            }
            return {
               ...row,
               analysisItem: {
                  ...existing,
                  paataleberettiget: existing.paataleberettiget || resultItem.paataleberettiget,
                  paataleberettiget_confidence:
                     existing.paataleberettiget_confidence ?? resultItem.paataleberettiget_confidence,
                  paataleberettiget_resolution: resultItem.paataleberettiget_resolution,
               },
            };
         }),
      },
   };
}

function isAbortError(error: unknown): boolean {
   return error instanceof DOMException && error.name === "AbortError";
}

function createAbortError(): DOMException {
   return new DOMException("Aborted", "AbortError");
}

function waitForAbort(signal: AbortSignal): Promise<never> {
   return new Promise((_, reject) => {
      if (signal.aborted) {
         reject(createAbortError());
         return;
      }
      signal.addEventListener("abort", () => reject(createAbortError()), { once: true });
   });
}

async function waitForPending(promise: Promise<unknown>, signal?: AbortSignal): Promise<void> {
   if (!signal) {
      await promise;
      return;
   }
   await Promise.race([promise, waitForAbort(signal)]);
}

function createDeferred(): { promise: Promise<void>; resolve: () => void } {
   let resolve!: () => void;
   const promise = new Promise<void>((res) => {
      resolve = res;
   });
   return { promise, resolve };
}

function buildCustomUploadMetadata(
   row: ServitutRedegoerelsePayload["matrix"]["rows"][number],
   fileName?: string,
   caseDescription?: string,
): LLMMetadata {
   const normalizedCaseDescription = caseDescription?.trim();
   return {
      id: row.key,
      document: fileName || row.customPdf?.filename || row.aktDocument || "servitut.pdf",
      documentUrl: null,
      // A null aktId is the digital-XML signal. Use the row key so locate
      // treats the uploaded PDF as a scanned akt, not a tingbog XML document.
      aktId: row.key,
      tekster: [...getServitutTekster(row.servitut), row.summary].filter(Boolean),
      fromOvrigDataSource: false,
      caseDescription: normalizedCaseDescription || null,
   };
}

function buildRowEvaluationMetadata(
   row: ServitutRedegoerelsePayload["matrix"]["rows"][number],
   payload?: ServitutRedegoerelsePayload | null,
   caseDescription?: string,
): LLMMetadata {
   if (isCustomUploadRow(row)) {
      return buildCustomUploadMetadata(row, undefined, caseDescription);
   }
   const fromPayload = payload?.metadata.find((metadata) => metadata.id === row.key);
   const normalizedCaseDescription = caseDescription?.trim();
   const metadata: LLMMetadata = fromPayload ? { ...fromPayload } : {
      id: row.key,
      document: row.annotationDocument?.filename || row.aktDocument || row.exportFileName || `${row.displayAktId || row.key}.pdf`,
      documentUrl: row.documentUrl || null,
      aktId: row.aktId || row.analysisAktId || row.key,
      tekster: [...getServitutTekster(row.servitut), row.summary].filter(Boolean),
      fromOvrigDataSource: row.pdfSource === "ovrigAkt",
   };
   return {
      ...metadata,
      id: row.key,
      caseDescription: normalizedCaseDescription || metadata.caseDescription || null,
   };
}

async function locateOneUploadedRow(
   row: ServitutRedegoerelsePayload["matrix"]["rows"][number],
   payload: ServitutRedegoerelsePayload,
   analyzer: ReturnType<typeof createANNLLMAnalyzer>,
   signal: AbortSignal,
): Promise<LLMAnalysisResultItem> {
   const sourceKey = row.documentSource === "customUpload"
      ? row.customPdf?.s3Key
      : row.annotationDocument?.s3Key;
   if (!sourceKey) {
      throw new Error(Localization.getText("No PDF available for this document"));
   }
   const localPdf = await downloadOverlayImage(sourceKey);
   if (signal.aborted) {
      throw createAbortError();
   }
   const result = await analyzer.locateServitutDocument({
      metadata: withSkippedPaataleResolution(
         buildRowEvaluationMetadata(row, payload),
      ),
      pdfBlob: localPdf.blob,
      documentFileName:
         row.customPdf?.filename
         || row.annotationDocument?.filename
         || localPdf.fileName
         || row.exportFileName
         || "servitut.pdf",
      signal,
   });
   if (signal.aborted) {
      throw createAbortError();
   }
   return { ...result, id: row.key };
}

async function getCutServitutPdfForContentAnalysis(
   row: ServitutRedegoerelsePayload["matrix"]["rows"][number],
   signal?: AbortSignal,
): Promise<Blob | null> {
   if ((row.handling ?? "legacyLlmPdf") === "electronicZipPackage") {
      return null;
   }
   const aktPages = normalizePages(
      row.selectedPages?.length ? row.selectedPages : (row.analysisItem?.pages_kept || []),
   );
   if (row.documentSource === "customUpload") {
      if (!row.customPdf?.s3Key) {
         return null;
      }
      const { blob } = await downloadOverlayImage(row.customPdf.s3Key);
      if (!aktPages.length) {
         return blob;
      }
      const pageCount = await getPdfPageCount(blob).catch(() => 0);
      const pages = pageCount ? clampPagesToDocument(aktPages, pageCount) : aktPages;
      return pages.length ? cutPdfToSelectedPages(blob, pages) : blob;
   }
   if (row.documentSource === "annotation") {
      if (!row.annotationDocument?.s3Key) {
         return null;
      }
      const { blob } = await downloadOverlayImage(row.annotationDocument.s3Key);
      const extracted = getAnnotationSourceExtractedPages(row);
      if (extracted.length) {
         const localPages = mapAktPagesToCutSelection(aktPages, extracted);
         if (localPages?.length && localPages.length !== extracted.length) {
            return cutPdfToSelectedPages(blob, localPages);
         }
         return blob;
      }
      return aktPages.length ? cutPdfToSelectedPages(blob, aktPages) : blob;
   }
   const sourceBlob = row.aktId
      ? await getANNLLMAnalyzer().fetchPdf(row.aktId, signal)
      : await getCachedRowPdfBlob(row, "source");
   if (!sourceBlob) {
      return null;
   }
   return aktPages.length ? cutPdfToSelectedPages(sourceBlob, aktPages) : sourceBlob;
}

async function getRowOriginalPdfBlob(row: ServitutRedegoerelsePayload["matrix"]["rows"][number]): Promise<Blob | null> {
   if (row.customPdf?.s3Key) {
      const { blob } = await downloadOverlayImage(row.customPdf.s3Key);
      return blob;
   }
   if ((row.handling ?? "legacyLlmPdf") === "electronicZipPackage") {
      const target = row.electronicServitut ? getTingbogDownloadTarget(row.electronicServitut) : undefined;
      return target ? await fetchTingbogDocumentBlob(target) : null;
   }
   if (row.aktId) {
      return await getANNLLMAnalyzer().fetchPdf(row.aktId);
   }
   return getRowSourcePdfBlob(row);
}

function rowPdfCacheKey(
   row: ServitutRedegoerelsePayload["matrix"]["rows"][number],
   kind: "original" | "source",
): string | null {
   if (row.customPdf?.s3Key) {
      return `${kind}:custom:${row.customPdf.s3Key}`;
   }
   if ((row.handling ?? "legacyLlmPdf") === "electronicZipPackage") {
      const target = row.electronicServitut ? getTingbogDownloadTarget(row.electronicServitut) : undefined;
      if (!target) {
         return null;
      }
      return target.kind === "akt"
         ? `${kind}:electronic:akt:${target.value}`
         : `${kind}:electronic:gateway:${target.dokumentIdentifikator}`;
   }
   if (kind === "source" && row.documentSource === "annotation" && row.annotationDocument?.s3Key) {
      return `${kind}:annotation:${row.annotationDocument.s3Key}`;
   }
   if (row.aktId) {
      return `${kind}:akt:${row.aktId}`;
   }
   if (row.annotationDocument?.s3Key) {
      return `${kind}:annotation:${row.annotationDocument.s3Key}`;
   }
   return null;
}

async function getViewerSourcePdfBlob(
   row: ServitutRedegoerelsePayload["matrix"]["rows"][number],
): Promise<Blob> {
   const blob = await getCachedRowPdfBlob(row, "source");
   if (!blob) {
      throw new Error(Localization.getText("No PDF available for this document"));
   }
   return blob;
}

async function getCachedRowPdfBlob(
   row: ServitutRedegoerelsePayload["matrix"]["rows"][number],
   kind: "original" | "source",
   cache?: Map<string, Promise<Blob | null>>,
): Promise<Blob | null> {
   const loader = kind === "original" ? getRowOriginalPdfBlob : getRowSourcePdfBlob;
   if (!cache) {
      return loader(row);
   }
   const key = rowPdfCacheKey(row, kind);
   if (!key) {
      return loader(row);
   }
   const existing = cache.get(key);
   if (existing) {
      return existing;
   }
   const pending = loader(row).catch((error) => {
      if (cache.get(key) === pending) {
         cache.delete(key);
      }
      throw error;
   });
   cache.set(key, pending);
   return pending;
}

function canSaveRowWithoutPdfFetch(
   row: ServitutRedegoerelsePayload["matrix"]["rows"][number],
   pagesOnly?: boolean,
): boolean {
   const storedDocument = row.annotationDocument || row.annotationRecord?.document;
   return Boolean(
      pagesOnly
      && storedDocument?.s3Key
      && (row.documentSource === "annotation" || row.annotationRecord),
   );
}

async function getRowSourcePdfBlob(row: ServitutRedegoerelsePayload["matrix"]["rows"][number]): Promise<Blob | null> {
   if (row.documentSource === "customUpload") {
      if (!row.customPdf?.s3Key) {
         return null;
      }
      const { blob } = await downloadOverlayImage(row.customPdf.s3Key);
      return blob;
   }
   if (row.documentSource === "annotation") {
      if (!row.annotationDocument?.s3Key) {
         return null;
      }
      const { blob } = await downloadOverlayImage(row.annotationDocument.s3Key);
      return blob;
   }
   if ((row.handling ?? "legacyLlmPdf") === "electronicZipPackage") {
      const target = row.electronicServitut ? getTingbogDownloadTarget(row.electronicServitut) : undefined;
      return target ? await fetchTingbogDocumentBlob(target) : null;
   }
   if (!row.aktId) {
      return null;
   }
   return await getANNLLMAnalyzer().fetchPdf(row.aktId);
}

function canSaveServitutRowAsAnnotation(row: ServitutRedegoerelsePayload["matrix"]["rows"][number]): boolean {
   if (row.documentSource === "customUpload") {
      return row.pdfStatus === "ready" && Boolean(row.customPdf?.s3Key);
   }
   if (row.annotationRecord || row.documentSource === "annotation" || row.documentSource === "rigsarkivetRequired") {
      return false;
   }
   if ((row.handling ?? "legacyLlmPdf") === "electronicZipPackage") {
      return row.pdfStatus === "ready" && Boolean(row.electronicServitut);
   }
   return row.pdfStatus === "ready" && Boolean(row.aktId);
}

async function yieldPreparePaint(): Promise<void> {
   await new Promise<void>((resolve) => {
      requestAnimationFrame(() => resolve());
   });
}

function stamOplysningerHintForRow(
   payload: ServitutRedegoerelsePayload,
   row: ServitutRedegoerelsePayload["matrix"]["rows"][number],
): ServitutAktSearchHint | undefined {
   for (const bfe of row.includedBfes || []) {
      const hint = payload.aktSearchByBfe?.[String(bfe)];
      if (hint) {
         return hint;
      }
   }
   return undefined;
}

function landRegistryBindPatch(
   row: ServitutRedegoerelsePayload["matrix"]["rows"][number],
): Pick<
   ServitutRedegoerelsePayload["matrix"]["rows"][number],
   "documentSource" | "pdfStatus" | "pdfSource" | "aktDocument" | "aktId" | "documentUrl" | "electronicServitut" | "sourceMessage"
> {
   return {
      documentSource: row.documentSource,
      pdfStatus: row.pdfStatus,
      pdfSource: row.pdfSource,
      aktDocument: row.aktDocument,
      aktId: row.aktId,
      documentUrl: row.documentUrl,
      electronicServitut: row.electronicServitut,
      sourceMessage: row.sourceMessage,
   };
}

function applySearchFailureToPreparedRow(
   row: ServitutRedegoerelsePayload["matrix"]["rows"][number],
   pdfStatus: "missing" | "failed",
   stamOplysninger?: ServitutAktSearchHint,
): ServitutRedegoerelsePayload["matrix"]["rows"][number] {
   const searchTargets = getServitutAktSearchTargets(row.servitut, stamOplysninger);
   const isRigsarkivet = isBeforeRigsarkivetCutoff(row.servitut, row.datoLoebenr);
   return {
      ...row,
      pdfStatus,
      sourceMessage: buildServitutSearchFailureNote(searchTargets, isRigsarkivet),
      documentSource: isRigsarkivet ? "rigsarkivetRequired" : "tingbog",
      aktDocument: isRigsarkivet ? Localization.getText("Rigsarkivet") : row.aktDocument,
   };
}

async function bindPreparedRowToLandRegistry(
   row: ServitutRedegoerelsePayload["matrix"]["rows"][number],
   options: {
      stamOplysninger?: ServitutAktSearchHint;
      firstBfe?: ServitutRapportBfeData;
      propertyDataList?: ServitutRapportBfeData[];
      filesByDocument?: Map<string, ServitutRedegoerelsePreparedAkt>;
   } = {},
): Promise<{
   row: ServitutRedegoerelsePayload["matrix"]["rows"][number];
   metadata?: LLMMetadata;
}> {
   const stamOplysninger = options.stamOplysninger || options.firstBfe?.stamOplysninger;
   if ((row.handling ?? "legacyLlmPdf") === "electronicZipPackage") {
      if (!isEligibleDigitalServitut(row.servitut)) {
         return { row: applySearchFailureToPreparedRow(row, "missing", stamOplysninger) };
      }
      const xmlText = await fetchDigitalServitutXml(row.servitut).catch((error) => {
         console.error("[Servitutredegørelse] failed to fetch digital servitut XML", row.key, error);
         return null;
      });
      return {
         row: {
            ...row,
            documentSource: "tingbog",
            pdfStatus: "ready",
            pdfSource: undefined,
            aktId: undefined,
            documentUrl: undefined,
            aktDocument: Localization.getText("Digital land registry PDF"),
            electronicServitut: row.electronicServitut || ({ ...row.servitut } as ServitutElectronicPackageServitut),
            sourceMessage: undefined,
         },
         metadata: xmlText ? {
            id: row.key,
            document: xmlText,
            documentUrl: null,
            aktId: null,
            tekster: getServitutTekster(row.servitut),
            fromOvrigDataSource: null,
         } : undefined,
      };
   }

   const analyzer = getANNLLMAnalyzer();
   const candidates = getServitutAktFetchCandidates(row.servitut, stamOplysninger);
   if (!candidates.length) {
      return { row: applySearchFailureToPreparedRow(row, "missing", stamOplysninger) };
   }

   const filesByDocument = options.filesByDocument ?? new Map<string, ServitutRedegoerelsePreparedAkt>();
   const builtMetadata = analyzer.buildMetadata(
      row.servitut,
      stamOplysninger,
      undefined,
      undefined,
      options.firstBfe ?? options.propertyDataList?.[0],
      { propertyDataList: options.propertyDataList },
   );

   for (const candidate of candidates) {
      try {
         let file = filesByDocument.get(candidate.aktId);
         if (!file) {
            file = {
               document: builtMetadata.document,
               aktId: candidate.aktId,
               documentUrl: await resolvePreparedAktUrl(analyzer, candidate.aktId),
               source: candidate.source,
               servitutKeys: [],
            };
            filesByDocument.set(candidate.aktId, file);
         }
         file.servitutKeys.push(row.key);
         return {
            row: {
               ...row,
               documentSource: "tingbog",
               pdfStatus: "ready",
               pdfSource: candidate.source,
               aktDocument: builtMetadata.document,
               aktId: candidate.aktId,
               documentUrl: file.documentUrl,
               electronicServitut: undefined,
               sourceMessage: undefined,
            },
            metadata: {
               ...builtMetadata,
               id: row.key,
               documentUrl: file.documentUrl,
               fromOvrigDataSource: candidate.source === "ovrigAkt",
            },
         };
      } catch (error) {
         console.error(
            "[Servitutredegørelse] failed to prepare akt",
            row.key,
            candidate.source,
            candidate.aktId,
            error,
         );
      }
   }

   return { row: applySearchFailureToPreparedRow(row, "failed", stamOplysninger) };
}

async function prepareServitutRedegoerelsePayload(
   data: ServitutRapportBfeData[],
   colorList: SFEColorEntry[],
   options: {
      organizationId?: string;
      onProgress?: (progress: ServitutRedegoerelseLoadProgress) => void;
   } = {},
): Promise<ServitutRedegoerelsePayload> {
   const rows = buildServitutMatrixRows(data);
   const filesByDocument = new Map<string, ServitutRedegoerelsePreparedAkt>();
   const metadata: LLMMetadata[] = [];
   const analyzer = getANNLLMAnalyzer();
   options.onProgress?.({ stage: "easements", completed: 0, total: rows.length });
   await yieldPreparePaint();

   for (let i = 0; i < rows.length; i++) {
      const row = rows[i];
      try {
      const firstBfe = data.find((bfeData) => row.includedBfes.has(Number(bfeData.BFEnummer)));
      const contextAnnotations = await listServitutContextAnnotations(row, options.organizationId);
      row.fieldAnnotations = collectFieldNotes(contextAnnotations, row.datoLoebenr);
      row.stedfaestelseAnnotation = pickStedfaestelseForRow(contextAnnotations, {
         documentId: getCanonicalServitutAnnotationDocumentId(row),
         datoloebenr: row.datoLoebenr,
      }) || undefined;
      const existingAnnotation = findServitutDocumentAnnotation(contextAnnotations, row.datoLoebenr);
      if (existingAnnotation?.document?.s3Key) {
         row.documentSource = "annotation";
         row.annotationRecord = existingAnnotation;
         row.annotationDocument = existingAnnotation.document;
         row.provenance = overlayAcceptedFieldStatuses(
            existingAnnotation.document?.provenance || {
               ...emptyProvenance(),
               source: existingAnnotation.document?.s3Key ? "file" : "akt",
               reviewState: existingAnnotation.verified ? "checked" : "unchecked",
            },
            row.fieldAnnotations,
         );
         row.pdfStatus = "ready";
         row.aktDocument = existingAnnotation.document.filename || row.exportFileName || Localization.getText("Saved annotation PDF");
         const hydratedAnalysis = hydrateAnalysisItemFromAnnotation(existingAnnotation, row.key);
         if (hydratedAnalysis) {
            row.analysisItem = hydratedAnalysis;
            row.resultsAccepted = Boolean(existingAnnotation.document?.llmAnalysis?.resultsAccepted);
            row.selectedPages = normalizePages(
               existingAnnotation.document.extractedPages || hydratedAnalysis.pages_kept || [],
            );
            row.sourceMessage = Localization.getText("Using saved annotation PDF");
         } else {
            row.sourceMessage = Localization.getText("Using saved annotation PDF");
         }
         continue;
      }
      if (Object.keys(row.fieldAnnotations || {}).length) {
         row.provenance = overlayAcceptedFieldStatuses(row.provenance, row.fieldAnnotations);
      }

      // Always try Tingbogen first — older acts (before 1 Apr 1927) are sometimes present.
      // Only refer to Rigsarkivet when the search does not yield a document.
      row.documentSource = "tingbog";
      const searchTargets = getServitutAktSearchTargets(row.servitut, firstBfe?.stamOplysninger);
      if (row.handling === "electronicZipPackage") {
         if (!isEligibleDigitalServitut(row.servitut)) {
            row.pdfStatus = "missing";
            markServitutNotFoundAfterSearch(row, searchTargets);
         } else {
            row.pdfStatus = "ready";
            row.aktDocument = Localization.getText("Digital land registry PDF");
            const xmlText = await fetchDigitalServitutXml(row.servitut).catch((error) => {
               console.error("[Servitutredegørelse] failed to fetch digital servitut XML", row.key, error);
               return null;
            });
            if (xmlText) {
               const entryMetadata: LLMMetadata = {
                  id: row.key,
                  document: xmlText,
                  documentUrl: null,
                  aktId: null,
                  tekster: getServitutTekster(row.servitut),
                  fromOvrigDataSource: null,
               };
               metadata.push(entryMetadata);
            }
         }
         continue;
      }

      const aktCandidates = getServitutAktFetchCandidates(row.servitut, firstBfe?.stamOplysninger);
      if (!aktCandidates.length) {
         row.pdfStatus = "missing";
         markServitutNotFoundAfterSearch(row, searchTargets);
         continue;
      }

      const includedBfeData = Array.from(row.includedBfes)
         .map((bfe) => data.find((bfeData) => Number(bfeData.BFEnummer) === bfe))
         .filter((entry): entry is ServitutRapportBfeData => Boolean(entry));
      const builtMetadata = analyzer.buildMetadata(
         row.servitut,
         firstBfe?.stamOplysninger,
         undefined,
         undefined,
         firstBfe ?? includedBfeData[0],
         { propertyDataList: includedBfeData }
      );

      let prepared = false;
      for (const candidate of aktCandidates) {
         try {
            let file = filesByDocument.get(candidate.aktId);
            if (!file) {
               file = {
                  document: builtMetadata.document,
                  aktId: candidate.aktId,
                  documentUrl: await resolvePreparedAktUrl(analyzer, candidate.aktId),
                  source: candidate.source,
                  servitutKeys: [],
               };
               filesByDocument.set(candidate.aktId, file);
            }
            const entryMetadata: LLMMetadata = {
               ...builtMetadata,
               id: row.key,
               documentUrl: file.documentUrl,
               fromOvrigDataSource: candidate.source === "ovrigAkt",
            };
            file.servitutKeys.push(row.key);
            metadata.push(entryMetadata);
            row.pdfStatus = "ready";
            row.pdfSource = candidate.source;
            row.aktDocument = builtMetadata.document;
            row.aktId = candidate.aktId;
            row.documentUrl = file.documentUrl;
            prepared = true;
            break;
         } catch (error) {
            console.error(
               "[Servitutredegørelse] failed to prepare akt",
               row.key,
               candidate.source,
               candidate.aktId,
               error,
            );
         }
      }
      if (!prepared) {
         row.pdfStatus = "failed";
         markServitutNotFoundAfterSearch(row, searchTargets);
      }
      } finally {
         options.onProgress?.({ stage: "easements", completed: i + 1, total: rows.length });
         await yieldPreparePaint();
      }
   }

   const electronicReadyRows = rows.filter((r) => r.handling === "electronicZipPackage" && r.pdfStatus === "ready").length;
   const annotationReadyRows = rows.filter((r) => r.documentSource === "annotation" && r.pdfStatus === "ready").length;

   return {
      metadata,
      aktSearchByBfe: Object.fromEntries(
         data.map((entry) => [
            String(entry.BFEnummer),
            { indskannetAktDokumentId: entry.stamOplysninger?.indskannetAktDokumentId ?? null },
         ]),
      ),
      matrix: {
         columns: colorList.map((entry) => entry.sfe),
         columnLabels: buildServitutMatrixColumnLabels(data, colorList),
         columnDetails: buildServitutMatrixColumnDetails(data, colorList),
         uniqueAktCount: filesByDocument.size + electronicReadyRows + annotationReadyRows,
         rows: rows.map((row) => ({
            key: row.key,
            servitut: row.servitut,
            handling: row.handling,
            electronicServitut:
               row.handling === "electronicZipPackage" && row.pdfStatus === "ready"
                  ? ({ ...row.servitut } as ServitutElectronicPackageServitut)
                  : undefined,
            displayAktId: row.displayAktId,
            datoLoebenr: row.datoLoebenr,
            priority: row.priority,
            summary: row.summary,
            analysisAktId: row.analysisAktId,
            includedBfes: Array.from(row.includedBfes),
            pdfStatus: row.pdfStatus,
            pdfSource: row.pdfSource,
            aktDocument: row.aktDocument,
            aktId: row.aktId,
            documentUrl: row.documentUrl,
            analysisItem: row.analysisItem,
            selectedPages: row.selectedPages,
            exportFileName: row.exportFileName,
            documentSource: row.documentSource,
            customPdf: row.customPdf,
            annotationRecord: row.annotationRecord,
            annotationDocument: row.annotationDocument,
            sourceMessage: row.sourceMessage,
            resultsAccepted: row.resultsAccepted,
            provenance: overlayAcceptedFieldStatuses(row.provenance, row.fieldAnnotations),
            fieldAnnotations: row.fieldAnnotations,
            stedfaestelseAnnotation: row.stedfaestelseAnnotation,
         })),
      },
   };
}

function buildServitutMatrixRows(data: ServitutRapportBfeData[]): ServitutMatrixRow[] {
   const rowsByKey = new Map<string, ServitutMatrixRow>();
   data.forEach((bfeData) => {
      const bfe = Number(bfeData.BFEnummer);
      (bfeData.servitutter || []).forEach((servitut) => {
         const handling: ServitutRedegoerelseRowHandling = isTingbogElectronicDocument(servitut)
            ? "electronicZipPackage"
            : "legacyLlmPdf";
         const key = getServitutKey(servitut);
         const existing = rowsByKey.get(key);
         if (existing) {
            existing.includedBfes.add(bfe);
            return;
         }
         rowsByKey.set(key, {
            key,
            servitut,
            handling,
            displayAktId: aktIdFormat(servitut.aktId || servitut.aktAliasId || key),
            datoLoebenr: getServitutDatoLoebenr(servitut, key),
            priority: `${servitut.prioritetNummer || ""}`,
            summary: getServitutSummary(servitut),
            analysisAktId: servitut?.aktId || servitut?.aktAliasId || key,
            includedBfes: new Set([bfe]),
            pdfStatus: "pending",
            exportFileName: getServitutMatrixFileName(servitut),
         });
      });
   });
   return Array.from(rowsByKey.values()).sort((a, b) => {
      const bfeDiff = Math.min(...Array.from(a.includedBfes)) - Math.min(...Array.from(b.includedBfes));
      if (bfeDiff !== 0) return bfeDiff;
      return comparePriorityThenDato(a, b);
   });
}

function getServitutKey(servitut: any): string {
   const identity = normalizeServitutIdentity(servitut);
   return identity.documentId || identity.aktAliasId || identity.aktId || servitut?.dokumentFilnavn || getServitutSummary(servitut);
}

const RIGSARKIVET_TINGBOG_CUTOFF_MS = Date.UTC(1927, 3, 1, 0, 0, 0, 0);

function parseDatoLoebenrToUtcMs(value?: string): number | null {
   const match = /^(\d{2})(\d{2})(\d{4})-/.exec(value?.trim() || "");
   if (!match) {
      return null;
   }
   const day = Number(match[1]);
   const month = Number(match[2]) - 1;
   const year = Number(match[3]);
   const ms = Date.UTC(year, month, day, 0, 0, 0, 0);
   return Number.isNaN(ms) ? null : ms;
}

function isBeforeRigsarkivetCutoff(servitut: any, datoLoebenr?: string): boolean {
   const tinglysningsDatoMs = parseTinglysningsDatoToUtcMs(servitut?.tinglysningsDato);
   const datoloebenrMs = parseDatoLoebenrToUtcMs(normalizeServitutIdentity(servitut).datoloebenr)
      ?? parseDatoLoebenrToUtcMs(datoLoebenr);
   const ms = tinglysningsDatoMs ?? datoloebenrMs;
   return ms !== null && ms < RIGSARKIVET_TINGBOG_CUTOFF_MS;
}

type ServitutSearchAttempt = {
   korrektAktId?: string | null;
   servitutAktId?: string | null;
   textAktId?: string | null;
   ovrigAktId?: string | null;
};

function buildServitutSearchDetail(attempt: ServitutSearchAttempt): string {
   const tried: string[] = [];
   const korrektAkt = attempt.korrektAktId?.trim() || "";
   const servitutAkt = attempt.servitutAktId?.trim() || "";
   const textAkt = attempt.textAktId?.trim() || "";
   const ovrigAkt = attempt.ovrigAktId?.trim() || "";
   if (korrektAkt) {
      tried.push(korrektAkt);
   }
   if (servitutAkt && servitutAkt !== korrektAkt) {
      tried.push(servitutAkt);
   }
   if (textAkt && textAkt !== servitutAkt && textAkt !== korrektAkt) {
      tried.push(textAkt);
   }
   if (ovrigAkt && ovrigAkt !== servitutAkt && ovrigAkt !== textAkt && ovrigAkt !== korrektAkt) {
      tried.push(ovrigAkt);
   }
   if (tried.length) {
      return Localization.getFormattedText(
         "Searched akt candidates {aktIds} without finding the document.",
         { aktIds: tried.join(", ") },
      );
   }
   return Localization.getText(
      "Neither the easement deed nor the property other deed was available to search.",
   );
}

function buildServitutSearchFailureNote(attempt: ServitutSearchAttempt, isRigsarkivet: boolean): string {
   const searchDetail = buildServitutSearchDetail(attempt);
   if (isRigsarkivet) {
      return Localization.getFormattedText(
         "Easement was not found in Tingbogen (before 1 April 1927). {searchDetail} Look in Rigsarkivet or upload the document as an annotation.",
         { searchDetail },
      );
   }
   return Localization.getFormattedText(
      "Easement could not be found. {searchDetail}",
      { searchDetail },
   );
}

function markServitutNotFoundAfterSearch(row: ServitutMatrixRow, attempt: ServitutSearchAttempt): void {
   const isRigsarkivet = isBeforeRigsarkivetCutoff(row.servitut, row.datoLoebenr);
   if (row.pdfStatus !== "failed") {
      row.pdfStatus = "missing";
   }
   row.sourceMessage = buildServitutSearchFailureNote(attempt, isRigsarkivet);
   if (isRigsarkivet) {
      row.documentSource = "rigsarkivetRequired";
      row.aktDocument = Localization.getText("Rigsarkivet");
   }
}

function getCanonicalServitutAnnotationDocumentId(row: { servitut?: any; key: string }): string {
   const identity = normalizeServitutIdentity(row.servitut || {});
   return getServitutAnnotationDocumentId(row.servitut || {}) || identity.documentId || row.key;
}

async function listServitutContextAnnotations(
   row: { key: string; servitut?: any; datoLoebenr?: string },
   organizationId?: string,
): Promise<ANNAnnotationRecord[]> {
   const documentId = getCanonicalServitutAnnotationDocumentId(row);
   if (!documentId) {
      return [];
   }
   try {
      return await ANNManager.listAnnotations(
         AnnotationContextType.Servitut,
         documentId,
         organizationId,
      );
   } catch (error) {
      console.warn("[Servitutredegørelse] failed to look up servitut annotations", row.key, error);
      return [];
   }
}

async function findAllExistingServitutPdfAnnotations(
   row: { key: string; servitut?: any; datoLoebenr?: string },
   organizationId?: string,
): Promise<ANNAnnotationRecord[]> {
   const annotations = await listServitutContextAnnotations(row, organizationId);
   return findAllServitutDocumentAnnotations(annotations, row.datoLoebenr);
}

async function findExistingServitutPdfAnnotation(
   row: { key: string; servitut?: any; datoLoebenr?: string },
   organizationId?: string,
): Promise<ANNAnnotationRecord | null> {
   return (await findAllExistingServitutPdfAnnotations(row, organizationId))[0] || null;
}

function getServitutSummary(servitut: any): string {
   return (servitut?.tekstSummarisk || (Array.isArray(servitut?.tekster) ? servitut.tekster.map((t: any) => t?.tekst ?? t).join(", ") : "") || "").trim();
}

function getServitutDatoLoebenr(servitut: any, fallback: string): string {
   const identity = normalizeServitutIdentity(servitut);
   return identity.datoloebenr || aktIdFormat(identity.aktAliasId || identity.aktId || servitut?.dokumentFilnavn || fallback);
}

function getServitutMatrixFileName(servitut: any): string {
   const identity = normalizeServitutIdentity(servitut);
   return getServitutDownloadFileName({
      datoloebenr: identity.datoloebenr,
      priorityNumber: servitut?.prioritetNummer,
      headingText: getServitutSummary(servitut),
      fallbackStem: identity.aktAliasId || identity.aktId || servitut?.dokumentFilnavn || "servitut",
   });
}

function getServitutTekster(servitut: any): string[] {
   const tekster: string[] = [];
   if (Array.isArray(servitut?.tekster)) {
      tekster.push(...servitut.tekster.map((t: any) => t?.tekst ?? t).filter(Boolean));
   }
   if (servitut?.tekstSummarisk) {
      tekster.push(servitut.tekstSummarisk);
   }
   return tekster;
}

async function fetchDigitalServitutXml(servitut: any): Promise<string | null> {
   const dokumentId = servitut?.dokumentId?.trim();
   if (!dokumentId) {
      return null;
   }
   const xmlDoc = await DC.TL.DokumentTingbog(dokumentId);
   return new XMLSerializer().serializeToString(xmlDoc);
}

function formatServitutType(value?: string): string {
   if (!value) return "-";
   const normalized = value.toLowerCase().replace(/\s+/g, "");
   if (normalized === "rådighedsservitut") return Localization.getText("Easement of use");
   if (normalized === "tilstandsservitut") return Localization.getText("Condition easement");
   return value;
}

type RowActionStatus = "saving" | "needsAnalysis" | "notSaved" | "saved";

function getRowActionStatus(params: {
   hasAnalysis: boolean;
   isSaving: boolean;
   wasJustSaved: boolean;
   hasUnsavedChange: boolean;
   hasSavedAnalysis: boolean;
}): RowActionStatus {
   if (params.isSaving) return "saving";
   if (!params.hasAnalysis) return "needsAnalysis";
   if (params.wasJustSaved) return "saved";
   if (params.hasUnsavedChange) return "notSaved";
   if (params.hasSavedAnalysis) return "saved";
   return "notSaved";
}

function normalizeConfidenceValue(value?: number): number | null {
   if (value === undefined || value === null) {
      return null;
   }
   if (!Number.isFinite(value)) {
      return null;
   }
   // Older cached/dev results used 1-10. Normalize defensively while backend now returns 0-1.
   return value > 1 ? value / 10 : value;
}

function formatPageRanges(pages: number[]): string {
   const sortedPages = normalizePages(pages);
   if (!sortedPages.length) return "-";

   const ranges: string[] = [];
   let rangeStart = sortedPages[0];
   let previousPage = sortedPages[0];
   for (let index = 1; index < sortedPages.length; index += 1) {
      const page = sortedPages[index];
      if (page === previousPage + 1) {
         previousPage = page;
         continue;
      }
      ranges.push(rangeStart === previousPage ? `${rangeStart}` : `${rangeStart}-${previousPage}`);
      rangeStart = page;
      previousPage = page;
   }
   ranges.push(rangeStart === previousPage ? `${rangeStart}` : `${rangeStart}-${previousPage}`);
   return ranges.join(", ");
}

function normalizePages(pages: number[]): number[] {
   return Array.from(new Set(pages))
      .filter((page) => Number.isFinite(page) && page > 0)
      .sort((a, b) => a - b);
}

function getAktSourceText(source?: ServitutRedegoerelsePreparedAkt["source"]): string {
   if (source === "ovrigAkt") return Localization.getText("Akt id was resolved via other deeds (øvrige akter).");
   if (source === "korrektAkt") return Localization.getText("Akt id was marked as the correct deed (KORREKT AKT).");
   if (source === "textAkt") return Localization.getText("Akt id was found in the easement text.");
   if (source === "servitutAkt") return Localization.getText("Akt id comes directly from the easement.");
   return Localization.getText("Akt source is not determined.");
}

async function resolvePreparedAktUrl(
   analyzer: { getAktFileUrl: (aktId: string) => Promise<string> },
   aktId: string,
): Promise<string> {
   try {
      return await analyzer.getAktFileUrl(aktId);
   } catch (error) {
      console.warn("[Servitutredegørelse] getAktFileUrl failed, using public tingbog URL", aktId, error);
      return TINGBOG_SCANNED_AKT_PREFIX + aktId;
   }
}

function sortServitutRows<T extends { includedBfes: number[]; priority: string; datoLoebenr?: string; displayAktId: string }>(
   rows: T[],
   columns: number[],
   datoSort: "default" | "asc" | "desc"
): T[] {
   return [...rows].sort((a, b) => {
      if (datoSort !== "default") {
         const dateDiff = compareDatoLoebenr(a, b);
         return datoSort === "asc" ? dateDiff : -dateDiff;
      }

      const bfeDiff = getFirstBfeColumnIndex(a.includedBfes, columns) - getFirstBfeColumnIndex(b.includedBfes, columns);
      if (bfeDiff !== 0) return bfeDiff;
      return comparePriorityThenDato(a, b);
   });
}

function sortRowsByReviewConfidence<T extends ServitutRedegoerelsePayload["matrix"]["rows"][number]>(
   rows: T[],
   direction: "asc" | "desc"
): T[] {
   return [...rows].sort((a, b) => {
      const diff = getReviewConfidenceSortValue(a) - getReviewConfidenceSortValue(b);
      return direction === "asc" ? diff : -diff;
   });
}

type ServitutMatrixSortColumn =
   | "dato"
   | "confidence"
   | "priority"
   | "servitutType"
   | "entitledParty"
   | "caseRelevant";

export type ServitutMatrixSortState =
   | { column: ServitutMatrixSortColumn; direction: "asc" | "desc" }
   | null;

function getCaseRelevantSortRank(
   row: ServitutRedegoerelsePayload["matrix"]["rows"][number],
   hasCaseDescription: boolean,
): number {
   if (!hasCaseDescription) return 0;
   const v = row.analysisItem?.case_relevant;
   if (v === true) return 3;
   if (v === false) return 2;
   return 1;
}

function sortServitutRowsByPriority(
   rows: ServitutRedegoerelsePayload["matrix"]["rows"][number][],
   columns: number[],
   direction: "asc" | "desc",
): ServitutRedegoerelsePayload["matrix"]["rows"][number][] {
   return [...rows].sort((a, b) => {
      const pa = parseInt(a.priority, 10);
      const pb = parseInt(b.priority, 10);
      const na = Number.isFinite(pa) ? pa : Number.MAX_SAFE_INTEGER;
      const nb = Number.isFinite(pb) ? pb : Number.MAX_SAFE_INTEGER;
      const diff = na - nb;
      if (diff !== 0) return direction === "asc" ? diff : -diff;
      const bfeDiff = getFirstBfeColumnIndex(a.includedBfes, columns) - getFirstBfeColumnIndex(b.includedBfes, columns);
      if (bfeDiff !== 0) return bfeDiff;
      return compareDatoLoebenr(a, b);
   });
}

function sortServitutRowsByServitutType(
   rows: ServitutRedegoerelsePayload["matrix"]["rows"][number][],
   columns: number[],
   direction: "asc" | "desc",
): ServitutRedegoerelsePayload["matrix"]["rows"][number][] {
   return [...rows].sort((a, b) => {
      const sa = formatServitutType(a.analysisItem?.servitut_type);
      const sb = formatServitutType(b.analysisItem?.servitut_type);
      const cmp = sa.localeCompare(sb, "da", { sensitivity: "base" });
      if (cmp !== 0) return direction === "asc" ? cmp : -cmp;
      const bfeDiff = getFirstBfeColumnIndex(a.includedBfes, columns) - getFirstBfeColumnIndex(b.includedBfes, columns);
      if (bfeDiff !== 0) return bfeDiff;
      return compareDatoLoebenr(a, b);
   });
}

function sortServitutRowsByEntitledParty(
   rows: ServitutRedegoerelsePayload["matrix"]["rows"][number][],
   columns: number[],
   direction: "asc" | "desc",
): ServitutRedegoerelsePayload["matrix"]["rows"][number][] {
   return [...rows].sort((a, b) => {
      const sa = (a.analysisItem?.paataleberettiget || "").trim();
      const sb = (b.analysisItem?.paataleberettiget || "").trim();
      const cmp = sa.localeCompare(sb, "da", { sensitivity: "base" });
      if (cmp !== 0) return direction === "asc" ? cmp : -cmp;
      const bfeDiff = getFirstBfeColumnIndex(a.includedBfes, columns) - getFirstBfeColumnIndex(b.includedBfes, columns);
      if (bfeDiff !== 0) return bfeDiff;
      return compareDatoLoebenr(a, b);
   });
}

function sortServitutRowsByCaseRelevant(
   rows: ServitutRedegoerelsePayload["matrix"]["rows"][number][],
   columns: number[],
   hasCaseDescription: boolean,
   direction: "asc" | "desc",
): ServitutRedegoerelsePayload["matrix"]["rows"][number][] {
   return [...rows].sort((a, b) => {
      const diff = getCaseRelevantSortRank(a, hasCaseDescription) - getCaseRelevantSortRank(b, hasCaseDescription);
      if (diff !== 0) return direction === "asc" ? diff : -diff;
      const bfeDiff = getFirstBfeColumnIndex(a.includedBfes, columns) - getFirstBfeColumnIndex(b.includedBfes, columns);
      if (bfeDiff !== 0) return bfeDiff;
      return compareDatoLoebenr(a, b);
   });
}

function applyServitutMatrixSort(
   rows: ServitutRedegoerelsePayload["matrix"]["rows"][number][],
   columns: number[],
   hasCaseDescription: boolean,
   sortState: ServitutMatrixSortState,
): ServitutRedegoerelsePayload["matrix"]["rows"][number][] {
   if (!sortState) {
      return sortServitutRows(rows, columns, "default");
   }
   const { column, direction } = sortState;
   const baseDefault = sortServitutRows(rows, columns, "default");

   switch (column) {
      case "dato":
         return sortServitutRows(rows, columns, direction === "asc" ? "asc" : "desc");
      case "confidence":
         return sortRowsByReviewConfidence(baseDefault, direction);
      case "priority":
         return sortServitutRowsByPriority(rows, columns, direction);
      case "servitutType":
         return sortServitutRowsByServitutType(rows, columns, direction);
      case "entitledParty":
         return sortServitutRowsByEntitledParty(rows, columns, direction);
      case "caseRelevant":
         return sortServitutRowsByCaseRelevant(rows, columns, hasCaseDescription, direction);
      default:
         return baseDefault;
   }
}

function getReviewConfidenceSortValue(row: ServitutRedegoerelsePayload["matrix"]["rows"][number]): number {
   const item = row.analysisItem;
   if (!item) {
      return -1;
   }
   const confidence = normalizeConfidenceValue(item.overall_confidence ?? item.confidence);
   if (item.needs_review === true) {
      return -0.5 + ((confidence ?? 0) / 100);
   }
   return confidence ?? -0.25;
}

function getFirstBfeColumnIndex(includedBfes: number[], columns: number[]): number {
   const columnIndexes = includedBfes.map((bfe) => {
      const index = columns.indexOf(bfe);
      return index === -1 ? Number.MAX_SAFE_INTEGER : index;
   });
   return Math.min(...columnIndexes);
}

function comparePriorityThenDato(
   a: { priority: string; datoLoebenr?: string; displayAktId: string },
   b: { priority: string; datoLoebenr?: string; displayAktId: string }
): number {
   const priorityDiff = (parseInt(a.priority) || 0) - (parseInt(b.priority) || 0);
   if (priorityDiff !== 0) return priorityDiff;
   return compareDatoLoebenr(a, b);
}

function compareDatoLoebenr(
   a: { datoLoebenr?: string; displayAktId: string },
   b: { datoLoebenr?: string; displayAktId: string }
): number {
   const aDate = parseDatoLoebenrDate(a.datoLoebenr || a.displayAktId);
   const bDate = parseDatoLoebenrDate(b.datoLoebenr || b.displayAktId);
   if (aDate !== bDate) return aDate - bDate;
   return (a.datoLoebenr || a.displayAktId).localeCompare(b.datoLoebenr || b.displayAktId);
}

function parseDatoLoebenrDate(value: string): number {
   const trimmed = value.trim();
   const danishDate = /^(\d{2})\.(\d{2})\.(\d{4})/.exec(trimmed);
   if (danishDate) {
      return toValidUtcDate(Number(danishDate[3]), Number(danishDate[2]), Number(danishDate[1]));
   }
   const compactDanishDate = /^(\d{2})(\d{2})(\d{4})/.exec(trimmed);
   if (compactDanishDate) {
      const date = toValidUtcDate(Number(compactDanishDate[3]), Number(compactDanishDate[2]), Number(compactDanishDate[1]));
      if (date !== Number.MAX_SAFE_INTEGER) return date;
   }
   const compactIsoDate = /^(\d{4})(\d{2})(\d{2})/.exec(trimmed);
   if (compactIsoDate) {
      return toValidUtcDate(Number(compactIsoDate[1]), Number(compactIsoDate[2]), Number(compactIsoDate[3]));
   }
   return Number.MAX_SAFE_INTEGER;
}

function toValidUtcDate(year: number, month: number, day: number): number {
   if (year < 1800 || year > 2200 || month < 1 || month > 12 || day < 1 || day > 31) {
      return Number.MAX_SAFE_INTEGER;
   }
   return Date.UTC(year, month - 1, day);
}

function buildServitutMatrixExcelBlob(
   payload: ServitutRedegoerelsePayload,
   options?: { includeAnalysisFields?: boolean },
): Blob {
   const rows = buildServitutMatrixExcelRows(payload, options);
   const worksheet = XlsxUtils.json_to_sheet(rows);
   const workbook = XlsxUtils.book_new();
   XlsxUtils.book_append_sheet(workbook, worksheet, "Servitutredegørelse");
   const buffer = writeXlsx(workbook, { bookType: "xlsx", type: "array" }) as ArrayBuffer;
   return new Blob([buffer], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" });
}

function buildServitutMatrixExcelRows(
   payload: ServitutRedegoerelsePayload,
   options?: { includeAnalysisFields?: boolean },
) {
   const caseDescription = payload.caseDescription?.trim() || "";
   const includeAnalysisFields = options?.includeAnalysisFields === true;
   return sortServitutRows(payload.matrix.rows, payload.matrix.columns, "default").map((row) => {
      const record: Record<string, string | number> = {
         "Prioritet": row.priority,
         "Dato-løbenummer": row.datoLoebenr || row.displayAktId,
         "Servituttekst": getRowHeading(row),
      };
      if (includeAnalysisFields) {
         record["Resume"] = getRowFieldText(row, "resume", caseDescription);
         record["Servituttype"] = getRowFieldText(row, "condition", caseDescription);
         record["Påtaleberettiget"] = getRowFieldText(row, "entitledParty", caseDescription);
         record["Redegørelse"] = getRowFieldText(row, "statement", caseDescription);
      }
      payload.matrix.columns.forEach((bfe) => {
         record[getServitutMatrixColumnLabel(payload, bfe)] = row.includedBfes.includes(bfe) ? "x" : "";
      });
      return record;
   });
}

async function buildServitutRedegoerelseZip(
   payload: ServitutRedegoerelsePayload,
   onProgress?: (state: ServitutRedegoerelseExportProgress) => void | Promise<void>,
   options?: { includeAnalysisFields?: boolean },
): Promise<Blob> {
   const zip = new JSZip();
   const usedNames = new Set<string>();
   const analyzer = getANNLLMAnalyzer();
   const rows = sortServitutRows(
      payload.matrix.rows,
      payload.matrix.columns,
      "default"
   );
   const totalSteps = 2 + rows.length;

   const emit = async (state: ServitutRedegoerelseExportProgress): Promise<void> => {
      await onProgress?.(state);
   };

   let completedSteps = 0;
   await emit({
      completedSteps,
      totalSteps,
      stage: "spreadsheet",
      activeRowKey: null,
   });

   zip.file(
      createUniqueZipEntryName("Servitutredegørelse.xlsx", usedNames),
      buildServitutMatrixExcelBlob(payload, { includeAnalysisFields: options?.includeAnalysisFields }),
   );
   completedSteps += 1;
   await emit({
      completedSteps,
      totalSteps,
      stage: "spreadsheet",
      activeRowKey: null,
   });

   for (let docIndex = 0; docIndex < rows.length; docIndex++) {
      const row = rows[docIndex];
      await emit({
         completedSteps,
         totalSteps,
         stage: "document",
         activeRowKey: row.key,
         documentIndex: docIndex + 1,
         documentTotal: rows.length,
      });

      const handling = row.handling ?? "legacyLlmPdf";
      if (handling === "electronicZipPackage") {
         const baseFileName = row.exportFileName || `${row.displayAktId || row.key}.pdf`;
         if (!row.electronicServitut) {
            zip.file(
               createUniqueZipEntryName(toTxtFileName(baseFileName), usedNames),
               `Digital servitutpakke ikke tilgængelig (${row.displayAktId}).\n${row.summary || ""}`,
            );
         } else {
            try {
               const packageResult = await addServitutPackageToZip(zip, row.electronicServitut);
               if (packageResult.warnings.length) {
                  console.warn("[Servitutredegørelse] digital pakke-advarsler", row.key, packageResult.warnings);
               }
            } catch (error) {
               zip.file(
                  createUniqueZipEntryName(toTxtFileName(baseFileName), usedNames),
                  `Kunne ikke hente digital servitutpakke: ${error instanceof Error ? error.message : String(error)}\n\n${row.summary || "Ingen servituttekst."}`,
               );
            }
         }
      } else {
         const pagesToKeep = normalizePages(row.selectedPages || []);
         const baseFileName = row.exportFileName || `${row.displayAktId || row.key}.pdf`;
         try {
            const pdfBlob = await getExportSourcePdfBlob(row, analyzer);
            if (!pdfBlob) {
               throw new Error(
                  row.documentSource === "annotation"
                     ? "PDF mangler"
                     : !row.aktId
                        ? "PDF kunne ikke hentes, fordi akt-id mangler."
                        : "PDF mangler",
               );
            }
            const exportBlob = await cutExportPdfIfNeeded(row, pdfBlob, pagesToKeep);
            zip.file(createUniqueZipEntryName(baseFileName, usedNames), exportBlob);
         } catch (error) {
            zip.file(
               createUniqueZipEntryName(toTxtFileName(baseFileName), usedNames),
               `${buildMissingServitutText(row)}\n\n${error instanceof Error ? error.message : String(error)}`,
            );
         }
      }

      completedSteps += 1;
   }

   await emit({
      completedSteps,
      totalSteps,
      stage: "compress",
      activeRowKey: null,
   });

   const blob = await zip.generateAsync({ type: "blob", compression: "DEFLATE" });
   completedSteps += 1;
   await emit({
      completedSteps,
      totalSteps,
      stage: "compress",
      activeRowKey: null,
   });

   return blob;
}

async function getExportSourcePdfBlob(
   row: ServitutRedegoerelsePayload["matrix"]["rows"][number],
   analyzer: ReturnType<typeof getANNLLMAnalyzer>,
): Promise<Blob | null> {
   if (row.documentSource === "annotation" || row.documentSource === "customUpload") {
      return getRowSourcePdfBlob(row);
   }
   if (row.aktId) {
      return analyzer.fetchPdf(row.aktId);
   }
   return getRowSourcePdfBlob(row);
}

async function cutExportPdfIfNeeded(
   row: ServitutRedegoerelsePayload["matrix"]["rows"][number],
   pdfBlob: Blob,
   pagesToKeep: number[],
): Promise<Blob> {
   if (row.documentSource === "annotation") {
      const pageCount = await getPdfPageCount(pdfBlob).catch(() => 0);
      const extracted = getAnnotationSourceExtractedPages(row);
      let localPages = pagesToKeep.length && extracted.length
         ? mapAktPagesToCutSelection(pagesToKeep, extracted)
         : null;
      if (
         !localPages?.length
         && pagesToKeep.length
         && pageCount
         && pagesToKeep.every((page) => page >= 1 && page <= pageCount)
      ) {
         localPages = pagesToKeep;
      }
      if (!localPages?.length || (pageCount > 0 && isFullCutAnnotationSelection(localPages, pageCount))) {
         return pdfBlob;
      }
      try {
         return await cutPdfToSelectedPages(pdfBlob, localPages);
      } catch (error) {
         console.warn("[Servitutredegørelse] export cut failed, using saved annotation PDF", row.key, error);
         return pdfBlob;
      }
   }
   if (!pagesToKeep.length) {
      throw new Error("Ingen sider valgt.");
   }
   return cutPdfToSelectedPages(pdfBlob, pagesToKeep);
}

function buildMissingServitutText(row: ServitutRedegoerelsePayload["matrix"]["rows"][number]): string {
   const parts = [
      `Could not find servitut: ${row.displayAktId}`,
      `Prioritet: ${row.priority || "-"}`,
      `AI forslag: ${row.analysisItem?.pages_kept?.length
         ? formatPageRanges(row.analysisItem.pages_kept)
         : (row.analysisItem && (row.handling ?? "legacyLlmPdf") === "electronicZipPackage"
            ? "Alle sider"
            : "Ingen sider")}`,
   ];
   if (row.sourceMessage?.trim()) {
      parts.push("", row.sourceMessage.trim());
   }
   parts.push("", row.summary || "Ingen servituttekst.");
   return parts.join("\n");
}

function toTxtFileName(fileName: string): string {
   return fileName.replace(/\.[^.]+$/i, "") + ".txt";
}

function createUniqueZipEntryName(fileName: string, usedNames: Set<string>): string {
   const trimmed = fileName.trim() || "servitut.pdf";
   if (!usedNames.has(trimmed)) {
      usedNames.add(trimmed);
      return trimmed;
   }
   const match = /^(.*?)(\.[^.]+)?$/.exec(trimmed);
   const baseName = match?.[1] || "servitut";
   const extension = match?.[2] || "";
   let index = 2;
   while (usedNames.has(`${baseName} (${index})${extension}`)) {
      index += 1;
   }
   const uniqueName = `${baseName} (${index})${extension}`;
   usedNames.add(uniqueName);
   return uniqueName;
}

function groupMetadataByAkt(metadata: LLMMetadata[]): LLMMetadata[][] {
   const batchesByAkt = new Map<string, LLMMetadata[]>();
   metadata.forEach((entry) => {
      const key = entry.documentUrl || entry.id || entry.aktId || entry.document;
      const batch = batchesByAkt.get(key) ?? [];
      batch.push(entry);
      batchesByAkt.set(key, batch);
   });
   return Array.from(batchesByAkt.values());
}

async function runWithConcurrency<T>(
   items: T[],
   concurrency: number,
   worker: (item: T) => Promise<void>,
   signal?: AbortSignal,
) {
   let nextIndex = 0;
   const workerCount = Math.min(concurrency, items.length);
   const runners = Array.from({ length: workerCount }, async () => {
      while (nextIndex < items.length && !signal?.aborted) {
         const item = items[nextIndex];
         nextIndex += 1;
         await worker(item);
      }
   });
   await Promise.all(runners);
}

function indexAnalysisItems(result: LLMAnalysisResult) {
   const byId = new Map<string, LLMAnalysisResultItem>();
   const byAktId = new Map<string, LLMAnalysisResultItem>();
   result.items?.forEach((item) => {
      if (item.id) {
         byId.set(item.id, item);
      }
      if (item.aktId) {
         byAktId.set(item.aktId, item);
      }
   });
   return { byId, byAktId };
}

function pickIncomingAnalysisItem(
   row: ServitutRedegoerelsePayload["matrix"]["rows"][number],
   byId: Map<string, LLMAnalysisResultItem>,
   byAktId: Map<string, LLMAnalysisResultItem>,
): LLMAnalysisResultItem | undefined {
   if (isCustomUploadRow(row) || (row.handling ?? "legacyLlmPdf") === "electronicZipPackage") {
      return byId.get(row.key);
   }
   return byId.get(row.key) ?? byAktId.get(row.analysisAktId);
}

function mergeLocateIntoAnalysisItem(
   previous: LLMAnalysisResultItem | undefined,
   locateItem: LLMAnalysisResultItem,
): LLMAnalysisResultItem {
   return {
      ...previous,
      ...locateItem,
      id: locateItem.id || previous?.id,
      summary: previous?.summary,
      servitut_type: previous?.servitut_type,
      servitut_type_confidence: previous?.servitut_type_confidence,
      paataleberettiget: previous?.paataleberettiget,
      paataleberettiget_confidence: previous?.paataleberettiget_confidence,
      paataleberettiget_resolution: previous?.paataleberettiget_resolution,
      case_relevant: previous?.case_relevant,
      case_relevance_reasoning: previous?.case_relevance_reasoning,
      case_relevance_confidence: previous?.case_relevance_confidence,
   };
}

function mergeContentIntoAnalysisItem(
   previous: LLMAnalysisResultItem | undefined,
   contentItem: LLMAnalysisResultItem,
   aktPages?: number[],
): LLMAnalysisResultItem {
   const keptPages = aktPages?.length
      ? aktPages
      : (previous?.pages_kept?.length ? previous.pages_kept : []);
   return {
      ...previous,
      ...contentItem,
      id: contentItem.id || previous?.id,
      pages_kept: keptPages,
      original_pages: previous?.original_pages ?? contentItem.original_pages,
      extracted_pages: previous?.extracted_pages ?? contentItem.extracted_pages ?? keptPages.length,
   };
}

function attachLocateResult(
   payload: ServitutRedegoerelsePayload,
   result: LLMAnalysisResult,
   options?: { rowKeys?: Set<string> },
): ServitutRedegoerelsePayload {
   const { byId, byAktId } = indexAnalysisItems(result);
   return {
      ...payload,
      analysisResult: result,
      matrix: {
         ...payload.matrix,
         rows: payload.matrix.rows.map((row) => {
            if (options?.rowKeys) {
               if (!options.rowKeys.has(row.key)) {
                  return row;
               }
            } else if (isCustomUploadRow(row)) {
               return row;
            }
            const incoming = pickIncomingAnalysisItem(row, byId, byAktId);
            if (!incoming) {
               return row;
            }
            const analysisItem = mergeLocateIntoAnalysisItem(row.analysisItem, incoming);
            const emptyMeansAll = (row.handling ?? "legacyLlmPdf") === "electronicZipPackage";
            const impliedPages = resolveAnalysisRelevantPages(
               analysisItem.pages_kept,
               analysisItem.original_pages || analysisItem.extracted_pages,
               emptyMeansAll,
            );
            const hadContent = hasContentAnalysisFields(row.analysisItem);
            const pagesChanged = !areServitutPagesEqual(row.analysisItem?.pages_kept, analysisItem.pages_kept);
            return {
               ...row,
               analysisItem,
               selectedPages: impliedPages.length > 0 || !emptyMeansAll
                  ? impliedPages
                  : (row.selectedPages || impliedPages),
               provenance: buildLocateProvenanceForAnalysisItem(
                  { ...row, analysisItem },
                  analysisItem,
                  { analysisStale: hadContent && pagesChanged },
               ),
            };
         }),
      },
   };
}

function attachContentResult(payload: ServitutRedegoerelsePayload, result: LLMAnalysisResult): ServitutRedegoerelsePayload {
   const { byId, byAktId } = indexAnalysisItems(result);
   return {
      ...payload,
      analysisResult: result,
      matrix: {
         ...payload.matrix,
         rows: payload.matrix.rows.map((row) => {
            const incoming = pickIncomingAnalysisItem(row, byId, byAktId);
            if (!incoming) {
               return row;
            }
            const analysisItem = mergeContentIntoAnalysisItem(row.analysisItem, incoming);
            return {
               ...row,
               analysisItem,
               provenance: buildContentProvenanceForAnalysisItem(
                  { ...row, analysisItem },
                  analysisItem,
                  { hasCaseDescription: Boolean(payload.caseDescription?.trim()) },
               ),
            };
         }),
      },
   };
}

